import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Separator } from "@/components/ui/separator";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";


import {
  Search,
  Map as MapIcon,
  Grid,
  Calendar,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Monitor,
  Users,
  ArrowLeft,
  List,
  X,
  Info,
  Clock
} from "lucide-react";
import PackageCard from "@/components/package-card";
import DealCard from "@/components/DealCard";
import MapView from "@/components/map-view";
import { getMediaOwner } from "@/data/media-owners";
import { useNavigate, Link } from "react-router-dom";

import debounce from 'lodash/debounce';
import type { BillboardFilters, CartItem } from '../types/billboard';
import { generateMockData } from '../Graphql/MockData';
import { GETPROPERTY_LIST } from '../Graphql/PropertyListQueries';
// import { toast } from 'react-toastify';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { ADD_TO_CART_SUBMIT, DELETE_CART_SUBMIT, GET_MY_CART_ITEMS } from '../Graphql/cartQueries';
import { PACKAGE_BILLBOARD } from '../Graphql/packageQurie';
import { parse, isValid, format } from 'date-fns';
import client, { MAPBOX_TOKEN } from '../Graphql/apolloClient';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { CART_PAGE_ITEM, GET_DISTRICT_LIST_MARKETPLACE, GET_STATE_LIST_MARKETPLACE, GET_MARKETPLACE_COUNTRIES, INVOICE, COMPANY_LIST, PROPERTY_LIST_VENUE_TYPE_LIST, GET_STATE_LIST } from '../Graphql/Queries';
import { useCart } from '@/utils/cartContext';
import Header from '@/components/header';
import Footer from '@/components/Footer1';
import { useToast } from '@/hooks/use-toast';

// Default filter values
const defaultFilters = {
  country: '', // Will be set from localStorage or country selection
  state: [],
  district: [],
  available: true,
  category: "digital",
  priceRange: null as [number, number] | null, // Changed to null initially
  venueType: "" as string,
  environment: "" as string,
  targetAgeRange: [] as string[],
  audienceType: [] as string[],
  targetGender: [] as string[],
  isIndoor: null as boolean | null,
  inventoryType: [] as string[],
  adPlayMode: [] as string[],
  screenSize: [] as string[],
  billboardFormat: [] as string[],
  orientation: [] as string[],
  mediaOwner: [] as string[]
};

const defaultExpandedSections = {
  location: true,
  category: true,
  buyingFormat: true,
  pricing: false,
  valuePacks: false,
  environment: false,
  mediaOwner: false,
  venue: false,
  audience: false,
  inventorySpec: false
};

const formatNumber = (num: number | undefined | null): string => {
  if (!num || typeof num !== 'number') return '0';
  if (num >= 1000000) {
    return (num / 1000000).toFixed(1) + 'M';
  } else if (num >= 1000) {
    return (num / 1000).toFixed(1) + 'K';
  } else {
    return num.toString();
  }
};


// Safe date formatter that handles null values
const safeFormatDate = (date: Date | null, formatStr: string, fallback: string = ''): string => {
  if (!date) return fallback;
  try {
    return format(date, formatStr);
  } catch (error) {
    console.error("Error formatting date:", error);
    return fallback;
  }
};

// Set Mapbox access token
mapboxgl.accessToken = MAPBOX_TOKEN;

type Field = {
  key: string;
  type: string;
  label: string;
  default: boolean | string;
  disabled?: boolean;
  options?: { label: string; value: string }[];
};

type Configuration = {
  key: string;
  label: string;
  fields?: Field[];
  options?: { label: string; value: string }[];
  type?: string;
  default?: boolean | string;
  disabled?: boolean;
};

type ConfigPage = {
  name: string;
  configurations: Configuration[];
};

interface CampaignInventory {
  id: string;
  cartItemId: string;
  [key: string]: any;
}



interface CheckoutProps {
  yourLogo?: string;
  footerLogo?: string;
  base_path?: string;
  currencyCode?: string;
  configData?: ConfigPage[];
  exposure?: string;
}

export default function StartCampaign(props?: CheckoutProps) {
  // Spot Based Booking flag for API payload
  const [spotBasedBooking, setSpotBasedBooking] = useState<boolean>(false);
  // Buying Format state: 'spot-based' or 'day-based'
  const [buyingFormat, setBuyingFormat] = useState<'spot-based' | 'day-based'>('day-based');

  // Track last countryId used for API call to prevent duplicate calls
  const lastCountryIdRef = useRef<string>();

  // Country initialization tracking
  const countryInitialized = useRef(false);

  // Single source of truth for countryId using 2-variable priority system
  // Priority: Cart Country > selectedCountryId > sessionDefaultCountryId
  const [selectedCountryId, setSelectedCountryId] = useState<string>(() => {
    // First check for navigation backup (strongest priority during state restoration)
    const navigationBackup = localStorage.getItem('navigationCountryBackup');
    if (navigationBackup) {
      try {
        const backup = JSON.parse(navigationBackup);
        // Use backup if it's recent (within 1 hour)
        if (backup.timestamp > Date.now() - 3600000) {
          console.log("🔄 Initial state: Using navigation backup country:", backup.countryLabel);
          countryInitialized.current = true; // Mark as initialized to prevent overrides
          return backup.countryValue;
        } else {
          // Remove old backup
          localStorage.removeItem('navigationCountryBackup');
        }
      } catch (e) {
        console.error("Error parsing navigation backup during init:", e);
        localStorage.removeItem('navigationCountryBackup');
      }
    }

    // DON'T mark as initialized here - let unified initialization handle priority!
    // 2-variable system: selectedCountryId > sessionDefaultCountryId
    const storedSelectedCountryId = localStorage.getItem('selectedCountryId');
    if (storedSelectedCountryId) {
      console.log("✅ Initial state: Found selectedCountryId (will be validated by unified init):", storedSelectedCountryId);
      return storedSelectedCountryId;
    }

    const sessionDefault = localStorage.getItem('sessionDefaultCountryId');
    if (sessionDefault) {
      console.log("✅ Initial state: Found sessionDefaultCountryId (will be validated by unified init):", sessionDefault);
      return sessionDefault;
    }

    return '';
  });
  const { toast } = useToast();
  const defaultProps: CheckoutProps = {
    base_path: '',
    currencyCode: '',
    configData: [],
    yourLogo: '',
    footerLogo: '',
    exposure: ''
  };
  const { base_path, currencyCode, configData, yourLogo, footerLogo, exposure } = { ...defaultProps, ...props };

  // Removed excessive render logging for performance
  // Base paths and currency code configured

  const navigate = useNavigate();
  const { isInCart, addToCart: addToCartContext, removeFromCart: removeFromCartContext, setCartItemCount: setCartItemCountContext, updateTaxInfo, cartItems: contextCartItems, setPriceFormat, priceFormat, setCartCountry, cartCountry, cartCountryId, hasCartCountry, updateStartDate: updateCartStartDate, updateEndDate: updateCartEndDate } = useCart();

  useEffect(() => {
    const today = new Date();
    updateCartStartDate(today);
    updateCartEndDate(today);
    console.log("🔄 New campaign started - dates reset to today");
  }, []);

  // Add a function to get the default view mode from config
  const getDefaultViewMode = (): "grid" | "list" | "map" => {


    if (!configData || configData.length === 0) {

      return "grid"; // fallback
    }

    const searchPageConfig = configData.find((config: ConfigPage) => config.name === "Search page");


    if (!searchPageConfig?.configurations) {

      return "grid";
    }

    const mapAndListViewConfig = searchPageConfig.configurations.find((config: Configuration) => config.key === "MapAndListView");


    if (!mapAndListViewConfig?.options) {

      return "grid";
    }


    // Case-insensitive search for "yes"
    const defaultOption = mapAndListViewConfig.options.find((option: any) =>
      option.value?.toLowerCase() === "yes"
    );


    if (!defaultOption) {
      return "grid";
    }

    // Map the labels to view mode values
    switch (defaultOption.label) {
      case "List View":
        return "list";
      case "Map View":
        return "map";
      case "Grid View":
        return "grid";
      default:
        return "grid";
    }
  };

  const [searchQuery, setSearchQuery] = useState("");
  const [viewMode, setViewMode] = useState<"grid" | "list" | "map">("grid"); // Start with grid as default
  const [localPriceRange, setLocalPriceRange] = useState<[number, number]>([0, 0]);

  // Update viewMode when configData becomes available
  useEffect(() => {

    if (configData && configData.length > 0) {
      const defaultViewMode = getDefaultViewMode();
      setViewMode(defaultViewMode);
    }
  }, [configData]); // Re-run when configData changes
  const [minPriceInput, setMinPriceInput] = useState<string>('');
  const [maxPriceInput, setMaxPriceInput] = useState<string>('');

  // Initialize filters from localStorage or defaults
  const [expandedSections, setExpandedSections] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('start-campaign-expanded-sections');
      return saved ? JSON.parse(saved) : defaultExpandedSections;
    }
    return defaultExpandedSections;
  });


  // Initialize filters from localStorage or defaults
  const [filters, setFilters] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('start-campaign-filters');
      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          // Ensure environment is properly initialized
          const initializedFilters = {
            ...defaultFilters,
            ...parsed,
            country: parsed.country || '',
            state: Array.isArray(parsed.state) ? parsed.state : parsed.state ? [parsed.state] : [],
            district: Array.isArray(parsed.district) ? parsed.district : parsed.district ? [parsed.district] : [],
            environment: parsed.environment || '' // Ensure empty string default
          };

          // Update ref with initial environment value
          currentEnvironmentRef.current = initializedFilters.environment;

          return initializedFilters;
        } catch (e) {
          return {
            ...defaultFilters,
            country: '' // Will be set after countryOptions are loaded
          };
        }
      }
    }
    return {
      ...defaultFilters,
      country: '' // Will be set after countryOptions are loaded
    };
  });

  const cartInitializationRef = useRef(false);
  const initialCartLoadDone = useRef(false);
  const isManualBillboardTypeChange = useRef(false); // Track manual changes to prevent duplicate calls
  const isManualStateChange = useRef(false); // Track manual state changes to prevent duplicate calls
  const isManualDistrictChange = useRef(false); // Track manual district changes to prevent duplicate calls
  const isManualCountryChange = useRef(false); // Track manual country changes to prevent duplicate calls
  const currentEnvironmentRef = useRef<string>(''); // Add this line

  // ➕ ADD THESE DEBUG REFS
  const nonDraftFetchCount = useRef(0);
  const mediaOwnerFetchCount = useRef(0);
  const packageFetchCount = useRef(0);
  const draftFetchCount = useRef(0);

  // Removed isManualPriceChange as it was causing issues with subsequent price filter calls

  // Deduplication refs to prevent multiple API calls for the same parameters
  const lastBillboardFetchRef = useRef<{ countryId?: string; page?: number; type?: string; timestamp?: number }>({});
  const lastPackageFetchRef = useRef<{ countryId?: string; page?: number; timestamp?: number }>({});

  // Initialize campaign details with generic defaults
  const [startDate, setStartDate] = useState<Date>(() => {
    const firstDay = new Date();
    firstDay.setDate(1); // Go to first of current month
    return firstDay;
  }); // First day of current month
  const [endDate, setEndDate] = useState<Date>(() => {
    const lastDay = new Date();
    lastDay.setDate(1); // Go to first of current month
    lastDay.setMonth(lastDay.getMonth() + 1); // Go to first of next month
    lastDay.setDate(0); // Go to last day of current month
    return lastDay;
  });
  const [campaignName, setCampaignName] = useState<string>(() => {
    const date = new Date();
    return `Campaign_${date.getFullYear()}${String(date.getMonth() + 1).padStart(2, '0')}${String(date.getDate()).padStart(2, '0')}_${Math.random().toString(36).substring(7)}`;
  });



  const [filteredBillboards, setFilteredBillboards] = useState<any>([]);
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  // const [selectedCategory, setSelectedCategory] = useState<string>('Classic');
  const categoryRef = useRef<HTMLDivElement>(null);
  const [mapMode, setMapMode] = useState<'map' | 'satellite'>('map');
  const [cartDataList, setCartDataList] = useState<CampaignInventory[]>([]);
  // const [resultObj, setResultObj] = useState<any>([]);


  const parseDateWithFallback = (date: any, fallback: Date | null = null): Date | null => {
    if (!date || typeof date !== 'string') return fallback;

    const possibleFormats = ['yyyy-MM-dd', 'dd MMMM yyyy', 'MM/dd/yyyy'];
    for (const format of possibleFormats) {
      const parsedDate = parse(date, format, new Date());
      if (isValid(parsedDate)) {
        return parsedDate;
      }
    }

    const parsed = new Date(date);
    return isValid(parsed) ? parsed : fallback;
  };


  // Initialize state and district from local storage only
  const [state, setState] = useState<string[]>(() => {
    const savedListState = localStorage.getItem('campaignListState');
    if (savedListState) {
      try {
        const parsed = JSON.parse(savedListState);
        if (parsed.state) {
          if (Array.isArray(parsed.state)) return parsed.state.filter(Boolean);
          if (typeof parsed.state === 'string') return parsed.state.split(',').map((s: string) => s.trim()).filter(Boolean);
        }
      } catch (e) {
        console.error('Error parsing campaignListState:', e);
      }
    }
    return [];
  });

  const [district, setDistrict] = useState<string[]>([]);

  const [availableStates, setAvailableStates] = useState<any[]>([]);
  const [availableDistricts, setAvailableDistricts] = useState<any[]>([]);
  const [statesLoading, setStatesLoading] = useState(false);
  const [districtsLoading, setDistrictsLoading] = useState(false);

  const [billBoardDataLoading, setBillBoardDataLoading] = useState(false);
  const [carouselLoading, setCarouselLoading] = useState(false);
  const [billBoardData, setBillBoardData] = useState<any>([]);
  // Package state management
  const [packageData, setPackageData] = useState<any>([]);
  const [packageDataLoading, setPackageDataLoading] = useState(false);
  // const [soldCampaignViewDetails, setSoldCampaignViewDetails] = useState<any>({});
  const [cartCount, setCartCount] = useState<number>(0);
  const [addtoCartQuery] = useMutation(ADD_TO_CART_SUBMIT);
  const [deleteCartQuery] = useMutation(DELETE_CART_SUBMIT);
  const [billboardType, setBillboardType] = useState("digital");

  // --- Dynamic Media Owner Filter ---
  // Fetch media owners dynamically using MEDIA_OWNER_FILTER
  const accessToken = typeof window !== 'undefined' ? localStorage.getItem('authToken') : '';
  const countryName = typeof window !== 'undefined' ? localStorage.getItem('sessionDefaultCountryName') : '';
  const type = "MEDIA_OPERATOR";
  const authentication = true;

  // Replace the MEDIA_OWNER_FILTER query with COMPANY_LIST
  const { data: mediaOwnerData, loading: mediaOwnerLoading, error: mediaOwnerError, refetch: refetchMediaOwners } = useQuery(COMPANY_LIST, {
    variables: {
      exposure: exposure,
      countryId: selectedCountryId || localStorage.getItem('sessionDefaultCountryId'),
      page: 0,  // Assuming starting page is 0
      size: 100,  // Assuming a reasonable size for the list
    },
    skip: !selectedCountryId,
    fetchPolicy: 'cache-first',
    onError: (error) => {
      console.error('Media Owner Filter API Error:', error);
    }
  });

  // Refetch media owners when country changes
  useEffect(() => {
    if (selectedCountryId) {
      console.log('🏢 Refetching media owners for country:', selectedCountryId);
      refetchMediaOwners({
        exposure: exposure,
        countryId: selectedCountryId,
        page: 0,
        size: 100,
      });
    }
  }, [selectedCountryId, exposure, refetchMediaOwners]);

  // Extract media owner options for filter dropdown with error handling
  const mediaOwnerOptions = (() => {
    if (mediaOwnerError) {
      console.error('Error loading media owners:', mediaOwnerError);
      return []; // Return empty array if there's an error
    }

    if (mediaOwnerLoading) {
      return []; // Return empty array while loading
    }

    if (!mediaOwnerData?.companyList) {
      console.warn('No media owner data available');
      return [];
    }

    // Sort media owners alphabetically by name before mapping
    return mediaOwnerData.companyList
      .slice() // copy to avoid mutating original
      .sort((a: any, b: any) => a.name.localeCompare(b.name))
      .map((owner: any) => ({
        label: owner.name,
        value: owner.id,
      }));
  })();

  // Use mediaOwnerOptions in your filter UI instead of hardcoded values

  const dataListLength = 12;
  const [totalDataLength, setTotalDataLength] = useState(0);
  const [locationData, setLocationData] = useState<string[]>([]);
  const [venueTypeData, setVenueTypeData] = useState<string[]>([]);
  const [siteTypeData, setSitetypeData] = useState<string[]>([]);
  const mapContainer = useRef<HTMLDivElement>(null);
  const [map, setMap] = useState<mapboxgl.Map | null>(null);
  const token = localStorage.getItem('authToken');
  const userId = localStorage.getItem('userId');
  // const companyId = import.meta.env.VITE_APP_COMPANY_ID;

  // Additional state variables that are used in functions
  const [pageStartData, setPageStartData] = useState(0);
  const [pageEndData, setPageEndData] = useState(0);
  const [digitalTotalSize, setdigitalTotalSize] = useState(0);
  const [clasicTotalSize, setclasicTotalSize] = useState(0);
  const [isCartLoading, setIsCartLoading] = useState(false);
  const [cartData, setCartData] = useState<any>([{}]);
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);

  const urlParams = new URLSearchParams(window.location.search);

  const [campaignId, setCampaignId] = useState<string | null>(() => {
    const id = urlParams.get('draftId');
    return id ? id : " ";
  });

  const [draftData, setDraftData] = useState<any>(null);
  const [draftInitialized, setDraftInitialized] = useState(false);
  const [campaignData, setCampaignData] = useState<any>(null);

  const [edit, setEdit] = useState(() => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('edit') === 'true';
  });

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const editFlag = urlParams.get('edit') === 'true';
    if (editFlag !== edit) {
      setEdit(editFlag);
    }
  }, []); // Remove dependencies to prevent loops, run only on mount
  const [source, setSource] = useState('');

  // Additional state variables that are used in functions
  const [badges, setBadges] = useState<string[]>([]);
  const [indoorChecked, setIndoorChecked] = useState(false);
  const [outdoorChecked, setOutdoorChecked] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [search, setSearch] = useState<string>("");
  const [markers, setMarkers] = useState<mapboxgl.Marker[]>([]);
  const hasInitialized = useRef(false);
  const [loadingCartItemIds, setLoadingCartItemIds] = useState<Set<string>>(new Set());
  const [noDataMessage, setNoDataMessage] = useState<string | null>(null);
  const [cartDataLoaded, setCartDataLoaded] = useState(false);


  // Cart URL state
  const [cartUrl, setCartUrl] = useState<string>("");

  // Venue types hierarchy state
  const [venueTypesHierarchy, setVenueTypesHierarchy] = useState<any[]>([]);

  const filtersRef = useRef(filters);

  // Load venue types hierarchy from JSON file
  useEffect(() => {
    const fetchVenueTypes = async () => {
      try {
        const { data } = await client.query({
          query: PROPERTY_LIST_VENUE_TYPE_LIST,
          fetchPolicy: "network-only", // optional: always fetch fresh data
        });

        if (data?.venueTypes) {
          setVenueTypesHierarchy(data.venueTypes);
        } else {
          console.error("No venue types found in response");
        }
      } catch (err) {
        console.error("Failed to load venue types hierarchy:", err);
      }
    };

    fetchVenueTypes();
  }, []);

  // Fetch cart data early for country initialization
  useEffect(() => {
    console.log("🛒 Early cart data fetch for country initialization");
    // Only reset country initialization if we don't have a stored selectedCountryId
    // This prevents resetting when user navigates back from detail pages
    if (!localStorage.getItem('selectedCountryId')) {
      countryInitialized.current = false;
      console.log("🔄 Reset country initialization state for fresh page load");
    } else {
      console.log("✅ Preserving country initialization - user has saved selection");
    }
    // Reset country initialization state on page load to ensure fresh initialization
    countryInitialized.current = false;
    fetchCartCountDetails();
  }, []);


  // Check for draft campaign data in URL parameters
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);

    const campaignId = urlParams.get('draftId');
    const campaignNameParam = urlParams.get('campaignName');
    const editFlag = urlParams.get('edit') === 'true';
    const sourceParam = urlParams.get('source') || '';
    if (editFlag && campaignId && campaignNameParam) {
      setCampaignId(campaignId);
      setCampaignName(campaignNameParam);
      setEdit(editFlag); // Set edit mode to true for draft campaigns
      setSource(sourceParam);
      // setCartUrl(`${base_path}/mycart?draftId=${campaignId}&draftName=${campaignName || ''}&startDate=${startDate || ''}&endDate=${endDate || ''}&source=${source}&edit=${edit}`);
      const fetchInvoice = async () => {
        const invoice = await fetchInvoiceDetails(campaignId);

        // Extract and set country from draft invoice to prevent reset
        if (invoice?.paymentInvoice?.priceSummary?.currency?.code) {
          const draftCountryCode = invoice.paymentInvoice.priceSummary.currency.code;
          console.log('Setting cartCountry from draft invoice:', draftCountryCode);
          localStorage.setItem('cartCountry', draftCountryCode);
          setCartCountry(draftCountryCode);
        } else {
          console.warn('No currency code found in invoice. Full invoice structure:', JSON.stringify(invoice, null, 2));
        }

        // Only update campaignName if invoice.name is present and different from URL param
        if (invoice?.name && invoice.name !== campaignNameParam) {
          setCampaignName(invoice.name);
        }
        setStartDate(invoice?.startDate?.date ? new Date(invoice.startDate.date) : new Date());
        setEndDate(invoice?.endDate?.date ? new Date(invoice.endDate.date) : new Date());
        setDraftData(invoice);
      };
      fetchInvoice();
    } else {
      setCartUrl(`${base_path}/mycart`);
    }
  }, []);

  useEffect(() => {
    // Fetch campaignName from URL if available
    const urlParams = new URLSearchParams(window.location.search);
    const campaignNameParam = urlParams.get('campaignName');
    if (campaignNameParam) {
      setCampaignName(decodeURIComponent(campaignNameParam));
    }
  }, []);

  useEffect(() => {
    if (edit && campaignId && campaignName) {
      // Format dates consistently (yyyy-MM-dd) to match cart.tsx expectations
      const formattedStartDate = startDate ? format(startDate, 'yyyy-MM-dd') : '';
      const formattedEndDate = endDate ? format(endDate, 'yyyy-MM-dd') : '';

      setCartUrl(`${base_path}/mycart?draftId=${campaignId}&campaignName=${encodeURIComponent(campaignName)}&startDate=${formattedStartDate}&endDate=${formattedEndDate}&source=${encodeURIComponent(source)}&edit=true`);

    }
  }, [campaignName, campaignId, startDate, endDate, source, edit, base_path]);

  // Function to get all children and grandchildren for a parent venue type
  const getAllChildrenForVenueType = (parentName: string): string[] => {
    const parent = venueTypesHierarchy.find(item => item.value === parentName);
    if (!parent) return [];

    const allChildren: string[] = [];

    const extractChildren = (item: any) => {
      if (item.child && Array.isArray(item.child)) {
        item.child.forEach((child: any) => {
          allChildren.push(child.value);
          extractChildren(child); // Recursively get grandchildren
        });
      }
    };

    extractChildren(parent);
    return allChildren;
  };

  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest('.relative')) {
        setShowCountryDropdown(false);
        setShowStateDropdown(false);
        setShowDistrictDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Update billboard and package isInCart flags when cartDataList changes
  useEffect(() => {
    // Create separate sets for different item types
    const cartBillboardIds = new Set(
      cartDataList.flatMap((cart: any) =>
        cart.campaignInventories
          ?.filter((inv: any) => inv.billboardType !== 'package') // Regular billboards (digital/classic)
          ?.map((inv: any) => inv.billboardId)
      ).filter(Boolean)
    );

    const cartPackageIds = new Set(
      cartDataList.flatMap((cart: any) =>
        cart.campaignInventories
          ?.filter((inv: any) => inv.billboardType === 'package') // Package items
          ?.map((inv: any) => inv.billboardId)
      ).filter(Boolean)
    );

    // Update billboard data with cart status
    setBillBoardData((prevData: any[]) =>
      prevData.map(billboard => ({
        ...billboard,
        isInCart: cartBillboardIds.has(billboard.id)
      }))
    );

    setFilteredBillboards((prev: any[]) =>
      prev.map(billboard => ({
        ...billboard,
        isInCart: cartBillboardIds.has(billboard.id)
      }))
    );

    // Update package data with cart status
    setPackageData((prevData: any[]) =>
      prevData.map(pkg => ({
        ...pkg,
        isInCart: cartPackageIds.has(pkg.id)
      }))
    );
  }, [cartDataList]);




  // GraphQL queries
  // Country filter query
  const { data: countryData, loading: countryLoading, error: countryError } = useQuery(GET_MARKETPLACE_COUNTRIES, {
    variables: { exposure: exposure },
    fetchPolicy: 'cache-first',
    onError: (error) => {
      console.error('Country Filter API Error:', error);
    }
  });



  // Country options mapping
  const countryOptions = (() => {
    if (countryError) {
      console.error('Error loading countries:', countryError);
      return [];
    }
    if (countryLoading) {
      return [];
    }
    if (!countryData?.getMarketplaceCountries) {
      return [];
    }
    console.log('Country Data:', countryData);

    // Sort countries alphabetically by name before mapping
    return countryData.getMarketplaceCountries
      .slice() // copy to avoid mutating original
      .sort((a: any, b: any) => a.name.localeCompare(b.name))
      .map((country: any) => ({
        label: country.name,
        value: country.id, // Use the id field instead of countryId
        countryId: country.countryId, // Keep countryId for reference
        id: country.id,
        iso: country.iso, // Include ISO code for reference
        tax: country.tax, // Include tax information for cart context
        currencyCode: country.currencyCode // Include currency code for cart context
      }));
  })();

  // ================= Country Resolution Helpers =================
  type CountryOption = {
    label: string;
    value: string; // Mongo _id
    countryId: string;
    id: string;
    iso?: string;
    tax?: any;
    currencyCode?: string;
  };

  // Derive country from cart inventories (first item's country fields)
  const deriveCountryFromCart = (cartDataList: any[], options: CountryOption[]): CountryOption | null => {
    const firstInventory = cartDataList[0]?.campaignInventories?.[0];
    let raw = firstInventory?.billboardCountry || firstInventory?.cartItemCountry || firstInventory?.country;
    // Fallback to stored cartCountry if inventories not yet populated
    if (!raw) {
      raw = localStorage.getItem('cartCountry') || cartCountry;
    }
    if (!raw) return null;
    const lowered = String(raw).toLowerCase();
    return options.find(c =>
      c.label.toLowerCase() === lowered ||
      c.countryId.toLowerCase() === lowered ||
      (c.iso && c.iso.toLowerCase() === lowered) ||
      c.value === raw ||
      c.id === raw
    ) || null;
  };

  // Resolve selected country based on priority Cart > manual selection > session default > first option
  const resolveSelectedCountry = (
    cartDataList: any[],
    options: CountryOption[],
    storedSelectedCountryId: string | null,
    sessionDefaultCountryId: string | null
  ): CountryOption | null => {
    // Priority 1: Cart
    const cartCountry = deriveCountryFromCart(cartDataList, options);
    if (cartCountry) return cartCountry;

    // Priority 2: Manual selection (selectedCountryId in localStorage)
    if (storedSelectedCountryId) {
      const manual = options.find(c => c.id === storedSelectedCountryId || c.value === storedSelectedCountryId);
      if (manual) return manual;
    }

    // Priority 3: Session default
    if (sessionDefaultCountryId) {
      const session = options.find(c => c.id === sessionDefaultCountryId || c.value === sessionDefaultCountryId);
      if (session) return session;
    }

    // Priority 4: First available
    return options[0] || null;
  };

  // Effective country id for API calls with explicit override param
  const getEffectiveCountryId = (explicit?: string): string => {
    // Highest precedence: explicit override
    if (explicit) return explicit;

    // Prefer raw cart country in storage even before inventories load
    const rawCart = localStorage.getItem('cartCountry');
    if (rawCart && countryOptions.length > 0) {
      const lowered = rawCart.toLowerCase();
      const cartOpt = (countryOptions as CountryOption[]).find(c =>
        c.label.toLowerCase() === lowered ||
        c.countryId.toLowerCase() === lowered ||
        (c.iso && c.iso.toLowerCase() === lowered)
      );
      if (cartOpt) return cartOpt.value;
    }

    // Inventories-derived cart country
    const invOpt = deriveCountryFromCart(cartDataList, countryOptions as CountryOption[]);
    if (invOpt) return invOpt.value;

    // After initialization trust selectedCountryId
    if (countryInitialized.current && selectedCountryId) return selectedCountryId;

    // Defer fetch until country resolves (return blank string -> caller should skip)
    return '';
  };

  // Track last effective country used for billboard API calls
  const previousApiCountryRef = useRef<string>('');

  // ✅ SIMPLE FIX: Sync country dropdown and inventory list on back navigation
  useEffect(() => {
    const shouldSync = localStorage.getItem('clearStartCampaignFilters') === 'true';
    // 🔴 FIX: Stop this logic from running during Draft/Edit flow
    if (edit) return;

    if (!shouldSync) return;

    // Wait until countryOptions are loaded
    if (!countryOptions || countryOptions.length === 0) {
      console.log('⏳ Waiting for countryOptions to load...');
      return;
    }

    localStorage.removeItem('clearStartCampaignFilters');

    // Determine if cart has items
    const cartHasItems = cartDataList.length > 0 && cartDataList[0]?.campaignInventories?.length > 0;
    const sessionDefaultCountryId = localStorage.getItem('sessionDefaultCountryId');
    let newCountryId = sessionDefaultCountryId;

    if (cartHasItems) {
      // Use cart country if cart has items
      const cartCountry = cartDataList[0]?.campaignInventories?.[0]?.billboardCountry;
      const cartCountryOption = countryOptions.find((c: any) => c.label === cartCountry);
      if (cartCountryOption) {
        newCountryId = cartCountryOption.value;
      }
    }

    console.log('✅ Syncing country:', { newCountryId, cartHasItems, sessionDefaultCountryId });

    // Sync dropdown and filters
    setSelectedCountryId(newCountryId || '');
    setFilters((prev: any) => ({
      ...prev,
      country: newCountryId || '',
    }));

    // Refetch inventory list for the new country
    const cartBillboardIds = new Set(
      cartDataList.flatMap((cart: any) =>
        cart.campaignInventories?.map((inv: any) => inv.billboardId)
      ).filter(Boolean)
    );

    console.log('🚀 Calling fetchAllBillboardData with country:', newCountryId);
    fetchAllBillboardData('', 0, cartBillboardIds, undefined, [], undefined, newCountryId ?? undefined);

  }, [cartDataList, countryOptions, edit]);

  // ✅ Sync country dropdown and billboard list when returning from detail page
  useEffect(() => {
    const navigationBackup = localStorage.getItem('navigationCountryBackup');
    
    if (navigationBackup && countryOptions.length > 0 && !edit && cartDataLoaded) {
      try {
        const backup = JSON.parse(navigationBackup);
        
        // If backup is recent (within 1 hour)
        if (backup.timestamp > Date.now() - 3600000) {
          const backupCountry = countryOptions.find((c: any) => c.id === backup.countryId);
          
          if (backupCountry) {
            console.log('🔄 [Return from Detail] Syncing country:', backupCountry.label);
            
            // ✅ FIX: Mark initialization to prevent other effects from overriding
            countryInitialized.current = true;
            
            // Sync dropdown with backup country
            setSelectedCountryId(backupCountry.value);
            setFilters((prev: any) => ({
              ...prev,
              country: backupCountry.value,
              state: [],
              district: []
            }));
            
            // Reset location states
            setState([]);
            setDistrict([]);
            setAvailableDistricts([]);
            
            // Fetch billboards for the backed-up country
            const cartBillboardIds = new Set(cartDataList.flatMap((cart: any) =>
              cart.campaignInventories?.map((inv: any) => inv.billboardId)
            ).filter(Boolean));
            
            // Pass backup country explicitly to API
            fetchAllBillboardData(search, 0, cartBillboardIds, undefined, [], undefined, backupCountry.value);
            
            console.log('✅ Country synced successfully from backup');
            
            // ✅ FIX: Clean up backup AFTER state is set to prevent re-triggering
            setTimeout(() => {
              localStorage.removeItem('navigationCountryBackup');
              console.log('🧹 Navigation backup cleaned up');
            }, 100);
          } else {
            console.log('⚠️ Backup country not found in options');
            localStorage.removeItem('navigationCountryBackup');
          }
        } else {
          // Backup expired, clean it up
          console.log('⏳ Navigation backup expired');
          localStorage.removeItem('navigationCountryBackup');
        }
      } catch (e) {
        console.error('Error parsing navigation backup:', e);
        localStorage.removeItem('navigationCountryBackup');
      }
    }
  }, [countryOptions.length, cartDataLoaded, edit]);

  // Get MongoDB id for API calls (use the selected country's MongoDB id directly)
  // const selectedCountryForStates = countryOptions.find((c: any) => c.value === filters.country);
  // Fallback: if no country selected, find by countryIdData from localStorage
  // const fallbackCountry = !selectedCountryForStates && countryIdData ?
  //   countryOptions.find((c: any) => c.countryId === countryIdData) : null;
  // const countryIdForStates = selectedCountryForStates?.value || fallbackCountry?.value; // Always use MongoDB id


  const fetchCartCountDetails = async () => {
    // Skip if already fetching to prevent duplicate calls
    if (cartCountRequestInProgress.current) {
      console.log('⏳ Skipping cart count fetch - already in progress');
      return 0; // Return 0 instead of undefined
    }

    cartCountRequestInProgress.current = true;
    try {
      let validStartDate = startDate;
      let validEndDate = endDate;
      setCartDataLoaded(false); // Mark cart data as not loaded

      // NOTE: Do not clear cartCountry pre-fetch; keep previous value until new data arrives to allow proper initialization.

      // If dates are strings, convert to Date objects
      if (typeof validStartDate === 'string') validStartDate = new Date(validStartDate);
      if (typeof validEndDate === 'string') validEndDate = new Date(validEndDate);

      if (!isValid(validStartDate) || !isValid(validEndDate)) {
        console.error("Invalid dates detected:", { startDate, endDate });
        throw new Error("Invalid date values for cart count fetch.");
      }

      if (import.meta.env.VITE_APP_MOCK_DATA === 'true') {
        try {
          const mockData = await generateMockData(GET_MY_CART_ITEMS);

          // Check if this is a draft flow - skip setting cartCountry for drafts
          const urlParams = new URLSearchParams(window.location.search);
          const isDraft = urlParams.get('edit') === 'true';

          if (mockData.data?.cartpageItem?.length > 0) {
            const cartCountry = mockData.data.cartpageItem[0].cartItemCountry;
            if (cartCountry && !isDraft) { // Only set for non-drafts
              localStorage.setItem('cartCountry', cartCountry.toLowerCase());
              setCartCountry(cartCountry.toLowerCase());
              console.log('🛒 Cart country set for non-draft:', cartCountry);
            } else if (isDraft) {
              console.log('🛒 Skipping cart country set for draft - will be handled by start-campaign');
            }
          } else {
            if (!isDraft) {
              localStorage.removeItem('cartCountry');
              setCartCountry('');
              console.log('🧹 Cart empty (mock) - retaining existing selectedCountryId for possible manual choice.');
              countryInitialized.current = false; // allow fallback if needed later
            }
          }

          // Count the items from cartpageItem array for mock data
          const count = mockData.data?.cartpageItem?.length || 0;
          setCartCount(count);
          setCartItemCountContext(count); // Sync with cart context
          window.dispatchEvent(new Event('cartUpdated'));
          setCartDataLoaded(true); // Mark cart data as loaded
          return count;
        } catch (error) {
          console.error("Error generating mock cart data:", error);
          // Clear cart country on error to ensure fallback to default country
          localStorage.removeItem('cartCountry');
          localStorage.removeItem('selectedCountryId');
          console.log('🧹 Cleared cart country and selectedCountryId due to mock data error');

          // Reset country initialization to allow re-initialization with default country
          countryInitialized.current = false;
          console.log('🔄 Reset country initialization due to mock data error');

          setCartCount(0);
          setCartItemCountContext(0);
          setCartDataLoaded(true); // Mark cart data as loaded
          return 0;
        }
      } else {
        try {
          const { data } = await client.query({
            query: GET_MY_CART_ITEMS, // Query for fetching cart items
            fetchPolicy: "network-only",
            variables: {
              accessToken: token,
              userId: userId,
            }
          });
          console.log('Cart data from API bf condition:', data.cartpageItem);

          // Store cart country data for unified initialization
          // Check if this is a draft flow - skip setting cartCountry for drafts
          const urlParams = new URLSearchParams(window.location.search);
          const isDraft = urlParams.get('edit') === 'true';

          if (data?.cartpageItem?.length > 0) {
            const cartCountry = data.cartpageItem[0].cartItemCountry;
            if (cartCountry && !isDraft) { // Only set for non-drafts
              localStorage.setItem('cartCountry', cartCountry.toLowerCase());
              setCartCountry(cartCountry.toLowerCase());
              console.log('🛒 Cart country set for non-draft:', cartCountry);
            } else if (isDraft) {
              console.log('🛒 Skipping cart country set for draft - will be handled by start-campaign');
            }
          } else {
            if (!isDraft) {
              localStorage.removeItem('cartCountry');
              setCartCountry('');
              console.log('🧹 Cart empty (api) - retaining existing selectedCountryId for possible manual choice.');
              // countryInitialized.current = false;
            }
          }

          // Count the items from cartpageItem array
          const count = data?.cartpageItem?.length || 0;
          setCartCount(count);
          setCartItemCountContext(count); // Sync with cart context
          window.dispatchEvent(new Event('cartUpdated'));
          setCartDataLoaded(true); // Mark cart data as loaded
          return count;
        } catch (error) {
          console.error("Error fetching cart count:", error);
          // On API error, don't nuke user selection; just allow re-init if cart state changes
          localStorage.removeItem('cartCountry');
          console.log('🧹 Cleared cart country due to API error (kept selectedCountryId).');
          countryInitialized.current = false;

          toast({
            title: "Error",
            description: "Failed to fetch cart count.",
            variant: "destructive"
          });
          setCartCount(0);
          setCartItemCountContext(0);
          setCartDataLoaded(true); // Mark cart data as loaded
          return 0;
        }
      }
    } catch (error) {
      console.error("Error fetching cart count:", error);
      // General error: remove cartCountry only
      localStorage.removeItem('cartCountry');
      console.log('🧹 Cleared cart country due to general error (kept selectedCountryId).');
      countryInitialized.current = false;

      toast({
        title: "Error",
        description: "Failed to fetch cart count.",
        variant: "destructive"
      });
      setCartCount(0);
      setCartItemCountContext(0);
      setCartDataLoaded(true); // Mark cart data as loaded
      return 0;
    } finally {
      // Always reset the in-progress flag
      cartCountRequestInProgress.current = false;
    }
  };

  const draftEffectRan = useRef(false);
  const [draftCountrySet, setDraftCountrySet] = useState(false); // Track if draft country has been set
  const dataFetchedForDraft = useRef(false); // Prevent multiple fetches for drafts

  // Reset draft data fetch flag when entering/re-entering draft mode
  useEffect(() => {
    if (edit && campaignId) {
      console.log('📝 Entering draft mode - resetting draft data fetch flag');
      dataFetchedForDraft.current = false;
    }
  }, [edit, campaignId]);

  // Dedicated effect to set draft country when both draftData and countryOptions are available
  useEffect(() => {

    if (edit && draftData && countryOptions.length > 0 && !draftEffectRan.current) {
      draftEffectRan.current = true;
      let draftCountryCode = cartCountry;

      // If cartCountry is not set, try to extract from draftData directly
      if (!draftCountryCode) {
        draftCountryCode = draftData.paymentInvoice?.priceSummary?.currency?.code ||
          draftData.currency?.code ||
          draftData.priceSummary?.currency?.code;
      }

      if (draftCountryCode) {
        const draftCountry = countryOptions.find((c: any) => c.currencyCode === draftCountryCode);
        if (draftCountry) {
          setSelectedCountryId(draftCountry.id);
          setFilters((prev: any) => ({
            ...prev,
            country: draftCountry.value
          }));
          localStorage.setItem('selectedCountryId', draftCountry.id);
          if (draftCountry.tax) {
            updateTaxInfo(draftCountry.tax.label || 'Tax', draftCountry.tax.percent || 0);
          }
          countryInitialized.current = true; // Mark as initialized to prevent unified initialization override
          // draftCountrySet.current = true;
          setDraftCountrySet(true);
          // Set cartCountry to the draft country's name for consistency
          localStorage.setItem('cartCountry', draftCountry.label);
          setCartCountry(draftCountry.label);
        } else {
          // Try fallback matching
          const fallbackCountry = countryOptions.find((c: any) =>
            c.countryId === draftCountryCode ||
            c.iso === draftCountryCode ||
            c.id === draftCountryCode
          );
          if (fallbackCountry) {
            console.log('Found fallback country:', fallbackCountry);
            setSelectedCountryId(fallbackCountry.id);
            setFilters((prev: any) => ({
              ...prev,
              country: fallbackCountry.value
            }));
            localStorage.setItem('selectedCountryId', fallbackCountry.id);
            if (fallbackCountry.tax) {
              updateTaxInfo(fallbackCountry.tax.label || 'Tax', fallbackCountry.tax.percent || 0);
            }
            countryInitialized.current = true;
            setDraftCountrySet(true);
            console.log('Fallback draft country set successfully');
          } else {
            console.error('No fallback country found either. Draft will use default country.');
          }
        }
      } else {
        console.warn('No draft country code found in cartCountry or draftData');
      }
      // Mark draft initialization as complete
      setDraftInitialized(true);
    }
  }, [edit, draftData, countryOptions, cartCountry]); // Runs when draftData or countryOptions or cartCountry change

  // ✅ Simplified unified country initialization using helpers (Cart > manual > sessionDefault > first)
  useEffect(() => {
    const isDraft = new URLSearchParams(window.location.search).get('edit') === 'true';
    if (isDraft) return;
    if (countryInitialized.current) return;
    if (countryOptions.length === 0) return;

    // ✅ SKIP if navigation backup exists - let that effect handle restoration
    const navigationBackup = localStorage.getItem('navigationCountryBackup');
    if (navigationBackup) {
      console.log('⏭️ Skipping unified initialization - navigation backup in progress');
      return;
    }

    // Wait until either cart data is loaded OR cartCountry exists (so we can prefer cart)
    const rawCartCountry = localStorage.getItem('cartCountry');
    const cartReady = cartDataLoaded || !!rawCartCountry;
    if (!cartReady) return;

    const storedSelectedCountryId = localStorage.getItem('selectedCountryId');
    const sessionDefaultCountryId = localStorage.getItem('sessionDefaultCountryId');

    // Check if cart has items
    const cartHasItems = cartDataList.length > 0 && cartDataList[0]?.campaignInventories?.length > 0;

    // Try direct cartCountry mapping first (rawCartCountry) for early cart preference
    let chosen: CountryOption | null = null;
    if (rawCartCountry) {
      const lowered = rawCartCountry.toLowerCase();
      chosen = (countryOptions as CountryOption[]).find(c =>
        c.label.toLowerCase() === lowered ||
        c.countryId.toLowerCase() === lowered ||
        (c.iso && c.iso.toLowerCase() === lowered)
      ) || null;
    }

    // If cart is empty and sessionDefaultCountryId exists, prioritize it
    if (!chosen && !cartHasItems && sessionDefaultCountryId) {
      chosen = (countryOptions as CountryOption[]).find(c =>
        c.id === sessionDefaultCountryId || c.value === sessionDefaultCountryId
      ) || null;
      if (chosen) {
        console.log('🌍 Cart empty - setting country to sessionDefaultCountryId:', chosen.label);
      }
    }

    if (!chosen) {
      chosen = resolveSelectedCountry(cartDataList, countryOptions as CountryOption[], storedSelectedCountryId, sessionDefaultCountryId);
    }
    if (!chosen) return;
    setSelectedCountryId(chosen.value);
    if (filters.country !== chosen.value) setFilters((prev: any) => ({ ...prev, country: chosen.value }));
    localStorage.setItem('selectedCountryId', chosen.id);
    if (chosen.tax) updateTaxInfo(chosen.tax.label || 'Tax', chosen.tax.percent || 0);
    countryInitialized.current = true;
  }, [countryOptions.length, cartDataLoaded, cartDataList.length, filters.country, updateTaxInfo]);

  // Debug: log final initialization state once selectedCountryId stabilizes
  useEffect(() => {
    if (!countryInitialized.current) return;
    console.log('[INIT COMPLETE]', {
      selectedCountryId,
      storedSelectedCountryId: localStorage.getItem('selectedCountryId'),
      cartCountry: localStorage.getItem('cartCountry'),
      sessionDefaultCountryId: localStorage.getItem('sessionDefaultCountryId')
    });
  }, [selectedCountryId]);

  // Late cart sync: if cart items appear after init and differ from selectedCountryId, switch
  useEffect(() => {
    if (!countryInitialized.current) return; // only act after initial selection
    if (countryOptions.length === 0) return;
    const rawCartCountry = localStorage.getItem('cartCountry');
    if (!rawCartCountry) return;
    const lowered = rawCartCountry.toLowerCase();
    const cartOption = (countryOptions as CountryOption[]).find(c =>
      c.label.toLowerCase() === lowered ||
      c.countryId.toLowerCase() === lowered ||
      (c.iso && c.iso.toLowerCase() === lowered)
    );
    if (!cartOption) return;
    const cartHasItems = cartDataList.length > 0 && cartDataList[0]?.campaignInventories?.length > 0;
    if (cartHasItems && selectedCountryId !== cartOption.value) {
      console.log('🔁 Late cart sync switching country to cart selection:', cartOption.label);
      setSelectedCountryId(cartOption.value);
      setFilters((prev: any) => ({ ...prev, country: cartOption.value }));
      if (cartOption.tax) updateTaxInfo(cartOption.tax.label || 'Tax', cartOption.tax.percent || 0);
      // Trigger immediate billboard refetch with new country to avoid showing previous country's data
      // Build current cart billboard id set for isInCart flag consistency
      const cartBillboardIds = new Set(
        cartDataList.flatMap((cart: any) =>
          cart.campaignInventories?.map((inv: any) => inv.billboardId)
        ).filter(Boolean)
      );
      // Avoid double fetch if one already in progress
      if (!billBoardDataLoading) {
        console.log('🚀 Refetching billboards after late country sync:', cartOption.value);
        fetchAllBillboardData(search, 0, cartBillboardIds, undefined, undefined, undefined, cartOption.value);
      } else {
        console.log('⏳ Skipping refetch (already loading). Will rely on upcoming completion.');
      }
    }
  }, [cartDataList.length, selectedCountryId, countryOptions.length, updateTaxInfo]);

  // 📝 Draft flow: If draft is active and cart has items, sync country dropdown and billboard list to cart country
  useEffect(() => {
    // Only run if in draft mode, draftData exists, and cart has items
    if (
      edit &&
      campaignId &&
      draftData &&
      cartDataList.length > 0 &&
      cartDataList[0]?.campaignInventories?.length > 0 &&
      countryOptions.length > 0
    ) {
      // Get cart country from first inventory
      const cartCountry = cartDataList[0]?.campaignInventories?.[0]?.billboardCountry;
      // Find the country option object
      const cartCountryObj = countryOptions.find((c: any) => c.label === cartCountry);

      if (cartCountryObj) {
        // 🔴 FIX: Check if the country is DIFFERENT before doing anything
        if (selectedCountryId !== cartCountryObj.value) {
          console.log('📝 Draft flow: Syncing country to cart country:', cartCountryObj.label);
          setSelectedCountryId(cartCountryObj.value);
          setFilters((prev: any) => ({
            ...prev,
            country: cartCountryObj.value,
          }));

          // 🔴 MOVED INSIDE: Only fetch if we actually CHANGED the country
        const cartBillboardIds = new Set(
          cartDataList.flatMap((cart: any) =>
            cart.campaignInventories?.map((inv: any) => inv.billboardId)
          ).filter(Boolean)
        );

        if (!billBoardDataLoading) {
            console.log('📝 Draft flow: Fetching billboards for NEW cart country:', cartCountryObj.value);
          fetchAllBillboardData(search, 0, cartBillboardIds, undefined, undefined, undefined, cartCountryObj.value);
        }
        } else {
          // 🔴 ADDED: Log that we are skipping the call because country matches
          console.log('📝 Draft flow: Cart updated but country matches. SKIPPING API call.');
        }
      }
    }
  }, [edit, campaignId, draftData, cartDataList, countryOptions, selectedCountryId, billBoardDataLoading, updateTaxInfo, search]);

  
  // Enhanced navigation protection - prevent country resets during navigation
  useEffect(() => {
    const handleBeforeUnload = () => {
      // Preserve user's manual country selection across navigation
      if (selectedCountryId) {
        const selectedCountry = countryOptions.find((c: any) => c.value === selectedCountryId);
        if (selectedCountry) {
          localStorage.setItem('selectedCountryId', selectedCountry.id);
          localStorage.setItem('navigationCountryBackup', JSON.stringify({
            countryId: selectedCountry.id,
            countryValue: selectedCountry.value,
            countryLabel: selectedCountry.label,
            timestamp: Date.now()
          }));
          console.log("🔒 Navigation protection: Preserving country selection:", selectedCountry.label);
        }
      }
    };

    const handleVisibilityChange = () => {
      // Also preserve on visibility change (covers more navigation scenarios)
      if (document.visibilityState === 'hidden' && selectedCountryId) {
        const selectedCountry = countryOptions.find((c: any) => c.value === selectedCountryId);
        if (selectedCountry) {
          localStorage.setItem('navigationCountryBackup', JSON.stringify({
            countryId: selectedCountry.id,
            countryValue: selectedCountry.value,
            countryLabel: selectedCountry.label,
            timestamp: Date.now()
          }));
          console.log("🔒 Visibility change: Preserving country selection:", selectedCountry.label);
        }
      }
    };

    const handleFocus = () => {
      // When page regains focus, check for navigation backup
      const navigationBackup = localStorage.getItem('navigationCountryBackup');

      if (navigationBackup && countryOptions.length > 0) {
        try {
          const backup = JSON.parse(navigationBackup);
          // Use backup if it's recent (within 1 hour)
          if (backup.timestamp > Date.now() - 3600000) {
            const backupCountry = countryOptions.find((c: any) => c.id === backup.countryId);
            if (backupCountry && selectedCountryId !== backupCountry.value) {
              console.log("🔄 Page focus restore: Restoring country selection:", backup.countryLabel);
              setSelectedCountryId(backupCountry.value);
              setFilters((prev: any) => ({
                ...prev,
                country: backupCountry.value
              }));
              countryInitialized.current = true;
            }
          }
        } catch (e) {
          console.error("Error parsing navigation backup on focus:", e);
        }
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [selectedCountryId, countryOptions]);

  // 🛒 Handle cart country override only when there's a real conflict
  useEffect(() => {
    // Skip initialization for draft campaigns to prevent overriding draft country
    const urlParams = new URLSearchParams(window.location.search);
    const isDraft = urlParams.get('edit') === 'true';
    if (isDraft) {
      console.log('📝 Skipping unified initialization for draft campaign');
      return;
    }

    // Wait for both country options and cart data to be loaded
    if (countryInitialized.current || countryOptions.length === 0 || !cartDataLoaded) {
      console.log("⏭️ Skipping initialization - not ready yet");
      return;
    }
    // Only override if cart country exists, cart has items, and it's different from current selection
    if (cartDataList.length > 0 && cartDataList[0]?.campaignInventories?.length > 0 && countryOptions.length > 0 && !countryInitialized.current) {
      const cartCountry = cartDataList[0]?.campaignInventories?.[0]?.billboardCountry;

      if (cartCountry) {
        const cartCountryObj = countryOptions.find((c: any) => c.label === cartCountry);

        // Only override if cart country is different from current selected country
        if (cartCountryObj && selectedCountryId !== cartCountryObj.value) {
          console.log('🛒 Cart country override - changing from', selectedCountryId, 'to', cartCountryObj.value);
          setSelectedCountryId(cartCountryObj.value);
          setFilters((prev: any) => ({
            ...prev,
            country: cartCountryObj.value
          }));

          // Update tax info
          if (cartCountryObj.tax) {
            const taxLabel = cartCountryObj.tax.label || 'Tax';
            const taxPercent = cartCountryObj.tax.percent || 0;
            updateTaxInfo(taxLabel, taxPercent);
          }
        }
      }
    }
  }, [cartDataList.length, countryOptions, updateTaxInfo, edit]);

  const { data: stateData, loading: stateApiLoading, refetch: refetchStates } = useQuery(GET_STATE_LIST_MARKETPLACE, {
    variables: { countryIds: selectedCountryId, exposure: exposure },
    skip: !selectedCountryId || import.meta.env.VITE_APP_MOCK_DATA === 'true',
    fetchPolicy: 'network-only',
  });

  // Refetch states when country changes
  useEffect(() => {
    if (selectedCountryId && !edit && refetchStates) {
      console.log('🔄 Country changed, refetching states for:', selectedCountryId);
      refetchStates();
    }
  }, [selectedCountryId, refetchStates, edit]);

  const [getDistricts, { data: districtData, loading: districtApiLoading }] = useLazyQuery(GET_DISTRICT_LIST_MARKETPLACE);

  const isPageLoading = (filters.category === 'packages' ? packageDataLoading : billBoardDataLoading) || stateApiLoading || districtApiLoading || countryLoading;

  // Removed conflicting localStorage useEffect - now handled in unified initialization above

  // Multi-select dropdown states for country, state, district
  // Sync filters.country with selectedCountryId
  useEffect(() => {
    // Avoid unnecessary state updates if already matched
    setFilters((prev: any) => {
      if (prev.country === selectedCountryId) return prev;
      return { ...prev, country: selectedCountryId };
    });
  }, [selectedCountryId]);
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [showStateDropdown, setShowStateDropdown] = useState(false);
  const [showDistrictDropdown, setShowDistrictDropdown] = useState(false);

  // Mock data generation for states
  const generateMockStates = async () => {
    try {
      setStatesLoading(true);
      const mockResponse = await generateMockData(GET_STATE_LIST);
      const states = mockResponse.data.stateList || [];
      setAvailableStates(states);
    } catch (error) {
      console.error('Error generating mock states:', error);
      toast({
        title: "Error",
        description: "Failed to load mock state data.",
        variant: "destructive"
      });
      // toast.error('Failed to load mock state data.');
      setAvailableStates([]);
    } finally {
      setStatesLoading(false);
    }
  };

  // Mock data generation for districts
  const generateMockDistricts = async () => {
    try {
      setDistrictsLoading(true);
      const mockResponse = await generateMockData(GET_DISTRICT_LIST_MARKETPLACE);
      const districts = mockResponse.data.districtListMarketplace || [];
      setAvailableDistricts(districts);
    } catch (error) {
      console.error('Error generating mock districts:', error);
      toast({
        title: "Error",
        description: "Failed to load mock district data.",
        variant: "destructive"
      });
      // toast.error('Failed to load mock district data.');
      setAvailableDistricts([]);
    } finally {
      setDistrictsLoading(false);
    }
  };

  // Fetch states on mount
  useEffect(() => {
    if (import.meta.env.VITE_APP_MOCK_DATA === 'true') {
      generateMockStates();
    } else {
      if (stateData?.stateListMarketplace) {
        setAvailableStates(stateData.stateListMarketplace);
      }
      setStatesLoading(stateApiLoading);
    }
  }, [stateData, stateApiLoading]);

  // Fetch districts when state changes
  useEffect(() => {
    console.log('District fetch effect triggered, state:', state);
    if (!state.length) {
      setAvailableDistricts([]);
      setDistrict([]);
      updateFilter('district', [], true);
      return;
    }
    if (import.meta.env.VITE_APP_MOCK_DATA === 'true') {
      generateMockDistricts();
    } else {
      getDistricts({ variables: { stateIds: Array.from(new Set(state)).filter(Boolean).join(','), exposure: exposure }, fetchPolicy: 'network-only' });
    }
  }, [state, getDistricts]);

  // Update districts when district data changes
  useEffect(() => {
    if (import.meta.env.VITE_APP_MOCK_DATA !== 'true' && districtData?.districtListMarketplace) {
      setAvailableDistricts(districtData.districtListMarketplace);
      setDistrictsLoading(districtApiLoading);
    }
  }, [districtData, districtApiLoading]);

  // Reset state and district when country changes
  useEffect(() => {
    // Skip if country not initialized yet or during draft/edit mode
    if (!countryInitialized.current || edit) return;
    
    // Only reset if we have a valid country selected
    if (!selectedCountryId) return;
    
    console.log('🔄 Country changed, resetting state and district filters');
    
    // Reset state and district selections
    setState([]);
    setDistrict([]);
    setAvailableDistricts([]);
    
    // Update filters to clear state/district
    setFilters((prev: any) => ({
      ...prev,
      state: [],
      district: [],
    }));
    
    // Clear localStorage
    localStorage.removeItem('campaignListState');
    
  }, [selectedCountryId, edit]);

  const flowType: 'create-campaign' | 'edit-campaign' | 'explore' = 'create-campaign';

  // Explore flow has been removed


  // First, declare fetchAllBillboardData function with loading guard
  const fetchAllBillboardData = async (searchValue = search, page = currentPage, cartBillboardIds = new Set<string>(), overrideBillboardType?: string, overrideState?: string[], overridePriceRange?: [number, number] | { minPrice?: number; maxPrice?: number }, countryId?: string, overrideDistrict?: string[], overrideCompanyId?: string, overrideVenueType?: string[]) => {

    // Add console logging for price override
    if (overridePriceRange) {
      console.log('🎯 fetchAllBillboardData called with overridePriceRange:', overridePriceRange);
      console.log('🌍 fetchAllBillboardData countryId:', selectedCountryId);
    }

    // Unified effective country resolution
    const effectiveCountryId = getEffectiveCountryId(countryId);
    console.log('🌐 Effective country resolved:', {
      passedParam: countryId,
      selectedCountryId,
      effectiveCountryId
    });

    console.log('🚀 API Call Country Check:', {
      effectiveCountryId,
      selectedCountryId,
      countryId,
      edit,
      draftCountrySet: edit && draftData?.paymentInvoice?.priceSummary?.currency?.code
    });
    if (!effectiveCountryId || countryOptions.length === 0) {
      console.log('🚫 Skipping API call: No valid country selected or country options not loaded');
      console.log('effectiveCountryId:', effectiveCountryId, 'countryOptions.length:', countryOptions.length);
      return;
    }

    // ✅ Deduplication: Skip if same fetch already requested recently (within last 1 second)
    const effectiveBillboardType = overrideBillboardType || billboardType;
    const now = Date.now();
    if (
      lastBillboardFetchRef.current.countryId === effectiveCountryId &&
      lastBillboardFetchRef.current.page === page &&
      lastBillboardFetchRef.current.type === effectiveBillboardType &&
      lastBillboardFetchRef.current.timestamp &&
      now - lastBillboardFetchRef.current.timestamp < 1000 // Within 1 second
    ) {
      console.log('⏭️ Skipping duplicate billboard fetch - same params within 1 second:', {
        countryId: effectiveCountryId,
        page,
        type: effectiveBillboardType
      });
      return;
    }

    // Update the last fetch ref
    lastBillboardFetchRef.current = {
      countryId: effectiveCountryId,
      page,
      type: effectiveBillboardType,
      timestamp: now
    };

    // Prevent multiple simultaneous calls
    if (billBoardDataLoading) {
      return;
    }

    const effectiveState = overrideState !== undefined ? overrideState : state;
    const effectiveDistrict = overrideDistrict !== undefined ? overrideDistrict : district;
    const stateIdString = Array.from(new Set(effectiveState)).filter(Boolean).join(',');

    console.log('🚀 State check - stateIdString:', stateIdString);
    console.log('🚀 State check - district.length:', district.length);
    console.log('🚀 State check - filters.priceRange:', filters.priceRange);
    console.log('🚀 State check - overrideState:', overrideState);
    console.log('🚀 State check - overrideDistrict:', overrideDistrict);

    // Check for price-only filtering (when overridePriceRange is provided, allow empty state)
    const isPriceOnlyFilter = overridePriceRange && (typeof overridePriceRange === 'object' && !Array.isArray(overridePriceRange));
    console.log('🚀 isPriceOnlyFilter:', isPriceOnlyFilter);

    // Allow API call when country changes (overrideState is explicitly passed as empty array)
    const isCountryChange = overrideState !== undefined && overrideState.length === 0;
    console.log('🚀 isCountryChange:', isCountryChange);

    // Skip API call only if state is empty AND other filters are applied (but NOT during country change)
    if (!stateIdString && !isCountryChange && (district.length > 0 || (filters.priceRange && !isPriceOnlyFilter))) {
      console.log('🚫 Skipping API call: stateId is empty but other filters are applied');
      setNoDataMessage('Please select at least one state to apply filters.');
      return;
    }

    try {
      setBillBoardDataLoading(true);
      setNoDataMessage(null);

      const effectiveStartDate = startDate;
      const effectiveEndDate = endDate;

      if (import.meta.env.VITE_APP_MOCK_DATA === 'true') {
        console.log("Mock data enabled, calling generateMockData...");
        try {
          const mockResponse = await generateMockData(GETPROPERTY_LIST);
          console.log("Mock response received:", mockResponse);

          if (!mockResponse || !mockResponse.data) {
            console.error("Mock data response is null or missing data property");
            setNoDataMessage('Mock data failed to load.');
            setBillBoardDataLoading(false);
            return;
          }

          const allProperties = mockResponse.data.landingpageExploreProperties || [];
          console.log("All properties in mockdata --", allProperties.length, "items");

          if (allProperties.length === 0) {
            console.error("Mock data returned empty array");
            setNoDataMessage('No mock data available.');
            setBillBoardDataLoading(false);
            return;
          }


          const classicProperties = allProperties.filter((p: any) =>
            p.type?.toUpperCase() === 'CLASSIC' || p.type?.toUpperCase() === 'STATIC'
          );
          const digitalProperties = allProperties.filter((p: any) =>
            p.type?.toUpperCase() === 'DIGITAL'
          );

          // Update count for current billboard type only
          const effectiveBillboardType = overrideBillboardType || billboardType;
          if (effectiveBillboardType.toLowerCase() === 'classic') {
            setclasicTotalSize(classicProperties.length);
            // Keep existing digital count, don't update it
          } else {
            setdigitalTotalSize(digitalProperties.length);
            // Keep existing classic count, don't update it
          }

          const filteredProperties = allProperties.filter((property: any) => {
            const propertyType = property.type?.toUpperCase();
            const targetType = effectiveBillboardType.toUpperCase();
            const matchesState = !effectiveState.length || effectiveState.includes(property.stateId);
            const matchesDistrict = !district.length || district.includes(property.districtId);
            return (propertyType === targetType || (targetType === 'CLASSIC' && propertyType === 'STATIC')) &&
              matchesState && matchesDistrict;
          });

          const startIndex = page * dataListLength;
          const endIndex = Math.min(startIndex + dataListLength, filteredProperties.length);
          const paginatedData = filteredProperties.slice(startIndex, endIndex);

          const billboardsWithCartState = paginatedData.map((billboard: any) => ({
            ...billboard,
            isInCart: cartBillboardIds.has(billboard.id),
          }));

          setBillBoardData(billboardsWithCartState);
          setTotalDataLength(filteredProperties.length);

          if (filteredProperties.length > 0) {
            setPageStartData(startIndex + 1);
            setPageEndData(endIndex);
          } else {
            setPageStartData(0);
            setPageEndData(0);
            setNoDataMessage(
              district
                ? `No ${billboardType} inventoires found for the selected district.`
                : effectiveState.length
                  ? `No ${billboardType} inventoires found for the selected state.`
                  : `No ${billboardType} inventoires found for your filters.`
            );
          }

          // Update filteredBillboards with the top 5 from the filtered properties
          const topBillboards = filteredProperties
            .slice(0, 5)
            .map((billboard: any) => ({
              ...billboard,
              isInCart: cartBillboardIds.has(billboard.id),
            }));
          setFilteredBillboards(topBillboards);

          if (viewMode === 'map' && map) {
            setTimeout(() => {
              if (map) {
                map.resize();
                map.flyTo({
                  center: [153.0251, -27.4698],
                  zoom: 5,
                  essential: true,
                });
              }
            }, 100);
          }
        } catch (error) {
          console.error("Error in mock data processing:", error);
          setNoDataMessage('Failed to load mock data.');
          setBillBoardDataLoading(false);
          return;
        }
      } else {
        // Similar logic for API data, ensuring state and district filters are applied
        const categories = locationData.map(category => category.toUpperCase());
        // Use ref value instead of filters.environment to get current value
        const currentEnvironment = currentEnvironmentRef.current;
        if (currentEnvironment && currentEnvironment !== '') {
          categories.push(currentEnvironment.toUpperCase());
          console.log(`🌍 Added environment to categories from ref: ${currentEnvironment.toUpperCase()}`);
        }
        const filter = {
          availableBooking: true,
          categories: categories,
          format: siteTypeData,
          venueType: overrideVenueType !== undefined ? overrideVenueType : venueTypeData,
        };

        // Debug: Log the venue types being sent
        console.log('venueTypeData being sent to GraphQL:', venueTypeData);
        console.log('filter object:', filter);
        console.log(`🎭 Override Check - overrideVenueType: ${JSON.stringify(overrideVenueType)}, final venueType: ${JSON.stringify(filter.venueType)}`);

        console.log(`🚀 Making API call with filters:`, filter);
        console.log(`🎭 VenueType data being sent:`, venueTypeData);
        console.log(`🏢 Selected Media Owner IDs:`, filters.mediaOwner);

        // Use selected media owner ID as companyId, or empty string if no media owner selected
        const selectedCompanyId = (overrideCompanyId !== undefined) 
        ? overrideCompanyId
        : (filters.mediaOwner && filters.mediaOwner.length > 0
          ? filters.mediaOwner.join(',') // Use first selected media owner ID
          : ""); // Send empty string when no media owner is selected
        console.log(`🏢test --->  Final companyId being sent:`, selectedCompanyId, selectedCountryId);
        console.log(`🏢 Override Check - overrideCompanyId: ${overrideCompanyId}, selectedCompanyId: ${selectedCompanyId}`);
        const freshCartCount = await fetchCartCountDetails();
        const hasCartData = freshCartCount > 0;
        console.log('🧹 test ---> Cart count:', freshCartCount, 'hasCartData:', hasCartData);

        // Determine finalCountryId: Cart country > selectedCountryId > countryId
        let finalCountryId = countryId || selectedCountryId;

        // If cart has items, ALWAYS use cart's country
        if (hasCartData && cartDataList.length > 0) {
          console.log('🛒 test --->  Cart has items', hasCartData);
          const cartCountryObj = cartDataList[0]?.campaignInventories?.[0]?.billboardCountry;
          if (cartCountryObj) {
            const cartCountryOption = countryOptions.find((c: any) => c.label === cartCountryObj);
            if (cartCountryOption) {
              finalCountryId = cartCountryOption.value;
              console.log("🛒test --->  Cart has items - using cart country:", cartCountryObj, "finalCountryId:", finalCountryId);
            }
          }
        }
        else if (!hasCartData && cartDataList.length === 0 && contextCartItems.length > 0) {
        console.log('🛒 test ---> Cart has items but cartDataList not yet populated, checking userCartData');
        try {
          const userCartDataStr = localStorage.getItem('userCartData');
          if (userCartDataStr) {
            const userCartData = JSON.parse(userCartDataStr);
            if (userCartData && userCartData.length > 0) {
              const cartItemCountryId = userCartData[0]?.cartItemCountryId;
              if (cartItemCountryId) {
                finalCountryId = cartItemCountryId;
                console.log('✅ Cart country ID from userCartData:', cartItemCountryId);
              }
            }
          }
        } catch (error) {
          console.error("❌ Error parsing userCartData from localStorage:", error);
        }
      }

        else if (edit) {
          console.log('🧹 test ---> Cart is empty draft--->', hasCartData);
          const currentSelected = localStorage.getItem('selectedCountryId');
          if (currentSelected) {
            finalCountryId = currentSelected;
            console.log('✏️ test ---> Edit Mode - keeping selectedCountryId:', finalCountryId);
          }
          // if (finalCountryId) {
          //   console.log('🧹 test ---> Final country id before condition Cart empty draft - using finalCountryId param:', finalCountryId);
          //   finalCountryId = selectedCountryId ?? countryId ?? '';
          //   console.log('🧹 test ---> Cart empty draft - using finalCountryId:', finalCountryId);
          // }
        }
        else {
          console.log('🧹 test ---> Cart is empty or has no items', hasCartData);
          // If cart is empty, use session default country
          const clearedAllCart = localStorage.getItem('clearedAllCart') === 'true';
          if (clearedAllCart && freshCartCount === 0) {
            const sessionDefaultCountryId = localStorage.getItem('sessionDefaultCountryId');
            if (sessionDefaultCountryId) {
              localStorage.setItem('selectedCountryId', sessionDefaultCountryId);
              finalCountryId = sessionDefaultCountryId;
              console.log('🧹 test ---> Cart empty - using sessionDefaultCountryId:', sessionDefaultCountryId);
            }
            localStorage.removeItem('clearedAllCart');
          }
        }
        console.log("test ---> final country id for API payload--->", finalCountryId);

        if (finalCountryId && finalCountryId !== selectedCountryId) {
          console.log('🔄 Syncing dropdown country to API country:', {
            oldSelectedCountryId: selectedCountryId,
            newFinalCountryId: finalCountryId
          });
          setSelectedCountryId(finalCountryId);
          setFilters((prev: any) => ({
            ...prev,
            country: finalCountryId,
          }));
        }
        console.log("test ---> final country id for API payload after country sync--->", finalCountryId);

        const commonVariables = {
          accessToken: token,
          sort: "last_modified_date,desc",
          userId: userId,
          companyId: selectedCompanyId, // Use selected media owner ID or empty string
          page: page,
          size: dataListLength,
          startDate: safeFormatDate(effectiveStartDate, 'dd MMMM yyyy'),
          endDate: safeFormatDate(effectiveEndDate, 'dd MMMM yyyy'),
          dspName: exposure,
          countryId: finalCountryId, // Use sessionDefaultCountryId if cart is empty, else old value
          stateId: Array.from(new Set(effectiveState)).filter(Boolean).join(','),
          districtId: Array.from(new Set(effectiveDistrict)).filter(Boolean).join(','),
          searchTerm: searchValue,
          // Handle selective price range - only include entered values
          ...(overridePriceRange ?
            (typeof overridePriceRange === 'object' && !Array.isArray(overridePriceRange) ?
              overridePriceRange : // Use selective price object directly
              {
                minPrice: overridePriceRange[0],
                maxPrice: overridePriceRange[1],
              }
            ) : filtersRef.current.priceRange ? {
              minPrice: filtersRef.current.priceRange[0],
              maxPrice: filtersRef.current.priceRange[1],
            } : {}),
        };

        // Log the price values being sent to API
        if (overridePriceRange) {
          if (typeof overridePriceRange === 'object' && !Array.isArray(overridePriceRange)) {
            console.log('🎯 API call using selective price range:', overridePriceRange);
          } else {
            console.log('🎯 API call using overridePriceRange array:', overridePriceRange);
          }
        } else if (filtersRef.current.priceRange) {
          console.log('📊 API call using filtersRef priceRange:', filtersRef.current.priceRange);
        } else {
          console.log('❌ API call with no price range');
        }

        // OPTIMIZED: Single API call for current billboard type only
        const effectiveBillboardType = overrideBillboardType || billboardType;
        console.log('[API CALL] spotBasedBookingOnly value:', spotBasedBooking);

        // Build variables for API call
        const gqlVariables: any = {
          ...commonVariables,
          page: page,
          size: dataListLength,
          billboard: effectiveBillboardType,
          filterData: { ...filter, type: effectiveBillboardType.toUpperCase() },
          isCurated: true,
        };
        if (effectiveBillboardType.toLowerCase() === 'digital') {
          gqlVariables.spotBasedBookingOnly = spotBasedBooking;
        }

        const response = await client.query({
          query: GETPROPERTY_LIST,
          variables: gqlVariables,
          fetchPolicy: 'no-cache', // Always fetch fresh data
        });

        const currentBillboards = response.data.landingpageExploreProperties || [];

        // Update count for current billboard type only
        const currentTypeTotal = currentBillboards[0]?.totalSize || 0;
        if (effectiveBillboardType.toLowerCase() === 'classic') {
          setclasicTotalSize(currentTypeTotal);
          // Keep existing digital count, don't fetch it
        } else {
          setdigitalTotalSize(currentTypeTotal);
          // Keep existing classic count, don't fetch it
        }

        // Update billboard data with cart status
        const billboardsWithCartState = currentBillboards.map((billboard: any) => ({
          ...billboard,
          isInCart: cartBillboardIds.has(billboard.id),
          nextAvailableDate: billboard.nextAvailableDate || format(new Date(), 'yyyy-MM-dd'),
        }));

        setBillBoardData(billboardsWithCartState);
        setTotalDataLength(currentBillboards[0]?.totalSize || 0);

        // Update pagination data
        if (currentBillboards.length > 0) {
          const totalSize = currentBillboards[0].totalSize;
          if (page === 0) {
            setPageStartData(1);
            setPageEndData(Math.min(dataListLength, totalSize));
          } else {
            const curStartData = dataListLength * page;
            setPageStartData(curStartData + 1);
            setPageEndData(Math.min(curStartData + dataListLength, totalSize));
          }
        } else {
          setPageStartData(0);
          setPageEndData(0);
          setNoDataMessage(
            district
              ? `No ${billboardType} inventories found for the selected district.`
              : state
                ? `No ${billboardType} inventories found for the selected state.`
                : `No ${billboardType} inventories found for your filters.`
          );
        }

        // Update filteredBillboards with the top 5 from the current billboards
        const topBillboards = currentBillboards
          .slice(0, 5)
          .map((billboard: any) => ({
            ...billboard,
            isInCart: cartBillboardIds.has(billboard.id),
            nextAvailableDate: billboard.nextAvailableDate || format(new Date(), 'yyyy-MM-dd'),
          }));
        setFilteredBillboards(topBillboards);

        if (viewMode === 'map' && map) {
          setTimeout(() => {
            if (map) {
              map.resize();
              map.flyTo({
                center: [153.0251, -27.4698],
                zoom: 5,
                essential: true,
              });
            }
          }, 100);
        }
      }
    } catch (error) {
      console.error("Error in fetchAllBillboardData:", error);
      setNoDataMessage(
        district
          ? `No ${billboardType} inventories found for the selected district.`
          : state
            ? `No ${billboardType} inventories found for the selected state.`
            : `No ${billboardType} inventories found for your filters.`
      );
      setBillBoardData([]);
      setFilteredBillboards([]);
      setTotalDataLength(0);
      setPageStartData(0);
      setPageEndData(0);
      toast({
        title: "Error",
        description: "Failed to load billboard data.",
        variant: "destructive"
      });
    } finally {
      setBillBoardDataLoading(false);
    }
  };

  // Function to fetch packages using PACKAGE_BILLBOARD query
  const fetchPackageData = async (cartPackageIds = new Set<string>(), countryId?: string, searchValue?: string) => {
    const effectiveCountryId = getEffectiveCountryId(countryId);
    console.log('📦 fetchPackageData', { passedParam: countryId, resolved: effectiveCountryId, selectedCountryId });
    if (!effectiveCountryId || countryOptions.length === 0) {
      console.log('🚫 Skipping package API call: no valid country');
      return;
    }

    // ✅ Deduplication: Skip if same package fetch already requested recently (within last 1 second)
    const now = Date.now();
    if (
      lastPackageFetchRef.current.countryId === effectiveCountryId &&
      lastPackageFetchRef.current.page === 0 && // packages typically use page 0
      lastPackageFetchRef.current.timestamp &&
      now - lastPackageFetchRef.current.timestamp < 1000 // Within 1 second
    ) {
      console.log('⏭️ Skipping duplicate package fetch - same params within 1 second:', {
        countryId: effectiveCountryId
      });
      return;
    }

    // Update the last fetch ref
    lastPackageFetchRef.current = {
      countryId: effectiveCountryId,
      page: 0,
      timestamp: now
    };

    // Prevent multiple simultaneous calls
    if (packageDataLoading) {
      console.log('📦 fetchPackageData skipped: already loading');
      return;
    }

    const stateIdString = Array.from(new Set(state)).filter(Boolean).join(',');
    if (!stateIdString && district.length > 0) {
      console.log('Skipping package API call: stateId is empty but districts are selected');
      setNoDataMessage('Please select at least one state to load packages.');
      return;
    }

    try {
      setPackageDataLoading(true);
      setNoDataMessage(null);

      const effectiveStartDate = startDate;
      const effectiveEndDate = endDate;

      console.log("🎁 Fetching package data...");

      if (import.meta.env.VITE_APP_MOCK_DATA === 'true') {
        // For mock data, use generateMockData
        try {
          const mockResponse = await generateMockData(PACKAGE_BILLBOARD);
          console.log("Mock package response received:", mockResponse);

          if (!mockResponse || !mockResponse.data) {
            console.error("Mock package data response is null or missing data property");
            setNoDataMessage('Mock package data failed to load.');
            setPackageDataLoading(false);
            return;
          }

          const packages = mockResponse.data.packageBillboard || [];
          console.log("Packages in mockdata before search filter:", packages.length, "items");

          // Apply search filter to mock data if search value is provided
          let filteredPackages = packages;
          const effectiveSearchValue = searchValue !== undefined ? searchValue : search;
          if (effectiveSearchValue && effectiveSearchValue.trim().length > 0) {
            console.log("Applying search filter to mock packages with search term:", effectiveSearchValue);
            filteredPackages = packages.filter((pkg: any) => {
              const searchTerm = effectiveSearchValue.toLowerCase();
              return (
                (pkg.displayName && pkg.displayName.toLowerCase().includes(searchTerm)) ||
                (pkg.name && pkg.name.toLowerCase().includes(searchTerm)) ||
                (pkg.description && pkg.description.toLowerCase().includes(searchTerm)) ||
                (pkg.location && pkg.location.toLowerCase().includes(searchTerm))
              );
            });
            console.log("Packages after search filter:", filteredPackages.length, "items");
          }

          console.log("Final packages to display:", filteredPackages.length, "items");

          if (filteredPackages.length === 0) {
            setNoDataMessage(effectiveSearchValue ? 'No packages found for your search.' : 'No packages available.');
            setPackageData([]);
            setPackageDataLoading(false);
            return;
          }

          // Add cart status to packages
          const packagesWithCartState = filteredPackages.map((pkg: any) => ({
            ...pkg,
            isInCart: cartPackageIds.has(pkg.id),
          }));

          setPackageData(packagesWithCartState);

        } catch (error) {
          console.error("Error in mock package data processing:", error);
          setNoDataMessage('Failed to load mock package data.');
          setPackageDataLoading(false);
          return;
        }
      } else {
        // Real API call
        try {
          // Use selected media owner ID as companyId, or empty string if no media owner selected
          const selectedCompanyId = filters.mediaOwner && filters.mediaOwner.length > 0
            ? filters.mediaOwner.join(',')
            : "";

          console.log("countryIdForStates in start campaign page--->", filters.country);
          console.log("📦 Package API call variables:", {
            searchTerm: searchValue !== undefined ? searchValue : search,
            searchValue,
            search,
            countryId: countryId || selectedCountryId,
            publisherId: selectedCompanyId
          });

          const { data } = await client.query({
            query: PACKAGE_BILLBOARD,
            variables: {
              accessToken: token,
              page: 0,
              size: 10,
              sort: "last_modified_date,desc",
              startDate: safeFormatDate(effectiveStartDate, 'yyyy-MM-dd'),
              endDate: safeFormatDate(effectiveEndDate, 'yyyy-MM-dd'),
              exposure: exposure,
              packageDate: safeFormatDate(effectiveStartDate, 'yyyy-MM-dd'),
              packageType: "DAYWISE",
              publisherId: selectedCompanyId, // Empty to fetch all publishers
              countryId: countryId || selectedCountryId, // Use passed countryId or fallback to state
              searchTerm: searchValue !== undefined ? searchValue : search,
            },
            fetchPolicy: 'no-cache', // Always fetch fresh data
          });

          console.log("Package API response:", data);

          const packages = data?.packageBillboard || [];

          if (packages.length === 0) {
            setNoDataMessage('No packages available for the selected criteria.');
            setPackageData([]);
            return;
          }

          // Add cart status to packages
          const packagesWithCartState = packages.map((pkg: any) => ({
            ...pkg,
            isInCart: cartPackageIds.has(pkg.id),
          }));

          setPackageData(packagesWithCartState);

        } catch (error) {
          console.error("Error fetching package data:", error);
          setNoDataMessage('Failed to load package data.');
          setPackageData([]);
          toast({
            title: "Error",
            description: "Failed to load package data.",
            variant: "destructive"
          });
        }
      }
    } catch (error) {
      console.error("Error in fetchPackageData:", error);
      setNoDataMessage('Failed to load package data.');
      setPackageData([]);
      toast({
        title: "Error",
        description: "Failed to load package data.",
        variant: "destructive"
      });
    } finally {
      setPackageDataLoading(false);
    }
  };

  // Helper function to handle billboard type change
  const handleBillboardTypeChange = useCallback((newType: string) => {
    if (billboardType !== newType) {
      // Set flag to prevent automatic API calls during manual change
      isManualBillboardTypeChange.current = true;

      setBillboardType(newType);

      // Reset page to first page when switching billboard types
      if (currentPage !== 0) {
        setCurrentPage(0);
      }

      // Clear any no data message
      setNoDataMessage(null);

      // Immediately trigger API call with the NEW billboard type
      const cartBillboardIds = new Set(cartDataList.flatMap((cart: any) =>
        cart.campaignInventories?.map((inv: any) => inv.billboardId)
      ).filter(Boolean));

      // Direct API call with new type
      fetchAllBillboardData(search, 0, cartBillboardIds, newType).finally(() => {
        // Reset flag after API call completes
        setTimeout(() => {
          isManualBillboardTypeChange.current = false;
        }, 500);
      });
    }
  }, [billboardType, currentPage, search, cartDataList, fetchAllBillboardData]);

  // Memoize with stable dependencies
  const fetchAllBillboardDataMemo = useCallback(fetchAllBillboardData, [
    search,
    currentPage,
    startDate,
    endDate,
    billboardType,
    JSON.stringify(locationData), // Stringify arrays to prevent unnecessary re-runs
    JSON.stringify(siteTypeData),
    JSON.stringify(venueTypeData), // Restore venueTypeData dependency for closure
    JSON.stringify(filters.mediaOwner || []), // Add media owner filter dependency
    JSON.stringify(filters.priceRange ?? null),
    token,
    userId,
    selectedCountryId,
    dataListLength,
    state,
    district,
    handleBillboardTypeChange,
  ]);

  // Single debounced function for all API calls to prevent conflicts
  const debouncedFetchAllBillboardData = useMemo(
    () => debounce((searchValue: string, page: number, cartBillboardIds: Set<string>, reason: string = 'unknown', stateIds?: string[], districtIds?: string[], countryId?: string) => {


      // Skip automatic calls during manual billboard type or state changes
      // Price changes use direct API calls and don't need debouncing protection
      if (isManualBillboardTypeChange.current || isManualStateChange.current || isManualDistrictChange.current || isManualCountryChange.current) {

        return;
      }

      // Correct parameter order: searchValue, page, cartBillboardIds, overrideBillboardType, overrideState, overridePriceRange, countryId, overrideDistrict
      fetchAllBillboardDataMemo(searchValue, page, cartBillboardIds, undefined, stateIds, undefined, countryId, districtIds);
    }, 300, { leading: false, trailing: true }), // Single debounce for all calls
    [fetchAllBillboardDataMemo, selectedCountryId] // Include selectedCountryId to fix closure issues
  );

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  useEffect(() => {
    // Only trigger if not during initial mount
    if (hasInitialized.current) {
      setCurrentPage(0); // Reset pagination to first page when filter changes
      const cartBillboardIds = new Set(cartDataList.flatMap((cart: any) =>
        cart.campaignInventories?.map((inv: any) => inv.billboardId)
      ).filter(Boolean));
      fetchAllBillboardData(search, 0, cartBillboardIds, undefined, undefined, undefined, selectedCountryId);
    } else {
      hasInitialized.current = true;
    }
  }, [spotBasedBooking]);

  // const isValidDate = (date: string) => !isNaN(Date.parse(date));
  // const validStartDate = isValidDate(cartformData?.startDate)
  //   ? new Date(cartformData.startDate)
  //   : new Date(formData.startDate);

  // const validEndDate = isValidDate(cartformData?.endDate)
  //   ? new Date(cartformData.endDate)
  //   : new Date(formData.endDate);

  // Set edit flag based on where the user is coming from

  const mapInitialized = useRef(false); // Track if map is initialized

  useEffect(() => {

    if (viewMode !== 'map' || !mapContainer.current || mapInitialized.current) return;

    const initializeMap = () => {
      try {
        mapboxgl.accessToken = MAPBOX_TOKEN;

        const newMap = new mapboxgl.Map({
          container: mapContainer.current!,
          style: mapMode === 'map'
            ? 'mapbox://styles/mapbox/streets-v11'
            : 'mapbox://styles/mapbox/satellite-v9',
          center: [153.0251, -27.4698], // Default center
          zoom: 10,
        });

        newMap.addControl(new mapboxgl.NavigationControl());

        // Handle map load event
        newMap.on('load', () => {
          newMap.resize();

          // Create markers after map is loaded if data is available
          if (billBoardData && billBoardData.length > 0) {

            // Clear any existing markers first
            markers.forEach(marker => {
              try {
                marker.remove();
              } catch (error) {
                console.error('Error removing marker:', error);
              }
            });

            // Create and add markers
            const initialMarkers = billBoardData
              .filter((billboard: any) =>
                billboard.latitude != null &&
                billboard.longitude != null &&
                !isNaN(billboard.latitude) &&
                !isNaN(billboard.longitude)
              )
              .map((billboard: any) => {
                try {
                  // Create popup for billboard
                  const popupContent = `
                    <div style="width: 200px; padding: 10px;">
                      <h6>${billboard.displayName || 'Unknown'}</h6>
                      <p class="mb-1"><small>${billboard.stateName || 'N/A'}</small></p>
                      <p class="mb-1"><small>${billboard?.price?.localCurrencies?.code || 'N/A'} ${billboard.type === 'DIGITAL' ? (priceFormat === 'spot-based' ? (billboard.perSpotPrice || 'N/A') + '/Spot' : (billboard.perDayPrice || 'N/A') + '/Day') : ((billboard.sellingRate?.monthly?.month1 && billboard.sellingRate.monthly.month1 > 0) ? billboard.sellingRate.monthly.month1 : (billboard.currentPrice || 'N/A')) + '/Month'}</small></p>
                      <button class="btn btn-outline-secondary btn-sm w-100 mt-1" onclick="document.getElementById('view-details-${billboard.id}').click()">
                        View Details
                      </button>
                    </div>
                  `;

                  const popup = new mapboxgl.Popup({
                    offset: 25,
                    closeOnClick: false,
                  }).setHTML(popupContent);

                  const marker = new mapboxgl.Marker({
                    color: billboard.availableBooking ? '#28a745' : '#dc3545',
                  })
                    .setLngLat([billboard.longitude, billboard.latitude])
                    .setPopup(popup)
                    .addTo(newMap);

                  return marker;
                } catch (error) {
                  console.error(`Error creating marker for billboard ${billboard.id}:`, error);
                  return null;
                }
              })
              .filter((marker: any): marker is mapboxgl.Marker => marker !== null);

            setMarkers(initialMarkers);

            // Fit bounds to show all markers
            if (initialMarkers.length > 0) {
              try {
                const bounds = new mapboxgl.LngLatBounds();
                let hasValidBounds = false;

                initialMarkers.forEach((marker: any) => {
                  try {
                    const [lng, lat] = marker.getLngLat().toArray();
                    bounds.extend([lng, lat]);
                    hasValidBounds = true;
                  } catch (error) {
                    console.error('Error extending bounds with marker:', error);
                  }
                });

                if (hasValidBounds) {
                  newMap.fitBounds(bounds, {
                    padding: 100,
                    maxZoom: 15,
                  });
                }
              } catch (error) {
                console.error('Error fitting map bounds:', error);
              }
            }
          }
        });

        // Error handling
        newMap.on('error', (e) => {
          console.error('Mapbox error:', e);
        });

        setMap(newMap);
        mapInitialized.current = true;

        return () => {
          // Remove all markers before removing the map
          markers.forEach(marker => {
            try {
              marker.remove();
            } catch (error) {
              console.error('Error removing marker during cleanup:', error);
            }
          });
          setMarkers([]);

          newMap.remove();
          setMap(null);
          mapInitialized.current = false;
        };
      } catch (error) {
        console.error('Error initializing Mapbox:', error);
        setMap(null);
        mapInitialized.current = false;
        return undefined;
      }
    };

    const cleanup = initializeMap();
    return () => {
      if (cleanup) cleanup();
    };
  }, [viewMode, mapMode, billBoardData, loadingCartItemIds]);


  const isFieldEnabled = (key: string, config: ConfigPage[]) => {
    const fieldConfig = config
      .flatMap(page => page.configurations || [])
      .flatMap(cfg => cfg.fields || [])
      .find(field => field.key === key);
    return fieldConfig ? fieldConfig.default : true;
  };

  // Update map markers when billBoardData changes
  useEffect(() => {
    if (!map || viewMode !== 'map') return;

    // Even if billboardData is empty, we should still clean up the markers
    // Clear existing markers
    markers.forEach(marker => {
      try {
        marker.remove();
      } catch (error) {
        console.error('Error removing marker:', error);
      }
    });
    setMarkers([]);

    // If there's no billboard data, log it but continue to show map without markers
    if (!billBoardData?.length) {
      // Center map on default location
      try {
        map.flyTo({
          center: [153.0251, -27.4698],
          zoom: 5,
          essential: true
        });

        // Make sure the map is fully rendered even with no data
        setTimeout(() => {
          if (map) map.resize();
        }, 100);
      } catch (error) {
        console.error("Error resetting map view:", error);
      }
      return;
    }

    // Add new markers
    const newMarkers: mapboxgl.Marker[] = billBoardData
      .filter((billboard: any) =>
        billboard.latitude != null &&
        billboard.longitude != null &&
        !isNaN(billboard.latitude) &&
        !isNaN(billboard.longitude)
      )
      .map((billboard: any) => {
        try {

          const popupContent = `
          <div style="width: 200px; padding: 10px;">
            <h6>${billboard.displayName || 'Unknown'}</h6>
            <p class="mb-1"><small>${billboard.stateName || 'N/A'}</small></p>
            <p class="mb-1"><small>${billboard?.price?.localCurrencies?.code || 'N/A'} ${billboard.type === 'DIGITAL' ? (priceFormat === 'spot-based' ? (billboard.perSpotPrice || 'N/A') + '/Spot' : (billboard.perDayPrice || 'N/A') + '/Day') : ((billboard.sellingRate?.monthly?.month1 && billboard.sellingRate.monthly.month1 > 0) ? billboard.sellingRate.monthly.month1 : (billboard.currentPrice || 'N/A')) + '/Month'}</small></p>
            <button class="btn btn-outline-secondary btn-sm w-100 mt-1" onclick="document.getElementById('view-details-${billboard.id}').click()">
              View Details
            </button>
          </div>
        `;

          const popup = new mapboxgl.Popup({
            offset: 25,
            closeOnClick: false,
          }).setHTML(popupContent);

          const marker = new mapboxgl.Marker({
            color: billboard.availableBooking ? '#28a745' : '#dc3545',
          })
            .setLngLat([billboard.longitude, billboard.latitude])
            .setPopup(popup);

          // Only add to map if valid
          if (map) {
            marker.addTo(map);
            marker.getElement().addEventListener('click', () => {
              if (map) {
                map.flyTo({
                  center: [billboard.longitude, billboard.latitude],
                  zoom: 14,
                });
                popup.addTo(map);
              }
            });
          }

          return marker;
        } catch (error) {
          console.error(`Error creating marker for billboard ${billboard.id}:`, error);
          return null;
        }
      })
      .filter((marker: any): marker is mapboxgl.Marker => marker !== null);

    setMarkers(newMarkers);

    // Fit map to bounds
    if (newMarkers.length > 0 && map) {
      try {
        const bounds = new mapboxgl.LngLatBounds([0, 0, 0, 0]); // Initialize with dummy coordinates
        let hasValidBounds = false;

        newMarkers.forEach(marker => {
          try {
            const [lng, lat] = marker.getLngLat().toArray();
            bounds.extend([lng, lat]);
            hasValidBounds = true;
          } catch (error) {
            console.error('Error extending bounds with marker:', error);
          }
        });

        if (hasValidBounds) {
          map.fitBounds(bounds as mapboxgl.LngLatBoundsLike, {
            padding: 100,
            maxZoom: 15,
          });
        } else {
          // Fallback if bounds calculation fails
          map.flyTo({
            center: [153.0251, -27.4698],
            zoom: 5,
            essential: true
          });
        }
      } catch (error) {
        console.error('Error fitting map bounds:', error);
        // Fallback if fitting bounds fails
        map.flyTo({
          center: [153.0251, -27.4698],
          zoom: 5,
          essential: true
        });
      }
    } else if (map) {
      // Reset view if no markers
      try {
        map.flyTo({
          center: [153.0251, -27.4698],
          zoom: 5,
          essential: true
        });
      } catch (error) {
        console.error("Error resetting map view:", error);
      }
    }
  }, [map, billBoardData, loadingCartItemIds, viewMode, billboardType]);

  // Update map style when mapMode changes
  // Update map style when mapMode changes
  useEffect(() => {
    if (map) {
      map.setStyle(
        mapMode === 'map'
          ? 'mapbox://styles/mapbox/streets-v11'
          : 'mapbox://styles/mapbox/satellite-v9'
      );
    }
  }, [map, mapMode]);

  // Make sure map is properly sized when in map view
  useEffect(() => {
    if (!map || viewMode !== 'map') return;

    // Even if the map is already initialized, we want to make sure it's properly sized
    // This helps with scenarios where we switch to map view but have no data
    setTimeout(() => {
      try {
        if (map) {
          map.resize();
        }
      } catch (error) {
        console.error("Error resizing map:", error);
      }
    }, 100);

  }, [map, viewMode, noDataMessage]);

  // Keep track of cart items in localStorage
  useEffect(() => {
    localStorage.setItem('campaignCart', JSON.stringify(cartItems));
  }, [cartItems]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (categoryRef.current && !categoryRef.current.contains(event.target as Node)) {
        setShowCategoryDropdown(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleFilterChange = async (filters: BillboardFilters, shouldApply: boolean = true) => {
    console.log('handleFilterChange called with filters:', filters);
    // Fix type mismatches - ensure arrays are properly handled
    const newState = Array.isArray(filters.state) ? filters.state : [filters.state].filter(Boolean);
    const newDistrict = Array.isArray(filters.district) ? filters.district : [filters.district].filter(Boolean);

    if (JSON.stringify(newState) !== JSON.stringify(state)) {
      setState(newState);
      if (shouldApply) {
        localStorage.setItem('billboardFilters_state', newState.join(','));
      }
    }
    if (JSON.stringify(newDistrict) !== JSON.stringify(district)) {
      setDistrict(newDistrict);
      if (shouldApply) {
        localStorage.setItem('billboardFilters_district', newDistrict.join(','));
      }
    }
    if (shouldApply) {
      localStorage.setItem('billboardFilters_environment', JSON.stringify(filters.environment || []));
      localStorage.setItem('billboardFilters_venueTypes', JSON.stringify(filters.venueTypes || []));
      localStorage.setItem('billboardFilters_siteTypes', JSON.stringify(filters.siteTypes || []));
      localStorage.setItem('billboardFilters_size', filters.size || '');
      localStorage.setItem('billboardFilters_priceRange', JSON.stringify(filters.priceRange || [0, 0]));
      try {
        setBillBoardDataLoading(true);
        const cartBillboardIds = new Set(cartDataList.flatMap((cart: any) =>
          cart.campaignInventories?.map((inv: any) => inv.billboardId)
        ).filter(Boolean));
        debouncedFetchAllBillboardData(search, 0, cartBillboardIds, 'handleFilterChange', undefined, undefined, selectedCountryId);
      } catch (error) {
        console.error('Error fetching filtered data:', error);
      } finally {
        setBillBoardDataLoading(false);
      }
    }
  };

  // Trigger API call when venueTypeData changes
  useEffect(() => {
    // Only trigger if venueTypeData changes and is not empty
    if (venueTypeData && venueTypeData.length > 0) {
      handleFilterChange({ ...filters, venueType: venueTypeData });
    }
  }, [venueTypeData]);

  const addToCart = async (items: any[] | any) => {
    // Skip if already processing a cart request
    if (cartRequestInProgress.current) {
      console.log('⏳ Skipping add to cart - already processing');
      return;
    }

    const itemsArray = Array.isArray(items) ? items : [items];

    // Get the IDs of the items to be added
    const itemIds = itemsArray.map(item => item.id || item.inventoryId || item.cartItemId);

    // Check if any of these items are already in the cart
    const existingItemIds = new Set(
      cartDataList.flatMap((cart: any) =>
        cart.campaignInventories?.map((inv: any) => inv.billboardId)
      ).filter(Boolean)
    );

    // Filter out items that are already in the cart or are currently loading
    const itemsToAdd = itemsArray.filter(item => {
      const itemId = item.id || item.inventoryId || item.cartItemId;
      return !existingItemIds.has(itemId) && !loadingCartItemIds.has(itemId);
    });

    // If all items are already in the cart, return early
    if (itemsToAdd.length === 0) {
      if (itemsArray.length === 1 && existingItemIds.has(itemsArray[0].id)) {
        toast({
          title: "Info",
          description: `${itemsArray[0].displayName || 'Item'} is already in your cart.`,
          variant: "info"
        });
      }
      return;
    }

    // Get IDs of items that will be added (not already in cart)
    const newItemIds = itemsToAdd.map(item => item.id || item.inventoryId || item.cartItemId);
    setLoadingCartItemIds(prev => new Set([...prev, ...newItemIds]));

    // Optimistically update isInCart for billboards
    setBillBoardData((prevData: any[]) =>
      prevData.map(billboard =>
        newItemIds.includes(billboard.id) ? { ...billboard, isInCart: true } : billboard
      )
    );
    setFilteredBillboards((prev: any[]) =>
      prev.map(billboard =>
        newItemIds.includes(billboard.id) ? { ...billboard, isInCart: true } : billboard
      )
    );

    // Optimistically update isInCart for packages
    setPackageData((prevData: any[]) =>
      prevData.map(pkg =>
        newItemIds.includes(pkg.id) ? { ...pkg, isInCart: true } : pkg
      )
    );

    // Optimistically update cart count
    const previousCartCount = cartCount;
    setCartCount(prev => prev + itemsToAdd.length);
    setCartItemCountContext(cartCount + itemsToAdd.length); // Sync with cart context optimistically

    if (!startDate || !endDate) {
      console.error('Campaign dates are not set. Please edit the campaign.');
      setLoadingCartItemIds(prev => {
        const newSet = new Set(prev);
        newItemIds.forEach(id => newSet.delete(id));
        return newSet;
      });
      setCartCount(previousCartCount); // Revert cart count
      return;
    }

    const formattedItems = itemsArray.map((item) => ({
      accessToken: token,
      userId: userId || item.userId,
      campaignName: campaignName,
      startDate: safeFormatDate(startDate, 'dd MMMM yyyy'),
      endDate: safeFormatDate(endDate, 'dd MMMM yyyy'),
      cartItemName: item.displayName || item.inventoryName || item.cartItemName,
      cartItemId: item.id || item.inventoryId || item.cartItemId,
      cartItemReferenceId: item.referenceId || item.inventoryReferenceId || item.cartItemReferenceId,
      cartItemType: item.type || item.inventoryType || item.cartItemType,
      cartItemCountry: item.countryName || item.cartItemCountry,
      cartItemVenueType: item.venueType || item.inventoryVenueType || item.cartItemVenueType || "",
      cartItemResolution:
        item.type === 'CLASSIC'
          ? `${item.panelHeight || 0}x${item.panelWidth || 0}`
          : `${item.resolutionHeight || 1080}x${item.resolutionWidth}` || item.cartItemResolution,
      cartItemThumbnailUrl:
        item.fullResImagePath && item.fullResImagePath !== "null"
          ? item.fullResImagePath
          : item.inventoryThumbnailUrl || item.cartItemThumbnailUrl || `${import.meta.env.VITE_APP_FOLDER_PATH}/nomedia.jpg`,
      cartItemLatitude: parseFloat(item.latitude || item.inventoryLatitude || item.cartItemLatitude),
      cartItemLongitude: parseFloat(item.longitude || item.inventoryLongitude || item.cartItemLongitude),
      packageDetails: item.packageDetails ?? "",
      isSpotBased: (item.type?.toLowerCase() === 'digital') ? spotBasedBooking : false,
    }));

    try {
      const { data, errors } = await addtoCartQuery({
        variables: { input: formattedItems },
      });

      if (data) {
        // Also add items to cart context for real-time updates
        itemsToAdd.forEach(item => {
          const cartItem = {
            id: (item.id || item.inventoryId || item.cartItemId).toString(),
            name: item.displayName || item.inventoryName || item.name || "Billboard",
            location: item.formattedAddress || `${item.trDistrictName}, ${item.trStateName}` || item.location || "Unknown",
            state: item.trStateName || item.state || "Unknown",
            price: (item.currentPrice || item.sellingRate?.monthly?.month1 || item.price || 0) * 100, // Convert to cents for context
            impressions: item.impressionPerSpot || item.monthlySummary?.totalVisitors || item.impressions || 0,
            uniqueVisitors: item.monthlySummary?.uniqueVisitors || 0,
            frequency: item.monthlySummary?.frequency || 0,
            size: item.type === 'CLASSIC'
              ? (item.panelWidth && item.panelHeight
                ? `${item.panelWidth}x${item.panelHeight}`
                : 'Standard')
              : (item.resolutionWidth && item.resolutionHeight
                ? `${item.resolutionWidth}x${item.resolutionHeight}`
                : 'Standard'),
            type: item.type === 'CLASSIC' ? 'Classic' : item.type === 'DIGITAL' ? 'Digital' : 'Billboard',
            status: item.availableBooking ? "available" as const : "sold-out" as const,
            image: item.fullResImagePath || item.thumbnailPath || item.imageUrl || item.image || "",
            specifications: {
              resolution: item.type === 'CLASSIC'
                ? (item.panelWidth && item.panelHeight
                  ? `${item.panelWidth}x${item.panelHeight}`
                  : 'Standard')
                : (item.resolutionWidth && item.resolutionHeight
                  ? `${item.resolutionWidth}x${item.resolutionHeight}`
                  : item.specification?.resolution1Width && item.specification?.resolution1Height
                    ? `${item.specification.resolution1Width}x${item.specification.resolution1Height}`
                    : "1920x1080"),
              duration: item.type === 'DIGITAL' ? item?.price?.spotDuration : "Monthly"
            },
            priceFormat,
          };
          addToCartContext(cartItem);
        });

        // Only call fetchCartDetails once - it will update cart count and trigger cartUpdated event
        // Don't call fetchCartCountDetails separately as it's redundant
        await fetchCartDetails();

        // setShowCart(true);
        window.dispatchEvent(new Event('cartUpdated'));
        toast({
          title: "Success",
          description: `${formattedItems.length} item(s) added to cart successfully!`,
          variant: "info"
        })
        // toast.success(`${formattedItems.length} item(s) added to cart successfully!`);
      } else if (errors && errors.length > 0) {
        // Revert optimistic updates
        setBillBoardData((prevData: any[]) =>
          prevData.map(billboard =>
            itemIds.includes(billboard.id) ? { ...billboard, isInCart: false } : billboard
          )
        );
        setFilteredBillboards((prev: any[]) =>
          prev.map(billboard =>
            itemIds.includes(billboard.id) ? { ...billboard, isInCart: false } : billboard
          )
        );
        setPackageData((prevData: any[]) =>
          prevData.map(pkg =>
            itemIds.includes(pkg.id) ? { ...pkg, isInCart: false } : pkg
          )
        );
        setCartCount(previousCartCount); // Revert cart count
        setCartItemCountContext(previousCartCount); // Sync with cart context
        toast({
          title: "Error",
          description: errors[0].message,
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error("Error adding to cart:", error);
      // Revert optimistic updates
      setBillBoardData((prevData: any[]) =>
        prevData.map(billboard =>
          itemIds.includes(billboard.id) ? { ...billboard, isInCart: false } : billboard
        )
      );
      setFilteredBillboards((prev: any[]) =>
        prev.map(billboard =>
          itemIds.includes(billboard.id) ? { ...billboard, isInCart: false } : billboard
        )
      );
      setPackageData((prevData: any[]) =>
        prevData.map(pkg =>
          itemIds.includes(pkg.id) ? { ...pkg, isInCart: false } : pkg
        )
      );
      setCartCount(previousCartCount); // Revert cart count
      setCartItemCountContext(previousCartCount); // Sync with cart context
      toast({
        title: "Error",
        description: "An unexpected error occurred.",
        variant: "destructive"
      });
    } finally {
      // Remove item IDs from the loading set
      setLoadingCartItemIds(prev => {
        const newSet = new Set(prev);
        newItemIds.forEach(id => newSet.delete(id));
        return newSet;
      });
      // Always reset the in-progress flag
      cartRequestInProgress.current = false;
    }
  };

  // Using refs to track if a request is in progress - prevent duplicate API calls
  const cartRequestInProgress = useRef(false);
  const cartCountRequestInProgress = useRef(false);
  const cartDetailsRequestInProgress = useRef(false);
  const lastCartUpdateTime = useRef<number>(0);

  // Function to fetch invoice details for a campaign
  const fetchInvoiceDetails = async (campaignId: string) => {
    if (!campaignId) {
      console.warn("Cannot fetch invoice: No campaign ID provided");
      return null;
    }

    try {
      if (import.meta.env.VITE_APP_MOCK_DATA === 'true') {
        const mockdata = await generateMockData(INVOICE);
        return mockdata?.data.paymentInvoice;
      } else {
        const { data } = await client.query({
          query: INVOICE,
          fetchPolicy: "network-only",
          variables: {
            accessToken: token,
            campId: campaignId,
          },
        });
        return data?.paymentInvoice;
      }
    } catch (error) {
      console.error("Error fetching invoice details:", error);
      return null;
    }
  };

  const fetchCartDetails = async () => {
    // If a request is already in progress, skip this one
    if (cartRequestInProgress.current) {
      // Skip duplicate cart fetch requests
      return;
    }

    cartRequestInProgress.current = true;
    setIsCartLoading(true);

    try {
      if (import.meta.env.VITE_APP_MOCK_DATA === 'true') {
        const mockdata = await generateMockData(CART_PAGE_ITEM);
        const campaignData = mockdata.data.campaign || {};
        setCartData(mockdata.data.cartPageItem || []);
        setCartDataList(mockdata.data.cartPageItem || []);
        setCartItems(mockdata.data.cartPageItem || []);
        const mockCount = mockdata.data.cartPageItem?.length || 0;
        setCartCount(mockCount);
        setCartItemCountContext(mockCount); // Sync with cart context
        // // Mock cart data
        // const mockCartData = [{
        //   id: 'mock-cart-id',
        //   cartItemId: 'mock-cart-item-id',
        //   campaignInventories: [],
        //   campaignId: 'mock-campaign-id',
        //   campaignName: campaignName || 'Mock Campaign'
        // }] as CampaignInventory[];

        // // Convert mockCartData to CartItem format
        // const mockCartItems: CartItem[] = mockCartData.map(item => {
        //   const { cartItemId, ...rest } = item;
        //   return {
        //     billboardId: item.id || '',
        //     cartItemId: cartItemId || '',
        //         dateRange: {
        //       start: startDate ? format(startDate, 'yyyy-MM-dd') : '',
        //       end: endDate ? format(endDate, 'yyyy-MM-dd') : ''
        //     },
        //     campaignInventories: item.campaignInventories || [],
        //     overAlltotalPrice: 0,
        //     ...rest
        //   };
        // });

        // setCartDataList(mockCartData);
        // setCartData(mockCartData);
        // setCartItems(mockCartItems);
        // setCartCount(mockCartData.length);
      } else {
        // Don't mark existing cart items as loading when fetching cart details
        // We only want to track new items being added as loading

        // Ensure startDate and endDate are not null since they're non-nullable in the GraphQL schema
        if (!startDate || !endDate) {
          console.error('Start date or end date is missing for cart fetch');
          toast({
            title: "Error",
            description: "Campaign dates are required to fetch cart details",
            variant: "destructive"
          });
          cartRequestInProgress.current = false;
          setIsCartLoading(false);
          return;
        }

        const { data } = await client.query({
          query: CART_PAGE_ITEM,
          fetchPolicy: "network-only",
          variables: {
            accessToken: token,
            campaignId: campaignId || '',
            userId: userId,
            edit: edit,
            startDate: safeFormatDate(startDate, 'yyyy-MM-dd', 'yyyy-MM-dd'),
            endDate: safeFormatDate(endDate, 'yyyy-MM-dd', 'yyyy-MM-dd'),
            exposure: exposure
          },
        });

        const fetchedCartDataList = data.cartPageItem || [];

        // If no cart data is returned, ensure cart count is 0
        if (fetchedCartDataList.length === 0) {
          console.log('No cart data found, setting cart count to 0');
          setCartCount(0);
          setCartItemCountContext(0); // Sync with cart context
          setCartDataList([]);
          setCartData([]);
          setCartItems([]);
          return;
        }

        // Create separate sets for different item types from cart response
        const cartBillboardIds = new Set(
          fetchedCartDataList.flatMap((cart: any) =>
            cart.campaignInventories
              ?.filter((inv: any) => inv.billboardType !== 'package') // Regular billboards (digital/classic)
              ?.map((inv: any) => inv.billboardId)
          ).filter(Boolean)
        );

        const cartPackageIds = new Set(
          fetchedCartDataList.flatMap((cart: any) =>
            cart.campaignInventories
              ?.filter((inv: any) => inv.billboardType === 'package') // Package items  
              ?.map((inv: any) => inv.billboardId)
          ).filter(Boolean)
        );

        console.log('[FETCH CART] Billboard IDs in cart:', Array.from(cartBillboardIds));
        console.log('[FETCH CART] Package IDs in cart:', Array.from(cartPackageIds));

        // Update billboard data with cart status - Important for keeping consistent cart state
        setBillBoardData((prevData: any) =>
          prevData.map((billboard: any) => ({
            ...billboard,
            isInCart: cartBillboardIds.has(billboard.id),
          }))
        );

        // Update filtered billboards with cart status
        setFilteredBillboards((prev: any[]) =>
          prev.map((billboard) => ({
            ...billboard,
            isInCart: cartBillboardIds.has(billboard.id),
          }))
        );

        // Update package data with cart status
        setPackageData((prevData: any[]) =>
          prevData.map((pkg: any) => ({
            ...pkg,
            isInCart: cartPackageIds.has(pkg.id),
          }))
        );

        // Add state information to each billboard in the cart
        const enhancedCartData = fetchedCartDataList.map((cartItem: any) => {
          if (cartItem.campaignInventories && cartItem.campaignInventories.length > 0) {
            return {
              ...cartItem,
              campaignInventories: cartItem.campaignInventories.map((inventory: any) => {
                // Find the corresponding billboard to get its state
                const matchingBillboard = billBoardData.find((bb: any) => bb.id === inventory.billboardId);
                return {
                  ...inventory,
                  billboardState: matchingBillboard?.stateName || state || '',
                };
              }),
            };
          }
          return cartItem;
        });

        setCartDataList(enhancedCartData);
        setCartData(enhancedCartData);
        setCartItems(enhancedCartData);
        setCartDataLoaded(true); // Mark cart data as loaded

        // Update cart count based on the number of items in the cart
        const totalItems = fetchedCartDataList.reduce((count: number, cart: any) => {
          return count + (cart.campaignInventories?.length || 0);
        }, 0);
        console.log(`Setting cart count to ${totalItems} based on fetched cart data`);
        setCartCount(totalItems);
        setCartItemCountContext(totalItems); // Sync with cart context

        // Notify other components
        window.dispatchEvent(new Event('cartUpdated'));
      }
    } catch (error) {
      console.error("Error fetching cart details:", error);
      if (import.meta.env.VITE_APP_MOCK_DATA === 'true') {
        console.log("Using mock data, ignoring API error");
      } else {
        // Provide more specific error messages based on the error type

        if (error instanceof Error) {
          if (error.message.includes('startDate') || error.message.includes('endDate')) {
            toast({
              title: "Error",
              description: "Campaign dates are required to fetch cart details",
              variant: "destructive"
            });
          } else {
            toast({
              title: "Error",
              description: `Failed to fetch cart details: ${error.message}`,
              variant: "destructive"
            });
          }
        } else {
          toast({
            title: "Error",
            description: "Failed to fetch cart details. Please try again.",
            variant: "destructive"
          });
        }
      }
    } finally {
      setIsCartLoading(false);
      cartRequestInProgress.current = false;
      // We intentionally don't reset loadingCartItemIds here
      // as that would mark all cart items as "loading"
    }
  };

  // Add useEffect to initialize cart state on component mount with better safeguards
  useEffect(() => {
    // Check if the component just mounted or if dates changed
    if (!initialCartLoadDone.current) {

      // Fetch cart details with valid dates
      fetchCartDetails();
      fetchCartCountDetails();

      // Mark initial cart load as done
      initialCartLoadDone.current = true;
      cartInitializationRef.current = true;
    }
  }, []); // Removed startDate and endDate dependencies to prevent refetching when dates change

  // Consolidate Cart Fetching Logic
  useEffect(() => {
    const loadCartDetails = async () => {
      // Guard: If in edit mode, we must wait for the campaignId to be parsed from URL
      if (edit && !campaignId) {
        return;
      }

      // Guard: Prevent duplicate calls if already loaded this session
      if (initialCartLoadDone.current) {
        return;
      }

      console.log("🛒 Performing Initial Cart Load...");

      try {
        // Run both fetches in parallel
        await Promise.all([
          fetchCartDetails(),
          fetchCartCountDetails()
        ]);

        // Lock future fetches
        initialCartLoadDone.current = true;
        cartInitializationRef.current = true;
      } catch (e) {
        console.error("Error during initial cart load:", e);
      }
    };

    loadCartDetails();
  }, [campaignId, edit]); // Only re-run if campaignId or edit mode changes
  // This effect is for fetching billboard data when filters change
  useEffect(() => {
    const initializeData = async () => {
      if (!startDate || !endDate || !cartDataLoaded || !selectedCountryId || countryOptions.length === 0) {
        console.warn('Required data not ready:', {
          startDate: !!startDate,
          endDate: !!endDate,
          cartDataLoaded,
          selectedCountryId,
          countryOptionsLoaded: countryOptions.length > 0
        });
        return;
      }

      const cartBillboardIds = new Set(
        cartDataList.flatMap((cart: any) =>
          cart.campaignInventories?.map((inv: any) => inv.billboardId)
        ).filter(Boolean)
      );

      // API call is handled by other useEffects and handlers
    };

    // Only run this effect if we have all required data including country options
    if (startDate && endDate && cartDataLoaded && selectedCountryId && countryOptions.length > 0) {
      initializeData();
    }
  }, [fetchAllBillboardDataMemo, search, currentPage, cartDataList.length, cartDataLoaded, selectedCountryId, countryOptions.length]);

  // Listen for cart updated events from other components
  useEffect(() => {
    const handleCartUpdated = () => {
      // Prevent multiple rapid calls to fetchCartDetails
      const now = Date.now();
      if (now - lastCartUpdateTime.current < 1000) {
        console.log('⏳ Skipping cart update - too soon (debounce 1s)');
        return;
      }
      lastCartUpdateTime.current = now;

      if (startDate && endDate && !cartDetailsRequestInProgress.current) {
        cartDetailsRequestInProgress.current = true;
        fetchCartDetails().finally(() => {
          cartDetailsRequestInProgress.current = false;
        });
      }
    };

    // Add event listener for cart updates
    window.addEventListener('cartUpdated', handleCartUpdated);

    // Clean up on unmount
    return () => {
      window.removeEventListener('cartUpdated', handleCartUpdated);
    };
  }, []); // Removed startDate and endDate dependencies to prevent refetching when dates change

  // Sync cart context with API cart count
  useEffect(() => {
    if (typeof cartCount === 'number') {
      setCartItemCountContext(cartCount);
    }
  }, [cartCount, setCartItemCountContext]);


  const deleteCartItem = async (itemData: any) => {

    // Get the billboard ID from either source (property list or cart)
    const billboardId = itemData.inventoryId || itemData.id;
    const trackingId = billboardId; // Use the same ID for tracking loading state

    // Double check for valid item data
    if (!itemData || !billboardId) {
      console.error('Invalid item data:', itemData);
      toast({
        title: "Error",
        description: "Invalid item data",
        variant: "destructive"
      });
      return;
    }

    // Find matching item in cart
    const matchingItem = cartDataList.find((list: any) => {
      return list.campaignInventories?.some((item: any) => item.billboardId === billboardId);
    });

    if (!matchingItem) {
      console.error('Item not found in cart:', billboardId);
      toast({
        title: "Error",
        description: "Item not found in cart",
        variant: "destructive"
      });
      return;
    }

    // Find the specific inventory item
    const matchedInventory = matchingItem.campaignInventories?.find((item: any) => item.billboardId === billboardId);
    const cartItemId = matchedInventory?.cartItemId;

    if (!cartItemId) {
      console.error('Cart item ID not found for:', billboardId);
      toast({
        title: "Error",
        description: "Cart item ID not found",
        variant: "destructive"
      });
      return;
    }

    // Check if item is already being processed
    if (loadingCartItemIds.has(trackingId)) {
      return;
    }

    // Determine item type
    const itemType = (itemData.isDraft || matchedInventory.isDraft) ? 'draft-data' : 'cart-data';

    // Save previous states for rollback if needed
    const previousBillboardData = billBoardData;
    const previousFilteredBillboards = filteredBillboards;
    const previousCartCount = cartCount;
    const previousCartDataList = cartDataList;

    try {
      // Add to loading set using the tracking ID
      setLoadingCartItemIds(prev => new Set([...prev, trackingId]));

      // Optimistically update UI states using billboardId for consistency
      setBillBoardData((prevData: any[]) =>
        prevData.map(billboard => ({
          ...billboard,
          isInCart: billboard.id === billboardId ? false : billboard.isInCart
        }))
      );

      setFilteredBillboards((prev: any[]) =>
        prev.map(billboard => ({
          ...billboard,
          isInCart: billboard.id === billboardId ? false : billboard.isInCart
        }))
      );

      // Optimistically update package states as well
      setPackageData((prevData: any[]) =>
        prevData.map(pkg => ({
          ...pkg,
          isInCart: pkg.id === billboardId ? false : pkg.isInCart
        }))
      );

      // Update cart count
      setCartCount(prev => Math.max(0, prev - 1));
      setCartItemCountContext(Math.max(0, cartCount - 1)); // Sync with cart context optimistically

      // Call deletion mutation
      const { data, errors } = await deleteCartQuery({
        variables: {
          accessToken: token,
          deleteItems: [{
            id: cartItemId,
            itemType: itemType
          }]
        },
      });

      // Handle successful deletion
      if (data?.deleteCartItem?.success) {
        // Also remove from cart context for real-time updates
        removeFromCartContext(billboardId.toString());

        toast({
          title: "Success",
          description: data.deleteCartItem.message || "Item removed from cart successfully",
          variant: "default"
        });
        // Remove item from cartDataList without mutating the original
        const updatedCartDataList = cartDataList.map((cart: any) => ({
          ...cart,
          campaignInventories: cart.campaignInventories?.filter(
            (inv: any) => inv.cartItemId !== cartItemId
          ) || []
        })).filter((cart: any) => cart.campaignInventories.length > 0);

        // Update cart data list first
        setCartDataList(updatedCartDataList);
        setCartData(updatedCartDataList);
        setCartItems(updatedCartDataList);

        // Fetch latest cart data only if there are still items - avoid Promise.all to prevent concurrent calls
        if (updatedCartDataList.length > 0) {
          // Only call fetchCartDetails which will update cart count internally
          await fetchCartDetails();
        } else {
          // If cart is now empty, update relevant states
          setCartCount(0);
          setCartItemCountContext(0); // Sync with cart context

          // // Reset country to session default when cart becomes empty
          // const sessionDefaultCountryId = localStorage.getItem('sessionDefaultCountryId');
          // if (sessionDefaultCountryId && countryOptions.length > 0) {
          //   const sessionDefaultCountry = countryOptions.find((c: any) => c.id === sessionDefaultCountryId);
          //   if (sessionDefaultCountry) {
          //     setSelectedCountryId(sessionDefaultCountry.value);
          //     setFilters((prev: any) => ({
          //       ...prev,
          //       country: sessionDefaultCountry.value
          //     }));
          //     localStorage.setItem('selectedCountryId', sessionDefaultCountry.id);
          //     if (sessionDefaultCountry.tax) {
          //       updateTaxInfo(sessionDefaultCountry.tax.label || 'Tax', sessionDefaultCountry.tax.percent || 0);
          //     }
          //     console.log('🌍 Cart empty - resetting country to session default:', sessionDefaultCountry.label);

          //     const emptyCartBillboardIds = new Set<string>();
          //     await fetchAllBillboardData('', 0, emptyCartBillboardIds, undefined, [], undefined, sessionDefaultCountry.value);
          //   }
          // }
        }

        // Notify other components
        window.dispatchEvent(new Event('cartUpdated'));
        toast({
          title: "Success",
          description: data.deleteCartItem.message || "Item removed from cart successfully",
          variant: "default"
        });
      } else {
        // Handle failure
        const errorMessage = errors?.[0]?.message || data?.deleteCartItem?.message || "Failed to delete item";
        console.error('Delete failed:', errorMessage);
        toast({
          title: "Error",
          description: errorMessage,
          variant: "destructive"
        });

        // Revert all state changes
        setBillBoardData(previousBillboardData);
        setFilteredBillboards(previousFilteredBillboards);
        setCartCount(previousCartCount);
        setCartItemCountContext(previousCartCount); // Sync with cart context
        setCartDataList(previousCartDataList);
      }
    } catch (error) {
      console.error("Error in deleteCartItem:", error);
      toast({
        title: "Error",
        description: "Failed to remove item from cart",
        variant: "destructive"
      });

      // Revert all state changes
      setBillBoardData(previousBillboardData);
      setFilteredBillboards(previousFilteredBillboards);
      setCartCount(previousCartCount);
      setCartItemCountContext(previousCartCount); // Sync with cart context
      setCartDataList(previousCartDataList);
    } finally {
      // Clean up loading state using the tracking ID
      setLoadingCartItemIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(trackingId);
        return newSet;
      });
    }
  };


  // const handleRemoveFromCart = (billboardId: string) => {
  //   setCartItems(cartItems.filter(item => item.billboardId !== billboardId));
  // };

  // const handleCategoryChange = (category: string) => {
  //   setSelectedCategory(category);
  //   setShowCategoryDropdown(false);
  // };

  const toggleViewMode = useCallback(
    debounce((mode: 'grid' | 'map') => {
      setViewMode(mode);

      // If switching to map mode, make sure map will be visible even with no data
      if (mode === 'map' && map) {
        setTimeout(() => {
          if (map) {
            map.resize();

            // Reset view to default
            map.flyTo({
              center: [153.0251, -27.4698],
              zoom: 5,
              essential: true
            });
          }
        }, 200);
      }
    }, 300),
    [map]
  );


  const paginationPlus = useCallback(() => {
    const totalPages = Math.ceil(totalDataLength / dataListLength);
    if (currentPage >= totalPages - 1) {
      toast({
        title: "Error",
        description: "You are already on the last page.",
        variant: "destructive"
      });
      return;
    }
    const newPage = currentPage + 1;
    setCurrentPage(newPage);
    localStorage.setItem('propcurrentPage', newPage.toString());

    // Get cart billboard IDs
    const cartBillboardIds = new Set(
      cartDataList.flatMap((cart: any) =>
        cart.campaignInventories?.map((inv: any) => inv.billboardId)
      ).filter(Boolean)
    );

    // Explicitly pass selectedCountryId to ensure country is available
    fetchAllBillboardData(search, newPage, cartBillboardIds, undefined, undefined, undefined, selectedCountryId);
    scrollToTop();
  }, [totalDataLength, dataListLength, currentPage, search, selectedCountryId, cartDataList, fetchAllBillboardData]);

  const paginationMinus = useCallback(() => {
    if (currentPage === 0) {
      toast({
        title: "Error",
        description: "You are already on the first page.",
        variant: "destructive"
      });
      return;
    }
    const newPage = currentPage - 1;
    setCurrentPage(newPage);
    localStorage.setItem('propcurrentPage', newPage.toString());

    // Get cart billboard IDs
    const cartBillboardIds = new Set(
      cartDataList.flatMap((cart: any) =>
        cart.campaignInventories?.map((inv: any) => inv.billboardId)
      ).filter(Boolean)
    );

    // Explicitly pass selectedCountryId to ensure country is available
    fetchAllBillboardData(search, newPage, cartBillboardIds, undefined, undefined, undefined, selectedCountryId);
    scrollToTop();
  }, [currentPage, search, selectedCountryId, cartDataList, fetchAllBillboardData]);

  // Update the getPageNumbers function to use proper total count
  const getPageNumbers = () => {
    const totalPages = Math.ceil(totalDataLength / dataListLength);
    const maxPagesToShow = 5;
    const pages = [];
    let startPage = Math.max(0, currentPage - Math.floor(maxPagesToShow / 2));
    let endPage = Math.min(totalPages - 1, startPage + maxPagesToShow - 1);

    startPage = Math.max(0, endPage - maxPagesToShow + 1);

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    const pageNumbers: (number | string)[] = [];
    if (startPage > 0) {
      pageNumbers.push(0);
      if (startPage > 1) {
        pageNumbers.push('...');
      }
    }
    pageNumbers.push(...pages);
    if (endPage < totalPages - 1) {
      if (endPage < totalPages - 2) {
        pageNumbers.push('...');
      }
      pageNumbers.push(totalPages - 1);
    }

    return pageNumbers;
  };

  const handlePageClick = (page: number) => {
    if (page === currentPage) return;
    setCurrentPage(page);
    localStorage.setItem('propcurrentPage', page.toString());

    // Get cart billboard IDs
    const cartBillboardIds = new Set(
      cartDataList.flatMap((cart: any) =>
        cart.campaignInventories?.map((inv: any) => inv.billboardId)
      ).filter(Boolean)
    );

    // Explicitly pass selectedCountryId to ensure country is available
    fetchAllBillboardData(search, page, cartBillboardIds, undefined, undefined, undefined, selectedCountryId);
    scrollToTop();
  };

  // Handle country selection (single-select)
  const handleCountrySelection = (countryId: string) => {
    // Check if cart has billboards - if yes, prevent country change
    if (contextCartItems && contextCartItems.length > 0) {
      toast({
        title: "Cannot change country",
        description: `You have already selected inventory from ${countryOptions.find((c: any) => c.value === filters.country)?.label || 'current country'}. To change countries, please remove all selected inventory or start a new proposal.`,
        variant: "destructive"
      });
      setShowCountryDropdown(false);
      return;
    }

    // Check if in draft/edit mode - prevent country change
    if (edit) {
      toast({
        title: "Cannot change country",
        description: "You cannot change the country for a draft campaign. The country is fixed based on the draft.",
        variant: "destructive"
      });
      setShowCountryDropdown(false);
      return;
    }

    // Set flag to prevent automatic API calls during manual country change
    isManualCountryChange.current = true;

    // Find the selected country to get its countryId for localStorage
    const selectedCountry = countryOptions.find((c: any) => c.value === countryId || c.id === countryId);
    console.log('🌐 Handling country selection for ID:', countryId, 'Found country:', selectedCountry);

    if (selectedCountry) {
      console.log('🎯 User selected country:', selectedCountry.label);

      // ✅ FIX START: Prevent resetting if the same country is selected
      if (selectedCountry.id === selectedCountryId) {
        console.log('🎯 Country already selected, skipping reset:', selectedCountry.label);
        setShowCountryDropdown(false);
        return;
      }
      // ✅ FIX END

      console.log('🔄 Country change detected:', {
        oldCountry: countryOptions.find((c: any) => c.id === selectedCountryId)?.label,
        newCountry: selectedCountry.label
      });

      // Set selectedCountryId directly
      setSelectedCountryId(selectedCountry.value);

      // ✅ Update filters to sync and EXPLICITLY clear state/district arrays
      setFilters((prev: any) => ({
        ...prev,
        country: selectedCountry.value,
        state: [],        // ✅ Clear state array
        district: [],     // ✅ Clear district array
        mediaOwner: []    // Clear media owner as it's country-specific
      }));

      // Always update localStorage with the user's selection
      localStorage.setItem('selectedCountryId', selectedCountry.id);
      localStorage.setItem('navigationCountryBackup', JSON.stringify({
        countryId: selectedCountry.id,
        countryValue: selectedCountry.value,
        countryLabel: selectedCountry.label,
        timestamp: Date.now(),
        isManualSelection: true
      }));
      console.log('🔒 Persisted manual country selection for navigation protection:', selectedCountry.id);

      // Mark country as manually initialized to prevent automatic resets
      countryInitialized.current = true;
      console.log('✅ Marked country as manually initialized to prevent resets');

      // Update tax information in cart context
      if (selectedCountry.tax) {
        const taxLabel = selectedCountry.tax.label || 'Tax';
        const taxPercent = selectedCountry.tax.percent || 0;
        updateTaxInfo(taxLabel, taxPercent);
        console.log('🏷️ Updated cart tax info:', { label: taxLabel, percent: taxPercent });
      } else {
        // Fallback tax info if country doesn't have tax data
        updateTaxInfo('Tax', 0);
        console.log('🏷️ No tax info found for country, using fallback');
      }

      // Reset to first page
      setCurrentPage(0);
      localStorage.setItem('propcurrentPage', '0');

      // ✅ Clear location filter states and UI selections
      setState([]);
      setDistrict([]);
      setAvailableStates([]);
      setAvailableDistricts([]);
      localStorage.removeItem('campaignListState');
      console.log('🧹 Cleared state and district selections');

      // Update filtersRef immediately to ensure API call uses updated values
      filtersRef.current = {
        ...filtersRef.current,
        country: selectedCountry.value,
        state: "",
        district: "",
        mediaOwner: [],
        venueType: []
      };

      // CRITICAL: Also reset filters.venueType in state so UI clears immediately
      setFilters((prev: any) => ({
        ...prev,
        country: selectedCountry.value,
        state: [],
        district: [],
        mediaOwner: [],
        venueType: "" // Clear venue type in UI
      }));

      // Call API directly based on category
      if (filters.category === 'packages') {
        const cartPackageIds = new Set(cartDataList.flatMap((cart: any) =>
          cart.campaignInventories
            ?.filter((inv: any) => inv.billboardType === 'package')
            ?.map((inv: any) => inv.billboardId)
        ).filter(Boolean));
        console.log('📦 Fetching packages for new country:', selectedCountry.label);
        fetchPackageData(cartPackageIds, selectedCountry.value, search);
      } else {
        const cartBillboardIds = new Set(cartDataList.flatMap((cart: any) =>
          cart.campaignInventories?.map((inv: any) => inv.billboardId)
        ).filter(Boolean));
        // Pass empty arrays for state and district, and ensure companyId is handled as empty string in fetchAllBillboardData
        // CRITICAL: Pass empty string for companyId and empty array for venueType to clear filters on country change
        fetchAllBillboardData(search, 0, cartBillboardIds, undefined, [], undefined, selectedCountry.id, [], "", []);
      }
    }

    setShowCountryDropdown(false);

    // Reset the manual change flag after API calls complete
    setTimeout(() => {
      isManualCountryChange.current = false;
    }, 500);
  };

  const handleStateChange = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => {
      const selectedStateId = event.target.value;

      // Set flag to prevent automatic API calls during manual state change
      isManualStateChange.current = true;

      setState([selectedStateId]); // Fix: setState expects array
      setDistrict([]); // Reset district when state changes

      // Reset any "no data" message when changing state
      setNoDataMessage(null);

      // Reset to default billboard type preference if it was auto-switched
      const defaultType = isFieldEnabled('digital', configData ?? [])
        ? 'digital'
        : 'classic';
      if (billboardType !== defaultType) {
        setBillboardType(defaultType);
      }

      // Immediately trigger API call with the new state
      const cartBillboardIds = new Set(cartDataList.flatMap((cart: any) =>
        cart.campaignInventories?.map((inv: any) => inv.billboardId)
      ).filter(Boolean));

      // Direct API call with current filters including new state - explicitly pass selectedCountryId
      fetchAllBillboardData(search, 0, cartBillboardIds, undefined, [selectedStateId], undefined, selectedCountryId).finally(() => {
        // Reset flag after API call completes
        setTimeout(() => {
          isManualStateChange.current = false;
        }, 500);
      });

      const selectedState = availableStates.find((s) => s.stateId === selectedStateId);
      if (selectedState) {
        // Store state information in localStorage
        localStorage.setItem('campaignState', selectedStateId);
        localStorage.setItem('campaignStateName', selectedState.name);

        if (selectedStateId && !districtsLoading) {
          getDistricts({
            variables: { stateIds: selectedStateId, exposure: exposure },
            fetchPolicy: 'network-only',
          });
        }
        // Persist selected state to localStorage and campaign object
        localStorage.setItem('campaignState', selectedStateId);
        // Update campaignListState
        localStorage.setItem(
          'campaignListState',
          JSON.stringify({
            search,
            currentPage,
            state: selectedStateId,
            district,
            billboardType,
            locationData,
            siteTypeData,
            venueTypeData,
          })
        );
        if (campaignData) {
          setCampaignData({ ...campaignData, state: selectedStateId, stateId: selectedStateId });
          localStorage.setItem('currentCampaign', JSON.stringify({ ...campaignData, state: selectedStateId, stateId: selectedStateId }));
        }
      } else if (!selectedStateId) {
        // Clear state information from localStorage
        localStorage.removeItem('campaignState');
        localStorage.removeItem('campaignStateName');
        localStorage.removeItem('campaignDistrict');
        localStorage.removeItem('campaignDistrictName');
        // Persist empty state to localStorage and campaign object
        localStorage.setItem('campaignState', '');
        // Update campaignListState
        localStorage.setItem(
          'campaignListState',
          JSON.stringify({
            search,
            currentPage,
            state: '',
            district,
            billboardType,
            locationData,
            siteTypeData,
            venueTypeData,
          })
        );
        if (campaignData) {
          setCampaignData({ ...campaignData, state: '', stateId: '' });
          localStorage.setItem('currentCampaign', JSON.stringify({ ...campaignData, state: '', stateId: '' }));
        }
      }

      // If in map view, ensure the map is updated
      if (viewMode === 'map' && map) {
        try {
          map.flyTo({
            center: [153.0251, -27.4698],
            zoom: 5,
            essential: true,
          });
        } catch (error) {
          console.error('Error resetting map during state change:', error);
        }
      }
    },
    [availableStates, districtsLoading, getDistricts, map, viewMode, campaignData, search, currentPage, billboardType, locationData, siteTypeData, venueTypeData, district, configData, isFieldEnabled, cartDataList, fetchAllBillboardData, selectedCountryId]
  );
  const handleDistrictChange = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => {
      const selectedDistrictId = event.target.value; // This is still districtId for display
      const selectedDistrict = availableDistricts.find(d => d.districtId === selectedDistrictId);
      const selectedId = selectedDistrict ? selectedDistrict.id : ''; // Use the id field
      setDistrict(selectedId); // Update state with id

      // Reset any "no data" message when changing district
      setNoDataMessage(null);

      // Reset to default billboard type if it was auto-switched
      const defaultType = isFieldEnabled('digital', configData ?? [])
        ? 'digital'
        : 'classic';
      if (billboardType !== defaultType) {
        setBillboardType(defaultType);
      }

      // Store district info in localStorage
      if (selectedDistrict) {
        localStorage.setItem('campaignDistrict', selectedDistrictId);
        localStorage.setItem('campaignDistrictName', selectedDistrict.name);

        // Update campaignListState
        localStorage.setItem(
          'campaignListState',
          JSON.stringify({
            search,
            currentPage,
            state,
            district: selectedId,
            billboardType,
            locationData,
            siteTypeData,
            venueTypeData,
          })
        );
      }

      // If in map view, ensure the map is updated
      if (viewMode === 'map' && map) {
        try {
          map.flyTo({
            center: [153.0251, -27.4698],
            zoom: 5,
            essential: true
          });
        } catch (error) {
          console.error('Error resetting map during district change:', error);
        }
      }
    }, [availableDistricts, billboardType, configData, isFieldEnabled, map, setNoDataMessage, viewMode, search, currentPage, state, locationData, siteTypeData, venueTypeData]
  );

  useEffect(() => {
    // Run only if not initialized
    if (hasInitialized.current) return;

    const savedListState = localStorage.getItem('campaignListState');
    if (savedListState) {
      try {
        const {
          search: savedSearch,
          currentPage: savedPage,
          state: savedState,
          district: savedDistrict,
          billboardType: savedBillboardType,
          locationData: savedLocationData,
          siteTypeData: savedSiteTypeData,
          venueTypeData: savedVenueTypeData,
        } = JSON.parse(savedListState);

        // Update state only if values are different to avoid unnecessary renders
        if (savedSearch !== undefined && savedSearch !== search) setSearch(savedSearch);
        if (savedPage !== undefined && savedPage !== currentPage) setCurrentPage(savedPage);
        if (savedState !== undefined && savedState !== state) setState(savedState);
        if (savedDistrict !== undefined && savedDistrict !== district) setDistrict(savedDistrict);

        if (savedBillboardType !== billboardType) {
          setBillboardType(savedBillboardType);
        }

        // // Only restore billboardType if explicitly saved and different
        // if (savedBillboardType !== undefined && savedBillboardType !== billboardType) {
        //   if (savedBillboardType === 'default') {
        //     const defaultType = formData?.propertyDetails
        //       ? formData?.propertyDetailsType
        //       : isFieldEnabled('digital', configData ?? [])
        //         ? 'digital'
        //         : 'classic';
        //     if (defaultType !== billboardType) setBillboardType(defaultType);
        //   } else if (savedBillboardType !== billboardType) {
        //     setBillboardType(savedBillboardType);
        //   }
        // }

        if (savedLocationData !== undefined && JSON.stringify(savedLocationData) !== JSON.stringify(locationData))
          setLocationData(savedLocationData);
        if (savedSiteTypeData !== undefined && JSON.stringify(savedSiteTypeData) !== JSON.stringify(siteTypeData))
          setSitetypeData(savedSiteTypeData);
        if (savedVenueTypeData !== undefined && JSON.stringify(savedVenueTypeData) !== JSON.stringify(venueTypeData))
          setVenueTypeData(savedVenueTypeData);

        // Trigger fetchAllBillboardData after restoring state
        const cartBillboardIds = new Set(
          cartDataList.flatMap((cart: any) =>
            cart.campaignInventories?.map((inv: any) => inv.billboardId)
          ).filter(Boolean)
        );
        fetchAllBillboardData(savedSearch || search, savedPage || currentPage, cartBillboardIds);
      } catch (e) {
        console.error('Error parsing campaignListState:', e);
      }
    } else {
      // Set default billboardType if no saved state - digital as default
      if (billboardType !== 'digital') {
        setBillboardType('digital');
      }
    }

    // Mark as initialized
    hasInitialized.current = true;
  }, [configData, isFieldEnabled, cartDataList, search, currentPage, state, district, billboardType, locationData, siteTypeData, venueTypeData])

  // Memoize JSON.stringify values to prevent unnecessary re-runs of data fetching useEffect
  const locationDataString = useMemo(() => JSON.stringify(locationData), [locationData]);
  const siteTypeDataString = useMemo(() => JSON.stringify(siteTypeData), [siteTypeData]);
  const venueTypeDataString = useMemo(() => JSON.stringify(venueTypeData), [venueTypeData]);

  // Separate effect to handle data fetching - only triggers after initialization
  useEffect(() => {
    // 🔍 LOGGING COUNTER
    nonDraftFetchCount.current += 1;
    console.log(`📊 [Non-Draft Effect] Call #${nonDraftFetchCount.current} triggered`);

    const urlParams = new URLSearchParams(window.location.search);
    const editFlag = urlParams.get('edit') === 'true';
    if (editFlag !== edit) {
      setEdit(editFlag);
    }
    // Only fetch data for non-draft flows
    if (!edit && hasInitialized.current && billboardType && selectedCountryId && countryOptions.length > 0) {
       if (billBoardDataLoading) {
        console.log(`⏳ [Non-Draft Effect] #${nonDraftFetchCount.current} Skipped: Already loading`);
        return;
      }

      console.log(`🚀 [Non-Draft Effect] #${nonDraftFetchCount.current} API Call Executing...`, {
        selectedCountryId,
        edit,
        billboardType
      });

      setNoDataMessage(null); // Reset message before fetching
      if (import.meta.env.VITE_APP_MOCK_DATA === 'true') {
        generateMockData(GETPROPERTY_LIST).then((mockResponse: any) => {
          if (mockResponse && mockResponse.data) {
            const data = mockResponse.data.landingpageExploreProperties || [];
            setBillBoardData(data);
            setFilteredBillboards(data);
            setTotalDataLength(data.length);
            setPageStartData(1);
            setPageEndData(Math.min(dataListLength, data.length));
          } else {
            console.error("Mock data non-draft useEffect: No data in response");
            setNoDataMessage('No mock data available.');
          }
        }).catch((err: any) => {
          console.error("Error generating mock data for non-draft:", err);
          setNoDataMessage('For your filters, no data found.');
          toast({
            title: "Error",
            description: "Failed to load mock data.",
            variant: "destructive"
          });
        });
      } else {
        const cartBillboardIds = new Set(cartDataList.flatMap((cart: any) =>
          cart.campaignInventories?.map((inv: any) => inv.billboardId)
        ).filter(Boolean));

        if (filters.category === 'packages') {
          const cartPackageIds = new Set(cartDataList.flatMap((cart: any) =>
            cart.campaignInventories
              ?.filter((inv: any) => inv.billboardType === 'package')
              ?.map((inv: any) => inv.billboardId)
          ).filter(Boolean));
          fetchPackageData(cartPackageIds, selectedCountryId, search);
        } else {
          debouncedFetchAllBillboardData(search, currentPage, cartBillboardIds, 'non-draft-useEffect');
        }
      }
    } else {
      console.log('Waiting for required data before fetching (non-draft):', {
        hasInitialized: hasInitialized.current,
        billboardType,
        selectedCountryId,
        edit
      });
    }

    return () => {
      if (debouncedFetchAllBillboardData && debouncedFetchAllBillboardData.cancel) {
        debouncedFetchAllBillboardData.cancel();
      }
    };
    // 🔴 FIX: Removed 'cartDataList?.length' from dependencies
  }, [
    search,
    currentPage,
    locationDataString,
    siteTypeDataString,
    venueTypeDataString,
    state,
    district,
    countryOptions.length,
    // cartDataList?.length, <--- REMOVED
    draftData,
    draftInitialized,
    draftCountrySet,
    edit
  ]);



  // Separate effect for draft flows to ensure API calls use the correct country
  useEffect(() => {
    // 1. Strict Guard Clauses
    if (!edit || !selectedCountryId || !billboardType || countryOptions.length === 0) {
      return;
    }
    
    // 2. STOPPER: If we already fetched specifically for the draft, do not fetch again.
    // This prevents the loop where Fetch -> Cart Update -> Dependency Change -> Fetch
    if (dataFetchedForDraft.current) {
      console.log('🛑 Draft data already fetched. Skipping duplicate call.');
      return;
    }

    if (billBoardDataLoading) {
      console.log('⏳ Skipping draft API call: Already loading');
      return;
    }

    // 3. Mark as fetched immediately
    dataFetchedForDraft.current = true;

    console.log('🚀 API Call Debug for Draft:', {
      selectedCountryId,
      draftCountrySet,
      edit,
      countryOptionsLength: countryOptions.length,
      billboardType
    });

    setNoDataMessage(null);

    // MOCK DATA HANDLING
    if (import.meta.env.VITE_APP_MOCK_DATA === 'true') {
      generateMockData(GETPROPERTY_LIST).then((mockResponse: any) => {
        if (mockResponse && mockResponse.data) {
          const data = mockResponse.data.landingpageExploreProperties || [];
          console.log("Mock data loaded in draft useEffect:", data.length, "items");
          setBillBoardData(data);
          setFilteredBillboards(data);
          setTotalDataLength(data.length);
          setPageStartData(1);
          setPageEndData(Math.min(dataListLength, data.length));
        } else {
          console.error("Mock data draft useEffect: No data in response");
          setNoDataMessage('No mock data available.');
        }
      }).catch((err: any) => {
        console.error("Error generating mock data for draft:", err);
        setNoDataMessage('For your filters, no data found.');
        toast({
          title: "Error",
          description: "Failed to load mock data.",
          variant: "destructive"
        });
      });
    } else {
      // REAL API HANDLING

      // We read cartDataList here for IDs, but we DO NOT put it in the dependency array
      const cartBillboardIds = new Set(cartDataList.flatMap((cart: any) =>
        cart.campaignInventories?.map((inv: any) => inv.billboardId)
      ).filter(Boolean));

      if (filters.category === 'packages') {
        const cartPackageIds = new Set(cartDataList.flatMap((cart: any) =>
          cart.campaignInventories
            ?.filter((inv: any) => inv.billboardType === 'package')
            ?.map((inv: any) => inv.billboardId)
        ).filter(Boolean));

        fetchPackageData(cartPackageIds, selectedCountryId, search);
      } else {
        // Explicitly pass selectedCountryId to ensure we don't fallback to session defaults
        fetchAllBillboardData(search, currentPage, cartBillboardIds, undefined, undefined, undefined, selectedCountryId);
      }
    }
    // 🔴 CRITICAL: 'cartDataList' is intentionally REMOVED from dependencies
  }, [selectedCountryId, edit, search, currentPage, filters.category, draftData, countryOptions.length, billboardType]);  // Save filters to localStorage whenever they change
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('start-campaign-filters', JSON.stringify(filters));
    }
  }, [JSON.stringify(filters)]); // Use JSON.stringify to prevent unnecessary updates

  // Save expanded sections to localStorage whenever they change
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('start-campaign-expanded-sections', JSON.stringify(expandedSections));
    }
  }, [JSON.stringify(expandedSections)]); // Use JSON.stringify to prevent unnecessary updates

  // Save search query to localStorage whenever it changes
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('start-campaign-search-query', searchQuery);
    }
  }, [searchQuery]);

  // Remove debounced price range update - we'll handle this manually on Enter key press

  // Initialize local price range from filters
  useEffect(() => {
    if (filters.priceRange) {
      setLocalPriceRange(filters.priceRange);
      setMinPriceInput(filters.priceRange[0] > 0 ? filters.priceRange[0].toString() : '');
      setMaxPriceInput(filters.priceRange[1] < 99999999 ? filters.priceRange[1].toString() : '');
    }
  }, [filters.priceRange]);

  // Handler for price range update on Enter key
  const handlePriceRangeUpdate = () => {


    // Only update filter if user has entered values
    if (minPriceInput !== '' || maxPriceInput !== '') {
      // Create price range object with only entered values
      const priceRangeForAPI: any = {};

      if (minPriceInput !== '') {
        const minValue = Math.max(0, parseInt(minPriceInput) || 0);
        priceRangeForAPI.minPrice = minValue;
        console.log('📊 Adding minPrice to API call:', minValue);
      }

      if (maxPriceInput !== '') {
        const maxValue = Math.max(parseInt(maxPriceInput) || 0, priceRangeForAPI.minPrice || 0);
        priceRangeForAPI.maxPrice = maxValue;
        console.log('📊 Adding maxPrice to API call:', maxValue);
      }

      console.log('📊 Price range for API call:', priceRangeForAPI);

      // Update local state for UI display
      const minForDisplay = priceRangeForAPI.minPrice || 0;
      const maxForDisplay = priceRangeForAPI.maxPrice || 99999999;

      setLocalPriceRange([minForDisplay, maxForDisplay]);
      setMinPriceInput(priceRangeForAPI.minPrice ? priceRangeForAPI.minPrice.toString() : '');
      setMaxPriceInput(priceRangeForAPI.maxPrice && priceRangeForAPI.maxPrice < 99999999 ? priceRangeForAPI.maxPrice.toString() : '');

      // Update filters for internal state management
      setFilters((prev: typeof defaultFilters) => ({
        ...prev,
        priceRange: [minForDisplay, maxForDisplay]
      }));

      // Immediately trigger API call with only the entered price values
      const cartBillboardIds = new Set(cartDataList.flatMap((cart: any) =>
        cart.campaignInventories?.map((inv: any) => inv.billboardId)
      ).filter(Boolean));

      console.log('📊 About to trigger direct API call with selective price range:', priceRangeForAPI);

      fetchAllBillboardData(search, 0, cartBillboardIds, undefined, undefined, priceRangeForAPI)
        .then(() => {
          console.log('📊 Price filter API call completed successfully');
        })
        .catch((error) => {
          console.error('📊 Price filter API call failed:', error);
        });

      console.log('📊 Direct API call triggered with selective price range:', priceRangeForAPI);
    } else {
      console.log('📊 No price values entered, not updating filter');
    }
  };

  // Handler for min price input changes
  const handleMinPriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Only allow numeric values or empty string
    if (value === '' || /^\d*$/.test(value)) {
      setMinPriceInput(value);
    }
  };

  // Handler for max price input changes
  const handleMaxPriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Only allow numeric values or empty string, and remove leading zeros
    if (value === '' || /^\d*$/.test(value)) {
      setMaxPriceInput(value === '' ? '' : value.replace(/^0+(?!$)/, ''));
    }
  };

  // Handler for Enter key press on price inputs
  const handlePriceKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      handlePriceRangeUpdate();
    }
  };

  // Reset pagination when filters change
  useEffect(() => {
    setCurrentPage(0);
  }, [search, filters.state, filters.district, filters.category, filters.priceRange, filters.venueType, filters.mediaOwner]); // More specific dependencies

  // Trigger API call when media owner filter changes
  useEffect(() => {
    // 🔍 LOGGING COUNTER
    mediaOwnerFetchCount.current += 1;
    console.log(`🏢 [Media Owner Effect] Call #${mediaOwnerFetchCount.current} triggered`);

    const cartBillboardIds = new Set(cartDataList.flatMap((cart: any) =>
      cart.campaignInventories?.map((inv: any) => inv.billboardId)
    ).filter(Boolean));

    debouncedFetchAllBillboardData(search, 0, cartBillboardIds, 'mediaOwner filter changed', undefined, undefined, selectedCountryId);

    // 🔴 FIX: Removed 'cartDataList' from dependencies
  }, [filters.mediaOwner, selectedCountryId]);

  // Handle search on Enter key press
  const handleSearchKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      // Only trigger search if query is empty or >= 3 characters
      if (searchQuery.length === 0 || searchQuery.length >= 3) {
        setSearch(searchQuery);
        setCurrentPage(0); // Reset to first page

        const cartBillboardIds = new Set(
          cartDataList.flatMap((cart: any) =>
            cart.campaignInventories?.map((inv: any) => inv.billboardId)
          ).filter(Boolean)
        );

        // Check if current category is packages
        if (filters.category === 'packages') {
          // Call package API for packages
          const cartPackageIds = new Set(
            cartDataList.flatMap((cart: any) =>
              cart.campaignInventories?.map((inv: any) => inv.packageId)
            ).filter(Boolean)
          );
          fetchPackageData(cartPackageIds, selectedCountryId, searchQuery);
        } else {
          // 🚀 Trigger billboard API immediately for other categories - explicitly pass selectedCountryId
          fetchAllBillboardData(searchQuery, 0, cartBillboardIds, billboardType, undefined, undefined, selectedCountryId);
        }
      }
    }
  };

  // Handle search reset
  const handleResetSearch = () => {
    setSearchQuery('');
    setSearch('');
    setCurrentPage(0);

    const cartBillboardIds = new Set(
      cartDataList.flatMap((cart: any) =>
        cart.campaignInventories?.map((inv: any) => inv.billboardId)
      ).filter(Boolean)
    );

    // Check if current category is packages
    if (filters.category === 'packages') {
      // Call package API for packages without search
      const cartPackageIds = new Set(
        cartDataList.flatMap((cart: any) =>
          cart.campaignInventories?.map((inv: any) => inv.packageId)
        ).filter(Boolean)
      );
      // Pass empty string explicitly to override any existing search state
      fetchPackageData(cartPackageIds, selectedCountryId, '');
    } else {
      // Trigger billboard API to reload all data without search filter - explicitly pass selectedCountryId
      fetchAllBillboardData('', 0, cartBillboardIds, billboardType, undefined, undefined, selectedCountryId);
    }
  };


  // const handleAddToCart = (inventoryItem: any) => {
  //   // Handle deals with special "Deal" type
  //   if (filters.category === 'deals' || inventoryItem.discountedPrice || inventoryItem.originalPrice) {
  //     const cartItem = {
  //       id: inventoryItem.id,
  //       name: inventoryItem.title,
  //       location: inventoryItem.location,
  //       state: inventoryItem.state || "Multiple",
  //       price: inventoryItem.discountedPrice || inventoryItem.price,
  //       impressions: inventoryItem.impressions,
  //       size: inventoryItem.specifications?.resolution || "Various",
  //       type: "Deal" as const,
  //       status: inventoryItem.availableBooking ? "available" as const : "sold-out" as const,
  //       image: inventoryItem.imageUrl,
  //       specifications: {
  //         resolution: inventoryItem.specifications?.resolution || "Various",
  //         duration: inventoryItem.specifications?.duration || "Various"
  //       }
  //     };
  //     addToCart(cartItem);
  //   } else if (inventoryItem.inventoryType === "Package") {
  //     // Handle packages
  //     const cartItem = {
  //       id: inventoryItem.id,
  //       name: inventoryItem.title || inventoryItem.name,
  //       location: inventoryItem.location,
  //       state: inventoryItem.state,
  //       price: inventoryItem.price || 0,
  //       impressions: inventoryItem.screenCount ? inventoryItem.screenCount * 100000 : inventoryItem.impressions || 0,
  //       size: `${inventoryItem.screenCount} locations`,
  //       type: "Package" as const,
  //       status: inventoryItem.availableBooking ? "available" as const : "sold-out" as const,
  //       image: inventoryItem.imageUrl || inventoryItem.image,
  //       specifications: {
  //         resolution: "Various",
  //         duration: "Package Deal"
  //       }
  //     };
  //     addToCart(cartItem);
  //   } else {
  //     // Map database inventory types to display types
  //     const getDisplayType = (inventoryType: string | undefined) => {
  //       switch (inventoryType) {
  //         case 'DIGITAL': return 'Digital' as const;
  //         case 'CLASSIC': return 'Classic' as const;
  //         case 'digital': return 'Digital' as const;
  //         case 'static': return 'Classic' as const;
  //         case 'Package': return 'Package' as const;
  //         case 'transit': return 'Transit' as const;
  //         case 'Digital': return 'Digital' as const;
  //         case 'Classic': return 'Classic' as const;
  //         case 'Transit': return 'Transit' as const;
  //         default: return 'Digital' as const;
  //       }
  //     };

  //     // Convert API inventory item to cart format
  //     const cartItem = {
  //       id: inventoryItem.id,
  //       name: inventoryItem.displayName || inventoryItem.name,
  //       location: `${inventoryItem.trDistrictName}, ${inventoryItem.trStateName}`,
  //       state: inventoryItem.trStateName || inventoryItem.state,
  //       price: inventoryItem.currentPrice || inventoryItem.sellingRate?.monthly?.month1 || 0,
  //       impressions: inventoryItem.monthlySummary?.totalVisitors || inventoryItem.impressions || 0,
  //       size: inventoryItem.type === 'CLASSIC'
  //         ? (inventoryItem.panelWidth && inventoryItem.panelHeight
  //           ? `${inventoryItem.panelWidth}x${inventoryItem.panelHeight}`
  //           : 'Standard')
  //         : (inventoryItem.resolutionWidth && inventoryItem.resolutionHeight
  //           ? `${inventoryItem.resolutionWidth}x${inventoryItem.resolutionHeight}`
  //           : inventoryItem.specification?.resolution1Width && inventoryItem.specification?.resolution1Height
  //             ? `${inventoryItem.specification.resolution1Width}x${inventoryItem.specification.resolution1Height}`
  //             : 'Standard'),
  //       type: getDisplayType(inventoryItem.type),
  //       status: inventoryItem.availableBooking ? "available" as const : "sold-out" as const,
  //       image: inventoryItem.fullResImagePath || inventoryItem.thumbnailPath || inventoryItem.imageUrl || `${import.meta.env.VITE_APP_FOLDER_PATH}/nomedia.jpg`,
  //       specifications: {
  //         resolution: inventoryItem.type === 'CLASSIC'
  //           ? (inventoryItem.panelWidth && inventoryItem.panelHeight
  //             ? `${inventoryItem.panelWidth}x${inventoryItem.panelHeight}`
  //             : 'Standard')
  //           : (inventoryItem.resolutionWidth && inventoryItem.resolutionHeight
  //             ? `${inventoryItem.resolutionWidth}x${inventoryItem.resolutionHeight}`
  //             : inventoryItem.specification?.resolution1Width && inventoryItem.specification?.resolution1Height
  //               ? `${inventoryItem.specification.resolution1Width}x${inventoryItem.specification.resolution1Height}`
  //               : "1920x1080"),
  //         duration: inventoryItem.type === 'DIGITAL' ? "15 seconds" : "Monthly"
  //       }
  //     };
  //     addToCart(cartItem);
  //   }
  // };

  const handleViewDetails = (inventoryItem: any) => {
    console.log("Viewing details for inventory item:", inventoryItem);

    // Backup current country before navigation (for draft/cart scenarios)
    const currentCountry = countryOptions.find((c: any) => c.value === selectedCountryId);
    if (currentCountry) {
      localStorage.setItem('navigationCountryBackup', JSON.stringify({
        countryId: currentCountry.id,
        countryValue: currentCountry.value,
        countryLabel: currentCountry.label,
        timestamp: Date.now(),
        isDraft: edit, // Mark if this is a draft flow
        isCartCountry: hasCartCountry() // Mark if this is cart country
      }));
      console.log('💾 [handleViewDetails] Backed up country before navigation:', currentCountry.label, {
        isDraft: edit,
        isCart: hasCartCountry(),
        countryId: currentCountry.id
      });
    }

    const billboardType = inventoryItem.inventoryType || inventoryItem.type || 'digital';

    // Build base URL for navigation
    let targetUrl = '';
    let stateData: any = {
      currentPrice: inventoryItem.type === 'DIGITAL'
        ? priceFormat === 'spot-based'
          ? inventoryItem.perSpotPrice
          : inventoryItem.perDayPrice
        : (inventoryItem.sellingRate?.monthly?.month1 && inventoryItem.sellingRate.monthly.month1 > 0)
          ? inventoryItem.sellingRate.monthly.month1
          : inventoryItem.currentPrice,
      currencyCode: inventoryItem.price?.localCurrencies?.code,
      startDate,
      endDate,
      campaignName,
      priceFormat
    };

    // Construct query params for draft/edit flow with dates
    const formattedStartDate = startDate ? format(startDate, 'yyyy-MM-dd') : '';
    const formattedEndDate = endDate ? format(endDate, 'yyyy-MM-dd') : '';

    const draftParams = edit
      ? `?draftId=${campaignId}&campaignName=${encodeURIComponent(campaignName)}&startDate=${formattedStartDate}&endDate=${formattedEndDate}&source=${encodeURIComponent(source)}&edit=true`
      : '';

    console.log('🔗 [handleViewDetails] Navigating with draft params:', {
      edit,
      draftId: campaignId,
      campaignName,
      startDate: formattedStartDate,
      endDate: formattedEndDate,
      source
    });

    // Choose navigation route based on item type/category
    if (filters.category === 'deals' || inventoryItem.discountedPrice || inventoryItem.originalPrice) {
      targetUrl = `/deal/${inventoryItem.id}${draftParams}`;
    } else if (filters.category === 'packages' || inventoryItem.registryPackageType === "DAYWISE") {
      console.log("Navigating to package details page with draft params");
      targetUrl = `/package/${inventoryItem.id}${draftParams}`;
    } else {
      console.log("Navigating to property details page with draft params");
      // For property details, append draft params to existing type param
      const separator = draftParams ? '&' : '';
      targetUrl = `/property-details/${inventoryItem.id}?type=${encodeURIComponent(billboardType)}${separator}${draftParams.substring(1)}`; // Remove leading ? from draftParams
    }

    navigate(targetUrl, { state: stateData });
  };


  const getStatusColor = (inventoryItem: any) => {
    return getStatusText(inventoryItem) === "Available"
      ? "bg-emerald-100 text-emerald-700"
      : "bg-red-100 text-red-700";
  };

  const getStatusText = (inventoryItem: any) => {
    // Calculate price validity (same as your button logic)
    let price = 0;
    if (inventoryItem.type === 'DIGITAL') {
      price = priceFormat === 'spot-based'
        ? inventoryItem.perSpotPrice
        : inventoryItem.perDayPrice;
    } else {
      price = (inventoryItem.sellingRate?.monthly?.month1 && inventoryItem.sellingRate.monthly.month1 > 0)
        ? inventoryItem.sellingRate.monthly.month1
        : inventoryItem.currentPrice;
    }
    const isPriceValid = !!price && price !== 0;

    if (!isPriceValid) {
      return "Unavailable";
    } else if (!inventoryItem.availableBooking || !inventoryItem.active) {
      return "Sold Out";
    } else {
      return "Available";
    }
  };

  const toggleSection = (section: keyof typeof defaultExpandedSections) => {
    setExpandedSections((prev: any) => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const updateFilter = (key: string, value: any, shouldTriggerApi: boolean = true) => {
    console.log(`🎯 updateFilter called: key=${key}, value=`, value, `shouldTriggerApi=${shouldTriggerApi}`);

    setFilters((prev: typeof defaultFilters) => ({
      ...prev,
      [key]: value
    }));

    // Only trigger API call for certain filter changes
    if (shouldTriggerApi && key !== 'state') {
      // For category change to 'packages', trigger package API
      if (key === 'category' && value === 'packages') {
        console.log(`🎁 Triggering package API call for category change to packages`);
        setTimeout(() => {
          const cartPackageIds = new Set(cartDataList.flatMap((cart: any) =>
            cart.campaignInventories
              ?.filter((inv: any) => inv.billboardType === 'package')
              ?.map((inv: any) => inv.billboardId)
          ).filter(Boolean));
          console.log(`🎁 About to call fetchPackageData for category change`);
          fetchPackageData(cartPackageIds, selectedCountryId, search);
        }, 300);
      }
      // For district changes or other changes when in billboard category, trigger billboard API
      else if (key === 'district' || key === 'priceRange' || (key === 'category' && value !== 'packages')) {
        console.log(`🚀 Triggering billboard API call for ${key} change`);
        setTimeout(() => {
          const cartBillboardIds = new Set(cartDataList.flatMap((cart: any) =>
            cart.campaignInventories?.map((inv: any) => inv.billboardId)
          ).filter(Boolean));
          console.log(`🚀 About to call debouncedFetchAllBillboardData for ${key}Change`);
          debouncedFetchAllBillboardData(search, 0, cartBillboardIds, `${key}Change`);
        }, 300);
      }
    } else {
      console.log(`⏸️ Not triggering API call for ${key} (shouldTriggerApi: ${shouldTriggerApi})`);
    }
  };

  const toggleArrayFilter = (key: string, value: string) => {
    console.log(`🎯 toggleArrayFilter called: key=${key}, value=${value}`);

    setFilters((prev: typeof defaultFilters) => {
      const currentArray = prev[key as keyof typeof prev] as string[];
      console.log(`📋 Current ${key} array:`, currentArray);

      const newArray = currentArray.includes(value)
        ? currentArray.filter(item => item !== value)
        : [...currentArray, value];

      console.log(`📋 New ${key} array:`, newArray);

      const newFilters = {
        ...prev,
        [key]: newArray
      };
      if (typeof window !== 'undefined') {
        localStorage.setItem('start-campaign-filters', JSON.stringify(newFilters));
      }

      // If this is venue type filter, update venueTypeData with expanded children
      if (key === 'venueType') {
        console.log(`🏢 Processing venue type filter change`);
        const expandedVenueTypes: string[] = [];
        newArray.forEach(parentVenueType => {
          // Add the parent itself (now using API spelling directly)
          expandedVenueTypes.push(parentVenueType);
          // Add all children and grandchildren (also using API spelling directly)
          const children = getAllChildrenForVenueType(parentVenueType);
          expandedVenueTypes.push(...children);
        });

        // Remove duplicates and update venueTypeData
        const uniqueVenueTypes = [...new Set(expandedVenueTypes)];
        console.log(`🌳 Setting venueTypeData to:`, uniqueVenueTypes);
        setVenueTypeData(uniqueVenueTypes);

        // API call will be triggered automatically by useEffect when venueTypeData changes
        console.log(`⏳ API call will be triggered by useEffect dependency`);
      }

      return newFilters;
    });
  };

  // New function for single value filters (like radio buttons)
  const handleSingleFilter = (key: string, value: string) => {
    console.log(`🎯 handleSingleFilter called: key=${key}, value=${value}`);

    setFilters((prev: typeof defaultFilters) => {
      const newFilters = {
        ...prev,
        [key]: value
      };

      if (typeof window !== 'undefined') {
        localStorage.setItem('start-campaign-filters', JSON.stringify(newFilters));
      }

      // If this is venue type filter, update venueTypeData with expanded children
      // If this is environment filter, trigger API call
      if (key === 'environment') {
        currentEnvironmentRef.current = value;
        console.log(`🌍 Processing environment filter change`);
        // Trigger API call for environment change
        setTimeout(() => {
          const cartBillboardIds = new Set(cartDataList.flatMap((cart: any) =>
            cart.campaignInventories?.map((inv: any) => inv.billboardId)
          ).filter(Boolean));
          debouncedFetchAllBillboardData(search, 0, cartBillboardIds, 'environmentChange', undefined, undefined, selectedCountryId);
        }, 300);
      }
      // If this is venue type filter, update venueTypeData with expanded children
      else if (key === 'venueType') {

        console.log(`🏢 Processing venue type filter change with parent value: ${value}`);
        const parentVenueType = value ? [value] : []; // Only include the parent value
        console.log(`🌳 Setting venueTypeData to:`, parentVenueType);
        setVenueTypeData(parentVenueType);

        // API call will be triggered automatically by useEffect when venueTypeData changes
        console.log(`⏳ API call will be triggered by useEffect dependency`);


        // console.log(`🏢 Processing venue type filter change`);
        // const expandedVenueTypes: string[] = [];

        // if (value) { // Only process if value is not empty
        //   // Add the parent itself
        //   expandedVenueTypes.push(value);
        //   // Add all children and grandchildren
        //   const children = getAllChildrenForVenueType(value);
        //   expandedVenueTypes.push(...children);
        // }

        // // Remove duplicates and update venueTypeData
        // const uniqueVenueTypes = [...new Set(expandedVenueTypes)];
        // console.log(`🌳 Setting venueTypeData to:`, uniqueVenueTypes);
        // setVenueTypeData(uniqueVenueTypes);

        // // API call will be triggered automatically by useEffect when venueTypeData changes
        // console.log(`⏳ API call will be triggered by useEffect dependency`);
      }

      return newFilters;
    });
  };

  const clearAllFilters = async () => {
    // Get fresh cart data to avoid stale state issues
    console.log('🧹 Clear All - Fetching fresh cart data to avoid stale state...');
    const freshCartCount = await fetchCartCountDetails();
    const hasCartData = freshCartCount > 0;
    console.log('🧹 Clear All Filters - Fresh cart count:', freshCartCount, 'hasCartData:', hasCartData);

    let finalCountryId = selectedCountryId; // Default to current selection
    let finalCountryObj = null;

    if (hasCartData) {
      // Cart has data → Keep current country, clear other filters
      setFilters({
        ...defaultFilters,
        country: selectedCountryId, // Preserve cart country
        environment: "",
      });
      currentEnvironmentRef.current = '';
      finalCountryId = selectedCountryId;
      console.log('🛒 Preserved cart country during clear all:', selectedCountryId);
    } else {
      // Cart is empty → Reset to session default
      console.log('🧹 Cart empty - resetting to session default country...');

      let targetCountryId = null;
      let targetCountryObj = null;
      let source = '';

      // Get session default country
      const sessionDefaultId = localStorage.getItem('sessionDefaultCountryId');
      if (sessionDefaultId) {
        targetCountryObj = countryOptions.find((c: any) => c.id === sessionDefaultId);
        if (targetCountryObj) {
          targetCountryId = sessionDefaultId;
          source = 'sessionDefault';
          console.log('🌍 Using session default country:', targetCountryObj.label);
        }
      }

      setFilters({
        ...defaultFilters,
        country: targetCountryObj?.value || '', // Use determined country
        environment: "",
      });
      currentEnvironmentRef.current = '';

      if (targetCountryObj) {
        setSelectedCountryId(targetCountryObj.value);
        finalCountryId = targetCountryObj.value;
        finalCountryObj = targetCountryObj;
        console.log(`✅ Reset to ${source} country:`, targetCountryObj.label);
      } else {
        console.log('⚠️ No default country found, using empty');
        finalCountryId = '';
      }
    }

    // Clear other filter states
    setSearchQuery("");
    setSearch(""); // Clear the actual search state that triggers API calls
    setLocalPriceRange([0, 0]);
    setMinPriceInput('');
    setMaxPriceInput('');

    // Reset location filters
    setState([]);
    setDistrict([]);
    setAvailableDistricts([]);

    // Reset billboard type to default (digital)
    setBillboardType("digital");

    // Reset other filter states
    setLocationData([]);
    setSitetypeData([]);
    setVenueTypeData([]); // ✅ IMPORTANT: Clear venueTypeData state
    setIndoorChecked(false);
    setOutdoorChecked(false);

    setSpotBasedBooking(false);
    setPriceFormat('day-based');
    setBuyingFormat('day-based'); // <-- Reset Buying Format UI
    console.log('[CLEAR ALL] spotBasedBooking set to false', spotBasedBooking);
    
    // Reset pagination
    setCurrentPage(0);

    // Reset no data message
    setNoDataMessage(null);

    // Clear from localStorage
    if (typeof window !== 'undefined') {
      localStorage.removeItem('start-campaign-filters');
      localStorage.removeItem('start-campaign-search-query');
      localStorage.removeItem('campaignListState');
      localStorage.removeItem('start-campaign-expanded-sections');
      localStorage.removeItem('propcurrentPage');
    }

    // ✅ UPDATE filtersRef with cleared values BEFORE API call
    filtersRef.current = {
      ...defaultFilters,
      country: finalCountryId,
      environment: "",
      mediaOwner: [],  // ✅ IMPORTANT: Clear media owner
      venueType: "",   // ✅ IMPORTANT: Clear venue type
    };

    // Trigger API call with cleared filters and explicit country ID
    setTimeout(() => {
      console.log('🚀 Clear All - Triggering fetch with country:', finalCountryId);
      console.log('🚀 Clear All - Cleared mediaOwner:', filtersRef.current.mediaOwner);
      console.log('🚀 Clear All - Cleared venueType:', filtersRef.current.venueType);
      fetchAllBillboardData(
        "", // search value
        0, // page
        new Set(cartDataList.flatMap((cart: any) =>
          cart.campaignInventories?.map((inv: any) => inv.billboardId) || []
        )), // cartBillboardIds
        undefined, // overrideBillboardType
        [], // overrideState
        undefined, // overridePriceRange
        finalCountryId, // countryId
        undefined, // overrideDistrict
        "", // overrideCompanyId - ✅ Explicitly pass empty string
        [] // overrideVenueType - ✅ Explicitly pass empty array
      );
    }, 100);
  };

  const FilterSection = ({ title, isExpanded, onToggle, children }: any) => (
    <div className="border-b border-gray-200 pb-4 mb-4">
      <button
        onClick={onToggle}
        className="flex items-center justify-between w-full text-left"
      >
        <h3 className="text-sm font-medium text-gray-900">{title}</h3>
        {isExpanded ? (
          <ChevronUp className="h-4 w-4 text-gray-500" />
        ) : (
          <ChevronDown className="h-4 w-4 text-gray-500" />
        )}
      </button>
      {isExpanded && <div className="mt-3 space-y-3">{children}</div>}
    </div>
  );

  const CheckboxGroup = ({ options, value, onChange }: any) => (
    <div className="space-y-2">
      {options.map((option: string) => (
        <div key={option} className="flex items-center space-x-2">
          <Checkbox
            id={option}
            checked={value.includes(option)}
            onCheckedChange={() => onChange(option)}
          />
          <Label htmlFor={option} className="text-sm text-gray-700 cursor-pointer">
            {option}
          </Label>
        </div>
      ))}
    </div>
  );

  useEffect(() => {
    console.log("🏁 StartCampaign mount useEffect triggered", localStorage.getItem('clearStartCampaignFilters'), sessionStorage.getItem('filtersClearedThisSession'));

    // Safeguard: Only process if not already handled in this session
    // if (sessionStorage.getItem('filtersClearedThisSession') === 'true') {
    //   console.log('Filters already cleared this session, skipping');
    //   return;
    // }

    const shouldClear = localStorage.getItem('clearStartCampaignFilters');
    if (shouldClear) {
      console.log('🔄 Clearing filters due to localStorage flag (mount detected)');
      clearAllFilters(); // Your existing function
      localStorage.removeItem('clearStartCampaignFilters');
      sessionStorage.setItem('filtersClearedThisSession', 'true'); // Prevent repeats
    } else {
      console.log('✅ No clear flag found, filters intact');
    }
  }, []); // Still runs only on mount

  // Place the package API useEffect here - after all state and function declarations
  useEffect(() => {
    // 🔍 LOGGING COUNTER
    packageFetchCount.current += 1;
    console.log(`📦 [Package Effect] Call #${packageFetchCount.current} triggered`);

    if (filters.category === 'packages' && selectedCountryId && countryOptions.length > 0) {
      const cartPackageIds = new Set(cartDataList.flatMap((cart: any) =>
        cart.campaignInventories
          ?.filter((inv: any) => inv.billboardType === 'package')
          ?.map((inv: any) => inv.billboardId)
      ).filter(Boolean));
      fetchPackageData(cartPackageIds, selectedCountryId, search);
    }
    // 🔴 FIX: Removed 'cartDataList' from dependencies
  }, [filters.category, selectedCountryId, countryOptions.length, search]);

  return (
    <div className="min-h-screen bg-gray-50">
      <Header yourLogo={yourLogo} configData={configData} base_path={base_path} cartUrl={cartUrl} />

      <div className="max-w-[95vw] mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-emerald-600">Start New Campaign</h1>
            <p className="text-gray-600 mt-2">Select Inventories for your advertising campaign</p>
          </div>
          <div className="flex items-center gap-4">
            {/* {cartCount > 0 && (
              <Link
                to={
                  edit && campaignId
                    ? `/mycart?draftId=${campaignId}&draftName=${encodeURIComponent(campaignName)}&startDate=${startDate || ''}&endDate=${endDate || ''}&source=${source}&edit=${edit}`
                    : "/mycart"
                }
              >
                <Button className="bg-emerald-600 hover:bg-emerald-700">
                  <ShoppingCart className="h-4 w-4 mr-2" />
                  View Cart ({cartCount})
                </Button>
              </Link>
            )} */}
            <Button
              variant="outline"
              onClick={() => {
                if (edit) {
                  setCartCount(0);
                  setCartItemCountContext(0);
                  setCartItems([]);
                  setCartDataList([]);
                  setCartData([]);
                }
                navigate(`${base_path}/my-campaigns`);
              }}
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Campaigns
            </Button>
          </div>
        </div>

        <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
          {/* Left Sidebar - Filters */}
          <div className="w-full lg:w-64 flex-shrink-0">
            <Card>
              <CardHeader className="pb-4">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg text-emerald-600">Filter By</CardTitle>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearAllFilters}
                    className="text-emerald-600 hover:text-emerald-700"
                  >
                    Clear All
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Search */}
                <div>
                  <Label htmlFor="search" className="text-sm font-medium text-gray-900">
                    Search
                  </Label>
                  <div className="relative mt-1 h-10">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      id="search"
                      type="text"
                      placeholder="Search Inventory"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onKeyPress={handleSearchKeyPress}
                      className={`px-10 pr-10 ${searchQuery.length > 0 && searchQuery.length < 3
                        ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                        : 'border-emerald-200 focus:border-emerald-500 focus:ring-emerald-500'
                        }`}
                    />
                    {searchQuery && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleResetSearch}
                        className="absolute right-2 top-1/2 transform -translate-y-1/2 h-6 w-6 p-0 hover:bg-gray-200 rounded-full"
                        title="Clear search"
                      >
                        <X className="h-3 w-3 text-gray-400" />
                      </Button>
                    )}
                  </div>
                  {searchQuery.length > 0 && searchQuery.length < 3 && (
                    <p className="text-xs text-red-600 mt-1">
                      Enter at least 3 characters, then press Enter to search
                    </p>
                  )}

                </div>

                <Separator />

                {/* Location Filters */}
                <FilterSection
                  title="Location"
                  isExpanded={expandedSections.location}
                  onToggle={() => toggleSection('location')}
                >
                  {/* Country Dropdown */}
                  <div>
                    <div className="flex items-center space-x-1">
                      <Label className="text-sm font-medium text-gray-700">
                        Country
                      </Label>
                      {(countryLoading || !countryOptions.length || (contextCartItems && contextCartItems.length > 0) || edit) && (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span tabIndex={0}>
                                <Info className="h-3 w-3 text-gray-400 cursor-pointer" />
                              </span>
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs text-left bg-gray-900 text-white p-3 rounded-md shadow-lg border border-gray-700">
                              <div className="space-y-1">
                                <p className="text-gray-200">Remove all Inventories or start a new proposal to change country.</p>
                              </div>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}
                    </div>
                    <div className="relative mt-1">
                      <Button
                        variant="outline"
                        onClick={() => {
                          if (contextCartItems && contextCartItems.length > 0) {
                            toast({
                              title: "Cannot change country",
                              description: "You have items in your cart from a specific country. Please complete your current campaign or clear your cart to change countries.",
                              variant: "destructive"
                            });
                            return;
                          }
                          if (edit) {
                            toast({
                              title: "Cannot change country",
                              description: "You cannot change the country for a draft campaign. The country is fixed based on the draft.",
                              variant: "destructive"
                            });
                            return;
                          }
                          setShowCountryDropdown(!showCountryDropdown);
                        }}
                        className="w-full justify-between text-left font-normal"
                        disabled={countryLoading || !countryOptions.length || (contextCartItems && contextCartItems.length > 0) || edit}
                      >
                        {countryLoading ? (
                          "Loading countries..."
                        ) : filters.country ? (
                          countryOptions.find((c: any) => c.value === filters.country)?.label || "Select Country"
                        ) : (
                          "Select Country"
                        )}
                        <ChevronDown className="h-4 w-4" />
                      </Button>
                      {showCountryDropdown && countryOptions.length > 0 && (
                        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-y-auto">
                          {countryOptions.map((country: any) => {
                            const isSelected = filters.country === country.value;
                            return (
                              <div
                                key={country.value}
                                className={`px-3 py-2 hover:bg-gray-50 cursor-pointer flex items-center space-x-2 ${isSelected ? 'bg-emerald-50 text-emerald-600' : 'text-gray-700'}`}
                                onClick={() => handleCountrySelection(country.value)}
                              >
                                <span className="text-sm flex-1">{country.label}</span>
                                {isSelected && (
                                  <span className="text-emerald-600">✓</span>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>

                  <div>
                    <Label className="text-sm font-medium text-gray-700">State</Label>
                    <div className="space-y-2 max-h-40 overflow-y-auto border rounded p-2 mt-1">
                      {availableStates.map((stateItem) => (
                        <div key={stateItem.stateId || stateItem.id} className="flex items-center space-x-2">
                          <Checkbox
                          className="!border-emerald-600 !data-[state=checked]:bg-emerald-600"
                            id={`state-${stateItem.stateId || stateItem.id}`}
                            checked={filters.state.includes(stateItem.stateId || stateItem.id)}
                            onCheckedChange={async () => {
                              const newStates = filters.state.includes(stateItem.stateId || stateItem.id)
                                ? filters.state.filter((id: string) => id !== (stateItem.stateId || stateItem.id))
                                : [...filters.state, stateItem.stateId || stateItem.id];

                                console.log('Updating state to:', newStates);

                                // Use manual state change flag to prevent duplicate API calls
                                isManualStateChange.current = true;

                                setState(newStates);
                                updateFilter('state', newStates, false);

                                // Immediately trigger API call with new states
                                const cartBillboardIds = new Set(cartDataList.flatMap((cart: any) =>
                                  cart.campaignInventories?.map((inv: any) => inv.billboardId)
                                ).filter(Boolean));

                                // Pass newStates directly to avoid React state delay
                                fetchAllBillboardData(search, 0, cartBillboardIds, undefined, newStates).finally(() => {
                                  // Reset flag after API call completes
                                  setTimeout(() => {
                                    isManualStateChange.current = false;
                                  }, 500);
                                });

                                if (newStates.length > 0) {
                                  try {
                                    const { data } = await getDistricts({
                                      variables: { stateIds: Array.from(new Set(newStates)).filter(Boolean).join(','), exposure: exposure },
                                      fetchPolicy: 'network-only',
                                    });
                                    const newAvailableDistricts = data?.districtListMarketplace || [];
                                    setAvailableDistricts(newAvailableDistricts);

                                    // Remove invalid districts
                                    const validDistrictIds = newAvailableDistricts.map((d: any) => d.districtId || d.id);
                                    const newDistricts = filters.district.filter((districtId: string) => validDistrictIds.includes(districtId));
                                    setDistrict(newDistricts);
                                    updateFilter('district', newDistricts, true); // Trigger API call with valid districts
                                  } catch (error) {
                                    console.error('Error fetching districts:', error);
                                    setAvailableDistricts([]);
                                    setDistrict([]);
                                    updateFilter('district', [], true);
                                  }
                                } else {
                                  setAvailableDistricts([]);
                                  setDistrict([]);
                                  updateFilter('district', [], true);
                                }
                              }}
                            />
                            <Label htmlFor={`state-${stateItem.stateId || stateItem.id}`} className="text-sm text-gray-700 cursor-pointer">
                              {stateItem.name}
                            </Label>
                          </div>
                        ))}
                    </div>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-gray-700">District</Label>
                    <div className="space-y-2 max-h-40 overflow-y-auto border rounded p-2 mt-1">
                      {availableDistricts.map((districtItem) => (
                        <div key={districtItem.districtId || districtItem.id} className="flex items-center space-x-2">
                          <Checkbox
                          className="!border-emerald-600 !data-[state=checked]:bg-emerald-600"
                            id={`district-${districtItem.districtId || districtItem.id}`}
                            checked={filters.district.includes(districtItem.id || districtItem.districtId)}
                            onCheckedChange={() => {
                              let newDistricts = filters.district.includes(districtItem.id || districtItem.districtId)
                                ? filters.district.filter((id: string) => id !== (districtItem.id || districtItem.districtId))
                                : [...filters.district, districtItem.id || districtItem.districtId];
                              // Use manual district change flag to prevent duplicate API calls
                              isManualDistrictChange.current = true;

                                setDistrict(newDistricts);
                                updateFilter('district', newDistricts); // This will trigger billboard API
                                // Immediately trigger API call with new districts
                                const cartBillboardIds = new Set(cartDataList.flatMap((cart: any) =>
                                  cart.campaignInventories?.map((inv: any) => inv.billboardId)
                                ).filter(Boolean));

                                // Pass both state (filters.state) and new districts to API
                                fetchAllBillboardData(search, 0, cartBillboardIds, undefined, filters.state, undefined, undefined, newDistricts).finally(() => {
                                  // Reset flag after API call completes
                                  setTimeout(() => {
                                    isManualDistrictChange.current = false;
                                  }, 500);
                                });
                              }}
                            />
                            <Label htmlFor={`district-${districtItem.districtId || districtItem.id}`} className="text-sm text-gray-700 cursor-pointer">
                              {districtItem.name}
                            </Label>
                          </div>
                        ))}
                    </div>
                  </div>
                </FilterSection>

                {/* Category Filters */}
                <FilterSection
                  title="Inventory Type"
                  isExpanded={expandedSections.category}
                  onToggle={() => toggleSection('category')}
                >
                  <div className="space-y-2">
                    {['digital', 'classic'].map((category) => (
                      <div key={category} className="flex items-center space-x-2">
                        <input
                          type="radio"
                          id={category}
                          name="inventoryTypeRadio"
                          checked={filters.category === category}
                          onChange={() => {
                            handleBillboardTypeChange(category);
                            updateFilter('category', category);
                            setCurrentPage(0);
                            if (category === 'digital') {
                              // updateFilter('spotBasedBookingOnly', true);
                              setPriceFormat('day-based');
                            }
                          }}
                          className="form-radio h-4 w-4 !accent-emerald-600 border-emerald-600 focus:ring-emerald-500"
                        />
                        <Label htmlFor={category} className="text-sm text-gray-700 cursor-pointer capitalize">
                          {category}
                        </Label>
                      </div>
                    ))}
                  </div>
                </FilterSection>

                {/* Buying Format Filter */}
                {filters.category === 'digital' && (
                  <FilterSection
                    title="Buying Format"
                    isExpanded={expandedSections.buyingFormat}
                    onToggle={() => toggleSection('buyingFormat')}
                  >
                    <div className="space-y-2">
                      <div className="flex items-center space-x-2">
                        <input
                          type="radio"
                          id="spot-based"
                          name="buyingFormatRadio"
                          checked={buyingFormat === 'spot-based'}
                          onChange={() => {
                            setBuyingFormat('spot-based');
                            setPriceFormat('spot-based');
                            setSpotBasedBooking(true);
                            console.log('[RADIO] Spot based selected, spotBasedBooking set to TRUE');
                          }}
                          className="form-radio h-4 w-4 !accent-emerald-600 border-emerald-600 focus:ring-emerald-500"
                        />
                        <Label htmlFor="spot-based" className="text-sm text-gray-700 cursor-pointer capitalize">
                          Spot based
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <input
                          type="radio"
                          id="day-based"
                          name="buyingFormatRadio"
                          checked={buyingFormat === 'day-based'}
                          onChange={() => {
                            setBuyingFormat('day-based');
                            setPriceFormat('day-based');
                            setSpotBasedBooking(false);
                            console.log('[RADIO] Day based selected, spotBasedBooking set to FALSE');
                          }}
                          className="form-radio h-4 w-4 !accent-emerald-600 border-emerald-600 focus:ring-emerald-500"
                        />
                        <Label htmlFor="day-based" className="text-sm text-gray-700 cursor-pointer capitalize">
                          Day based
                        </Label>
                      </div>
                    </div>
                  </FilterSection>
                )}

                {/* Value Packs Filters */}
                <FilterSection
                  title="Value Packs"
                  isExpanded={expandedSections.valuePacks}
                  onToggle={() => toggleSection('valuePacks')}
                >
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <input
                        type="radio"
                        id="packages"
                        name="inventoryTypeRadio"
                        checked={filters.category === 'packages'}
                        onChange={() => {
                          updateFilter('category', 'packages');
                        }}
                        className="form-radio h-4 w-4 !accent-emerald-600 border-emerald-600 focus:ring-emerald-500"
                      />
                      <Label htmlFor="packages" className="text-sm text-gray-700 cursor-pointer capitalize">
                        packages
                      </Label>
                    </div>
                  </div>
                </FilterSection>

                {filters.category !== 'packages' && (
                  <FilterSection
                    title="Environment"
                    isExpanded={expandedSections.environment}
                    onToggle={() => toggleSection('environment')}
                  >
                    <div className="space-y-2">
                      <div className="flex items-center space-x-2">
                        <input
                          type="radio"
                          id="environment-all"
                          name="environmentRadio"
                          checked={filters.environment === '' || !filters.environment}
                          onChange={() => handleSingleFilter('environment', '')}
                          className="form-radio h-4 w-4 !accent-emerald-600 border-emerald-600 focus:ring-emerald-500"
                        />
                        <Label htmlFor="environment-all" className="text-sm text-gray-700 cursor-pointer">
                          All
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <input
                          type="radio"
                          id="indoor"
                          name="environmentRadio"
                          checked={filters.environment === 'INDOOR'}
                          onChange={() => handleSingleFilter('environment', 'INDOOR')}
                          className="form-radio h-4 w-4 !accent-emerald-600 border-emerald-600 focus:ring-emerald-500"
                        />
                        <Label htmlFor="indoor" className="text-sm text-gray-700 cursor-pointer">
                          Indoor
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <input
                          type="radio"
                          id="outdoor"
                          name="environmentRadio"
                          checked={filters.environment === 'OUTDOOR'}
                          onChange={() => handleSingleFilter('environment', 'OUTDOOR')}
                          className="form-radio h-4 w-4 !accent-emerald-600 border-emerald-600 focus:ring-emerald-500"
                        />
                        <Label htmlFor="outdoor" className="text-sm text-gray-700 cursor-pointer">
                          Outdoor
                        </Label>
                      </div>
                    </div>
                  </FilterSection>
                )}

                {/* Pricing Filter */}
                <FilterSection
                  title="Pricing"
                  isExpanded={expandedSections.pricing}
                  onToggle={() => toggleSection('pricing')}
                >
                  {(() => {
                    const selectedCurrencyCode = countryOptions.find((c: any) => c.value === filters.country)?.currencyCode || 'N/A';

                    return (
                      <div className="space-y-3">
                        <Label className="text-sm font-medium text-gray-700">
                          Price Range: {selectedCurrencyCode} {filters.priceRange?.[0] ?? 0} - {selectedCurrencyCode} {filters.priceRange && filters.priceRange[1] < 99999999 ? filters.priceRange[1] : 'No Limit'}
                        </Label>
                        <div className="flex items-center space-x-2">
                          <Input
                            type="number"
                            min={0}
                            value={minPriceInput}
                            onChange={handleMinPriceChange}
                            onKeyPress={handlePriceKeyPress}
                            className="w-24"
                            placeholder="Min"
                            aria-label="Minimum price"
                            autoComplete="off"
                          />
                          <span className="text-gray-500">-</span>
                          <Input
                            type="number"
                            min={0}
                            value={maxPriceInput}
                            onChange={handleMaxPriceChange}
                            onKeyPress={handlePriceKeyPress}
                            className="w-24"
                            placeholder="No Limit"
                            aria-label="Maximum price"
                            autoComplete="off"
                          />
                        </div>
                        <div className="flex justify-between text-xs text-gray-500">
                          <span>{selectedCurrencyCode} 0</span>
                          <span>{selectedCurrencyCode} No Limit</span>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          Press Enter to apply price filter
                        </p>
                      </div>
                    );
                  })()}
                </FilterSection>

                {/* Media Owner Filter */}
                <FilterSection
                  title="Media Owner"
                  isExpanded={expandedSections.mediaOwner}
                  onToggle={() => toggleSection('mediaOwner')}
                >
                  <div className="space-y-2">
                    <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                      {mediaOwnerLoading && (
                        <div className="text-sm text-gray-500">Loading media owners...</div>
                      )}
                      {mediaOwnerError && (
                        <div className="text-sm text-red-500">No media owner filter available</div>
                      )}
                      {!mediaOwnerLoading && !mediaOwnerError && mediaOwnerOptions.length === 0 && (
                        <div className="text-sm text-gray-500">No media owners found</div>
                      )}
                      {!mediaOwnerLoading && !mediaOwnerError && mediaOwnerOptions.map((owner: { label: string; value: string }) => (
                        <div key={owner.value} className="flex items-center space-x-2">
                          <Checkbox
                            id={owner.value}
                            checked={filters.mediaOwner?.includes(owner.value) || false}
                            onCheckedChange={() => {
                              toggleArrayFilter('mediaOwner', owner.value);
                              // scrollToTop();
                            }}
                            className="!border-emerald-600 !data-[state=checked]:bg-emerald-600"
                          />
                          <Label htmlFor={owner.value} className="text-sm text-gray-700 cursor-pointer">
                            {owner.label}
                          </Label>
                        </div>
                      ))}
                    </div>
                  </div>
                </FilterSection>


                {/* Venue Type - Hidden when packages category is selected */}
                {filters.category !== 'packages' && (
                  <FilterSection
                    title="Venue Type"
                    isExpanded={expandedSections.venue}
                    onToggle={() => toggleSection('venue')}
                  >
                    <div className="space-y-2">
                      <RadioGroup
                        value={filters.venueType || ""}
                        onValueChange={(value) => handleSingleFilter('venueType', value)}
                      >
                        {venueTypesHierarchy.map((venueType) => (
                          <div key={venueType.id} className="flex items-center space-x-2">
                            <RadioGroupItem
                              id={venueType.name}
                              value={venueType.name}
                              className="!border-emerald-600 !text-emerald-600"
                            />
                            <Label htmlFor={venueType.name} className="text-sm text-gray-700 cursor-pointer">
                              {venueType.value}
                            </Label>
                          </div>
                        ))}
                      </RadioGroup>
                    </div>
                  </FilterSection>
                )}

                {/* Target Audience Filter (commented out for future use)
                <FilterSection
                  title="Target Audience"
                  isExpanded={expandedSections.audience}
                  onToggle={() => toggleSection('audience')}
                >
                  <div className="space-y-3">
                    <div>
                      <Label className="text-sm font-medium text-gray-700">Age Range</Label>
                      <CheckboxGroup
                        options={['18-24', '25-34', '35-44', '45-54', '55+']}
                        value={filters.targetAgeRange || []}
                        onChange={(option: string) => toggleArrayFilter('targetAgeRange', option)}
                      />
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-gray-700">Gender</Label>
                      <CheckboxGroup
                        options={['Male', 'Female', 'All']}
                        value={filters.targetGender || []}
                        onChange={(option: string) => toggleArrayFilter('targetGender', option)}
                      />
                    </div>
                  </div>
                </FilterSection>
                */}

                {/* Inventory Specification Filter (commented out for future use)
                <FilterSection
                  title="Inventory Specification"
                  isExpanded={expandedSections.inventorySpec}
                  onToggle={() => toggleSection('inventorySpec')}
                >
                  <div className="space-y-3">
                    <div>
                      <Label className="text-sm font-medium text-gray-700">Screen Size</Label>
                      <CheckboxGroup
                        options={['Small (14x8)', 'Medium (20x10)', 'Large (30x15)', 'Extra Large (40x20)']}
                        value={filters.screenSize || []}
                        onChange={(option: string) => toggleArrayFilter('screenSize', option)}
                      />
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-gray-700">Orientation</Label>
                      <CheckboxGroup
                        options={['Landscape', 'Portrait', 'Square']}
                        value={filters.orientation || []}
                        onChange={(option: string) => toggleArrayFilter('orientation', option)}
                      />
                    </div>
                  </div>
                </FilterSection>
                */}
              </CardContent>
            </Card>
          </div>

          {/* Right Content Area */}
          <div className="flex-1">
            {/* Top Bar with View Controls */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-semibold text-emerald-600">{filters.category === 'packages' ? 'Available Packages' : 'Available Inventories'}</h2>
                  <p className="text-sm text-gray-600 mt-1">
                    Showing {filters.category === 'packages' ? packageData.length : billBoardData.length} {filters.category === 'packages' ? (packageData.length === 1 ? 'Package' : 'Packages') : (billBoardData.length === 1 ? 'Inventory' : 'Inventories')} {search && `for "${search}"`}
                    {/* {searchQuery.length > 0 && searchQuery.length < 3 && (
                      <span className="text-red-600"> • Enter 3+ characters to search</span>
                    )} */}
                  </p>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="flex items-center border border-gray-200 rounded-lg p-1">
                    <Button
                      variant={viewMode === 'grid' ? 'default' : 'ghost'}
                      size="sm"
                      onClick={() => setViewMode('grid')}
                      className={`h-8 w-8 p-0 ${viewMode === 'grid' ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : ''}`}
                    >
                      <Grid className="h-4 w-4" />
                    </Button>
                    <Button
                      variant={viewMode === 'list' ? 'default' : 'ghost'}
                      size="sm"
                      onClick={() => setViewMode('list')}
                      className={`h-8 w-8 p-0 ${viewMode === 'list' ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : ''}`}
                    >
                      <List className="h-4 w-4" />
                    </Button>
                    <Button
                      variant={viewMode === 'map' ? 'default' : 'ghost'}
                      size="sm"
                      onClick={() => setViewMode('map')}
                      className={`h-8 w-8 p-0 ${viewMode === 'map' ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : ''}`}
                    >
                      <MapIcon className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            {/* Loading State */}
            {filters.category === 'packages' ? (
              (viewMode === 'map' ? (
                <MapView
                  inventory={packageData}
                  onInventorySelect={handleViewDetails}
                  onAddToCart={addToCart}
                  onDeleteFromCart={deleteCartItem}
                  isInCart={isInCart}
                  currencyCode={packageData[1]?.currency || 'N/A'}
                  edit={edit}
                  draftData={draftData}
                  cartDataList={cartDataList}
                  loadingCartItemIds={loadingCartItemIds}
                  campaignStartDate={startDate}
                  campaignEndDate={endDate}
                />
              ) : viewMode === 'grid' ? (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 py-8">
                  {packageData && packageData.length > 0 ? (
                    packageData.map((packageItem: any, index: number) => (
                      <PackageCard
                        key={packageItem.id || index}
                        package={[packageItem]} // Pass as array since PackageCard expects an array
                        currencyCode={packageItem?.currency || 'N/A'}
                        onDeleteFromCart={deleteCartItem}
                        isAuthenticated={true} // User is logged in when on start-campaign page
                        viewMode={viewMode}
                        campaignStartDate={startDate}
                        campaignEndDate={endDate}
                      />
                    ))
                  ) : (
                    <div className="col-span-full text-center py-8 text-gray-500">No packages available</div>
                  )}
                </div>
              ) : (
                <div className="space-y-4 py-8">
                  {packageData && packageData.length > 0 ? (
                    packageData.map((packageItem: any, index: number) => (
                      <PackageCard
                        key={packageItem.id || index}
                        package={[packageItem]} // Pass as array since PackageCard expects an array
                        currencyCode={packageItem?.currency || 'N/A'}
                        onDeleteFromCart={deleteCartItem}
                        isAuthenticated={true} // User is logged in when on start-campaign page
                        viewMode={viewMode}
                        campaignStartDate={startDate}
                        campaignEndDate={endDate}
                      />
                    ))
                  ) : (
                    <div className="text-center py-8 text-gray-500">No packages available</div>
                  )}
                </div>
              ))
            ) : isPageLoading ? (
              <div className="text-center py-12">
                <div className="animate-spin w-8 h-8 border-4 border-emerald-600 border-t-transparent rounded-full mx-auto mb-4" />
                <p className="text-gray-600">
                  {filters.category === 'packages' ? 'Loading packages...' : 'Loading inventory...'}
                </p>
              </div>
            ) : filters.category === 'packages' ? (
              // Render packages
              packageData.length === 0 ? (
                <div className="text-center py-12">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">No packages found</h3>
                  <p className="text-gray-600">Try adjusting your search or filters.</p>
                </div>
              ) : (
                <>
                  {viewMode === 'map' ? (
                    <MapView
                      inventory={packageData}
                      onInventorySelect={handleViewDetails}
                      onAddToCart={addToCart}
                      onDeleteFromCart={deleteCartItem}
                      isInCart={isInCart}
                      currencyCode={packageData?.[0]?.price?.localCurrencies?.code || 'N/A'}
                      edit={edit}
                      draftData={draftData}
                      cartDataList={cartDataList}
                      loadingCartItemIds={loadingCartItemIds}
                      campaignStartDate={startDate}
                      campaignEndDate={endDate}
                    />
                  ) : viewMode === 'grid' ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                      <PackageCard
                        package={packageData}
                        viewMode={viewMode}
                        campaignStartDate={startDate}
                        campaignEndDate={endDate}
                      />
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <PackageCard
                        package={packageData}
                        viewMode={viewMode}
                        campaignStartDate={startDate}
                        campaignEndDate={endDate}
                      />
                    </div>
                  )}
                </>
              )
            ) : billBoardData.length === 0 ? (
              <div className="text-center py-12">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No inventory found</h3>
                <p className="text-gray-600">Try adjusting your search or filters.</p>
              </div>
            ) : (
              <>
                {viewMode === 'map' ? (
                  <MapView
                    inventory={billBoardData}
                    onInventorySelect={handleViewDetails}
                    onAddToCart={addToCart}
                    onDeleteFromCart={deleteCartItem}
                    isInCart={isInCart}
                    currencyCode={billBoardData?.[0]?.price?.localCurrencies?.code || 'N/A'}
                    edit={edit} // <-- Pass edit flag
                    draftData={draftData} // <-- Pass draftData for edit flow
                    cartDataList={cartDataList} // <-- Pass cartDataList for normal flow
                    loadingCartItemIds={loadingCartItemIds} // Optional: for loading state
                    campaignStartDate={startDate}
                    campaignEndDate={endDate}
                  />
                ) : viewMode === 'grid' ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {billBoardData.map((inventoryItem: any) => {
                      // Use appropriate card based on inventory type
                      // if (inventoryItem.type === 'DEAL' || filters.category === 'deals') {
                      //   return (
                      //     <DealCard
                      //       key={inventoryItem.id}
                      //       deal={inventoryItem}
                      //       onAddToCart={() => addToCart(inventoryItem)}
                      //       onRemoveFromCart={() => deleteCartItem(inventoryItem)}
                      //       isInCart={cartDataList.some((cart: any) => 
                      //         cart.campaignInventories?.some((inv: any) => inv.billboardId === inventoryItem.id)
                      //       )}
                      //       isLoading={loadingCartItemIds.has(inventoryItem.id)}
                      //       onClick={() => handleCardClick(inventoryItem)}
                      //     />
                      //   );
                      // }
                      // // Use PackageCard for packages
                      // else if (inventoryItem.type === 'PACKAGE' || filters.category === 'packages') {
                      //   return (
                      //     <PackageCard
                      //       key={inventoryItem.id}
                      //       package={inventoryItem}
                      //       onAddToCart={() => addToCart(inventoryItem)}
                      //       onRemoveFromCart={() => deleteCartItem(inventoryItem)}
                      //       isInCart={cartDataList.some((cart: any) => 
                      //         cart.campaignInventories?.some((inv: any) => inv.billboardId === inventoryItem.id)
                      //       )}
                      //       isLoading={loadingCartItemIds.has(inventoryItem.id)}
                      //       onClick={() => handleCardClick(inventoryItem)}
                      //     />
                      //   );
                      // }

                      return (
                        <Card key={inventoryItem.id} className="bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow duration-200 overflow-hidden">
                          <div className="relative">
                            <Badge
                              className={`absolute top-3 left-3 z-10 ${
                                getStatusText(inventoryItem) === "Available"
                                  ? "bg-green-600"
                                  : "bg-red-600"
                                  }`}>
                              <div className="w-2 h-2 rounded-full bg-current mr-1"></div>
                              {getStatusText(inventoryItem)}
                            </Badge>
                            {cartDataList.some((cart: any) =>
                              cart.campaignInventories?.some((inv: any) => inv.billboardId === inventoryItem.id)
                            ) && (
                                <Badge className="absolute top-3 right-3 z-10 bg-emerald-600 text-white">
                                  In Cart
                                </Badge>
                              )}
                            <img
                              src={!inventoryItem.fullResImagePath || inventoryItem.fullResImagePath === "null"
                                ? `${import.meta.env.VITE_APP_FOLDER_PATH}/nomedia.jpg`
                                : inventoryItem.fullResImagePath}
                              alt={inventoryItem.displayName || inventoryItem.name}
                              className="w-full h-40 object-cover"
                            />
                          </div>

                          <CardContent className="p-4">
                            <div className="mb-3">
                              <h3 className="text-base font-semibold text-gray-900 mb-1 line-clamp-1" title={inventoryItem.displayName || inventoryItem.name}>
                                {inventoryItem.displayName || inventoryItem.name}</h3>
                              <p className="text-sm text-gray-600 mb-1">{`${inventoryItem.trDistrictName}, ${inventoryItem.trStateName}`}</p>
                              {/* {inventoryItem.mediaOwnerName && (
                                <Link
                                  to={`/store/${getMediaOwner(inventoryItem.mediaOwnerName)?.id || 'ooh-unmissable'}`}
                                  className="text-xs text-emerald-600 hover:text-emerald-700 hover:underline font-medium mb-2 inline-block"
                                  onClick={() => {
                                    const currentPath = window.location.pathname;
                                    sessionStorage.setItem('mediaOwnerBackUrl', currentPath);
                                    console.log('Start campaign grid: Setting back URL to', currentPath);
                                  }}
                                >
                                  by {getMediaOwner(inventoryItem.mediaOwnerName)?.name || inventoryItem.mediaOwnerName}
                                </Link>
                              )} */}
                              <span className='text-xs text-emerald-600 hover:text-emerald-700 font-medium mb-2 inline-block'>
                                by {getMediaOwner(inventoryItem.mediaOwnerName)?.name || inventoryItem.mediaOwnerName}
                              </span>

                              {(() => {
                                let price = 0;
                                let suffix = '';

                                if (inventoryItem.type === 'DIGITAL') {
                                  price = priceFormat === 'spot-based'
                                    ? inventoryItem.perSpotPrice
                                    : inventoryItem.perDayPrice;
                                  suffix = priceFormat === 'spot-based' ? '/Spot' : '/Day';
                                } else {
                                  price = (inventoryItem.sellingRate?.monthly?.month1 && inventoryItem.sellingRate.monthly.month1 > 0)
                                    ? inventoryItem.sellingRate.monthly.month1
                                    : inventoryItem.currentPrice;
                                  suffix = '/Month';
                                }

                                const isPriceValid = !!price && price !== 0;
                                const displayCurrencyCode = inventoryItem?.price?.localCurrencies?.code || currencyCode || 'N/A';

                                const priceDisplay = isPriceValid
                                  ? `${displayCurrencyCode} ${Number(price).toLocaleString('en-US', {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2
                                  })}${suffix}`
                                  : 'N/A';

                                return (
                                  <>
                                    <div className="text-lg font-bold text-emerald-600">
                                      {priceDisplay}
                                    </div>

                                    <div className="flex items-center justify-between text-xs text-gray-500 mb-3">
                                      <div className="flex items-center">
                                        <Monitor className="mr-1 h-3 w-3" />
                                        <span>
                                          {inventoryItem.type === 'CLASSIC'
                                            ? (inventoryItem.panelWidth && inventoryItem.panelHeight
                                              ? `${inventoryItem.panelWidth}x${inventoryItem.panelHeight}`
                                              : 'Standard')
                                            : (inventoryItem.resolutionWidth && inventoryItem.resolutionHeight
                                              ? `${inventoryItem.resolutionWidth}x${inventoryItem.resolutionHeight}`
                                              : inventoryItem.specification?.resolution1Width && inventoryItem.specification?.resolution1Height
                                                ? `${inventoryItem.specification.resolution1Width}x${inventoryItem.specification.resolution1Height}`
                                                : 'Standard')}
                                        </span>
                                      </div>
                                      <div className="flex items-center">
                                        <Users className="mr-1 h-3 w-3" />
                                        <span>{formatNumber(inventoryItem.impressionPerDay || 0)}</span>
                                      </div>
                                      <div className="flex items-center">
                                        <Clock className="mr-1 h-3 w-3" />
                                        <span>{inventoryItem.type === 'DIGITAL' ? inventoryItem?.price?.spotDuration : 'Monthly'}s</span>
                                      </div>
                                    </div>

                                    <div className="flex space-x-2">
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        className="flex-1"
                                        onClick={() => handleViewDetails(inventoryItem)}
                                      >
                                        Details
                                      </Button>
                                      {!loadingCartItemIds.has(inventoryItem.id) ? (
                                        <>
                                          {cartDataList.some((cart: any) =>
                                            cart.campaignInventories?.some((inv: any) => inv.billboardId === inventoryItem.id)
                                          ) ? (
                                            <Button
                                              onClick={() => deleteCartItem(inventoryItem)}
                                              size="sm"
                                              className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                                            >
                                              Remove
                                            </Button>
                                          ) : (
                                            <Button
                                              onClick={() => addToCart(inventoryItem)}
                                              disabled={!isPriceValid || !inventoryItem.availableBooking || !inventoryItem.active}
                                              size="sm"
                                              className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                                            >
                                              {!isPriceValid ? 'Unavailable' : !inventoryItem.availableBooking || !inventoryItem.active ? 'Sold Out' : 'Add to Cart'}
                                            </Button>
                                          )}
                                        </>
                                      ) : (
                                        <Button disabled size="sm" className="flex-1">
                                          <div className="animate-spin mr-2 h-4 w-4 border-2 border-b-transparent rounded-full" />
                                          Processing...
                                        </Button>
                                      )}
                                    </div>
                                  </>
                                );
                              })()}
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                ) : (
                  <div className="space-y-4">
                    {billBoardData.map((inventoryItem: any) => {
                      // Use DealCard for deals in list view too
                      if (filters.category === 'deals') {
                        return (
                          <DealCard
                            key={inventoryItem.id}
                            deal={inventoryItem}
                          />
                        );
                      }
                      // Use PackageCard for packages
                      else if (inventoryItem.inventoryType === "Package" || filters.category === 'packages') {
                        return (
                          <PackageCard
                            key={inventoryItem.id}
                            package={inventoryItem}
                            viewMode={viewMode}
                          />
                        );
                      }

                      return (
                        <Card key={inventoryItem.id} className="overflow-hidden hover:shadow-md transition-shadow">
                          <CardContent className="p-4 sm:p-6">
                            <div className="flex flex-col sm:flex-row items-start space-y-4 sm:space-y-0 sm:space-x-6">
                              {/* Image */}
                              <div className="relative flex-shrink-0 w-full sm:w-48">
                                <img
                                  src={!inventoryItem.fullResImagePath || inventoryItem.fullResImagePath === "null"
                                    ? `${import.meta.env.VITE_APP_FOLDER_PATH}/nomedia.jpg`
                                    : inventoryItem.fullResImagePath}
                                  alt={inventoryItem.displayName || inventoryItem.name}
                                  className="w-full h-32 object-cover rounded-lg"
                                />
                                {cartDataList.some((cart: any) =>
                                  cart.campaignInventories?.some((inv: any) => inv.billboardId === inventoryItem.id)
                                ) && (
                                    <Badge className="absolute top-2 right-2 bg-emerald-600 text-white">
                                      In Cart
                                    </Badge>
                                  )}
                              </div>

                              {/* Content */}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-start justify-between">
                                  <div className="flex-1 min-w-0">
                                    <h3 className="text-lg font-semibold text-gray-900 mb-1">{inventoryItem.displayName || inventoryItem.name}</h3>
                                    <p className="text-gray-600 mb-1">{`${inventoryItem.trDistrictName}, ${inventoryItem.trStateName}`}</p>
                                    {/* {inventoryItem.mediaOwnerName && (
                                      <Link
                                        to={`/store/${getMediaOwner(inventoryItem.mediaOwnerName)?.id || 'ooh-unmissable'}`}
                                        className="text-xs text-emerald-600 hover:text-emerald-700 hover:underline font-medium mb-2 inline-block"
                                        onClick={() => {
                                          // Set back URL context based on current page
                                          const currentPath = window.location.pathname;
                                          sessionStorage.setItem('mediaOwnerBackUrl', currentPath);
                                          console.log('Start campaign list: Setting back URL to', currentPath);
                                        }}
                                      >
                                        by {getMediaOwner(inventoryItem.mediaOwnerName)?.name || inventoryItem.mediaOwnerName}
                                      </Link>
                                    )} */}
                                    <span className='text-xs text-emerald-600 hover:text-emerald-700 font-medium mb-2 inline-block'>
                                      by {getMediaOwner(inventoryItem.mediaOwnerName)?.name || inventoryItem.mediaOwnerName}
                                    </span>

                                    <div className="flex items-center space-x-4 text-sm text-gray-500 mb-3">
                                      <div className="flex items-center">
                                        <Monitor className="mr-1 h-4 w-4" />
                                        <span>
                                          {inventoryItem.type === 'CLASSIC'
                                            ? (inventoryItem.panelWidth && inventoryItem.panelHeight
                                              ? `${inventoryItem.panelWidth}x${inventoryItem.panelHeight}`
                                              : 'Standard')
                                            : (inventoryItem.resolutionWidth && inventoryItem.resolutionHeight
                                              ? `${inventoryItem.resolutionWidth}x${inventoryItem.resolutionHeight}`
                                              : inventoryItem.specification?.resolution1Width && inventoryItem.specification?.resolution1Height
                                                ? `${inventoryItem.specification.resolution1Width}x${inventoryItem.specification.resolution1Height}`
                                                : 'Standard')}
                                        </span>
                                      </div>
                                      <div className="flex items-center">
                                        <Users className="mr-1 h-4 w-4" />
                                        <span>{formatNumber(inventoryItem.impressionPerDay || 0)} impressions</span>
                                      </div>
                                      <div className="flex items-center">
                                        <Clock className="mr-1 h-4 w-4" />
                                        <span>{inventoryItem.type === 'DIGITAL' ? inventoryItem?.price?.spotDuration : 'Monthly'}s</span>
                                      </div>
                                    </div>

                                    <Badge className={getStatusColor(inventoryItem)}>
                                      {getStatusText(inventoryItem)}
                                    </Badge>
                                  </div>

                                  <div className="text-right ml-4">
                                    <div className="text-2xl font-bold text-emerald-600">
                                      {(() => {
                                        let price = 0;
                                        let suffix = '';

                                        if (inventoryItem.type === 'DIGITAL') {
                                          price = priceFormat === 'spot-based'
                                            ? inventoryItem.perSpotPrice
                                            : inventoryItem.perDayPrice;
                                          suffix = priceFormat === 'spot-based' ? '/Spot' : '/Day';
                                        } else {
                                          price = (inventoryItem.sellingRate?.monthly?.month1 && inventoryItem.sellingRate.monthly.month1 > 0)
                                            ? inventoryItem.sellingRate.monthly.month1
                                            : inventoryItem.currentPrice;
                                          suffix = '/Month';
                                        }

                                        if (!price || price === 0) return 'N/A';

                                        const displayCurrencyCode = inventoryItem?.price?.localCurrencies?.code || currencyCode || 'N/A';

                                        return `${displayCurrencyCode} ${Number(price).toLocaleString('en-US', {
                                          minimumFractionDigits: 2,
                                          maximumFractionDigits: 2
                                        })}${suffix}`;
                                      })()}
                                    </div>
                                  </div>
                                </div>

                                <div className="flex space-x-3 mt-4">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleViewDetails(inventoryItem)}
                                  >
                                    View Details
                                  </Button>
                                  {!loadingCartItemIds.has(inventoryItem.id) ? (
                                    <>
                                      {cartDataList.some((cart: any) =>
                                        cart.campaignInventories?.some((inv: any) => inv.billboardId === inventoryItem.id)
                                      ) ? (
                                        <Button
                                          onClick={() => deleteCartItem(inventoryItem)}
                                          size="sm"
                                          className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                                        >
                                          Remove
                                        </Button>
                                      ) : (
                                        <Button
                                          onClick={() => addToCart(inventoryItem)}
                                          disabled={(() => {
                                            let price = 0;
                                            if (inventoryItem.type === 'DIGITAL') {
                                              price = priceFormat === 'spot-based'
                                                ? inventoryItem.perSpotPrice
                                                : inventoryItem.perDayPrice;
                                            } else {
                                              price = (inventoryItem.sellingRate?.monthly?.month1 && inventoryItem.sellingRate.monthly.month1 > 0)
                                                ? inventoryItem.sellingRate.monthly.month1
                                                : inventoryItem.currentPrice;
                                            }
                                            return !price || price === 0 || !inventoryItem.availableBooking || !inventoryItem.active;
                                          })()}
                                          size="sm"
                                          className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                                        >
                                          {(() => {
                                            let price = 0;
                                            if (inventoryItem.type === 'DIGITAL') {
                                              price = priceFormat === 'spot-based'
                                                ? inventoryItem.perSpotPrice
                                                : inventoryItem.perDayPrice;
                                            } else {
                                              price = (inventoryItem.sellingRate?.monthly?.month1 && inventoryItem.sellingRate.monthly.month1 > 0)
                                                ? inventoryItem.sellingRate.monthly.month1
                                                : inventoryItem.currentPrice;
                                            }
                                            if (!price || price === 0) return 'Unavailable';
                                            return !inventoryItem.availableBooking || !inventoryItem.active ? 'Sold Out' : 'Add to Cart';
                                          })()}
                                        </Button>
                                      )}
                                    </>
                                  ) : (
                                    <Button disabled size="sm" className="flex-1">
                                      <div className="animate-spin mr-2 h-4 w-4 border-2 border-b-transparent rounded-full" />
                                      Processing...
                                    </Button>
                                  )}
                                </div>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                )}

                {/* Pagination Controls */}
                  <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-t mt-6 rounded-lg">
                    <div className="flex items-center text-sm text-gray-700">
                      <span>
                        Showing {pageStartData} - {pageEndData} of {totalDataLength} {filters.category === 'packages' ? (totalDataLength === 1 ? 'Package' : 'Packages') : (totalDataLength === 1 ? 'Inventory' : 'Inventories')}
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={paginationMinus}
                        disabled={currentPage === 0}
                        className="flex items-center space-x-1"
                      >
                        <ChevronLeft className="h-4 w-4" />
                        <span>Previous</span>
                      </Button>

                      <div className="flex items-center space-x-1">
                        {getPageNumbers().map((page, index) => (
                          <div key={index}>
                            {page === '...' ? (
                              <span className="px-2 text-gray-500">...</span>
                            ) : (
                              <Button
                                variant={currentPage === page ? "default" : "outline"}
                                size="sm"
                                onClick={() => handlePageClick(page as number)}
                                className={`w-8 h-8 p-0 ${currentPage === page ? 'bg-emerald-600 hover:bg-emerald-700 text-white border-emerald-600' : ''}`}
                              >
                                {(page as number) + 1}
                              </Button>
                            )}
                          </div>
                        ))}
                      </div>

                      <Button
                        variant="outline"
                        size="sm"
                        onClick={paginationPlus}
                        disabled={currentPage >= Math.ceil(totalDataLength / dataListLength) - 1}
                        className="flex items-center space-x-1"
                      >
                        <span>Next</span>
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
              </>
            )}
          </div>
        </div>
      </div>
      <Footer configData={configData ?? []} base_path={base_path} yourLogo={footerLogo || yourLogo || ''} />

    </div>
  );
}